import { MutableRefObject, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createRoot } from "react-dom/client"
import { useSearchParams } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { useViewerStore } from "store/viewer.store"
import { useGetSeries } from "hooks/api/series/series.hook"
import {
	useGetQuotation,
	useDuplicateFurniture,
	useMoveFurnitureToEref,
	useDisconnectFurnitureFromEref,
	useBatchUpdateFurnitureStatus,
	useBatchDeleteFurniture,
	useBatchUpdateFurniture,
	useBatchUpdateFurnitureCategory,
	useBatchUpdateFurnitureCustomModelNumber,
	useBatchSelectArbitraryModelNumber,
	useBatchUpdateFurnitureConfig,
	useBatchUpdateFurnitureCountertopAttachments,
	useBatchUpdateFurnitureFixedPrice,
	useBatchUpdateFurnitureComment,
	useBatchDeleteFurnitureComment,
	useBatchUpdateFurnitureCustomization,
	useBatchDeleteFurnitureCustomization,
	useUpdateFurnitureStatus,
	useUpdateFurniture,
	useCreateEref,
	useCreateEcall,
	useCreateFurniture,
	useUpdateEref,
	useUpdateErefStatus,
	useDeleteEref,
	useUpdateEcall,
	useDeleteEcall,
	useUpdatePage,
} from "hooks/api/quotation/quotations.hook"
import { useResultCsvDownload } from "./interactions/useResultCsvDownload"
import { useResultPdfDownload } from "./interactions/useResultPdfDownload"
import { useDetailsWordDownload } from "./interactions/useDetailsWordDownload"
import { useDrawing } from "hooks/api/drawings/drawings.hook"
import { useMessage } from "hooks/message.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import { hasClipboardFor } from "helpers/furnitureClipboard.helper"
import { applyPageUpdateToCache } from "helpers/quotationCache.helper"
import type { AxiosResponse } from "axios"
import type { TQuotationDetail } from "types/quotations.type"
import { SelectionToolbar } from "./SelectionToolbar/SelectionToolbar"
import type { TToolbarAction } from "./SelectionToolbar/SelectionToolbar"
import {
	ConnectElevationIcon,
	DisconnectElevationIcon,
	CheckIcon,
	PlusIcon,
	MinusIcon,
	TrashIcon,
} from "./SelectionToolbar/icons"
import {
	buildConnectModalCopy,
	buildDeleteConfirmText,
	buildErefDisplayLabel,
	extractApiErrorMessage,
	findPageName,
	getEcallsForErefId,
	isFurnitureQuotationReady,
	isVersionConflictError,
	suggestNextElevationNumber,
} from "./quotationViewer.helpers"
import type { TDeleteConfirmTarget, TRightPanelMode } from "./quotationViewer.helpers"
import { ConnectToElevationModal } from "./ConnectToElevationModal/ConnectToElevationModal"
import { ConnectObjectsBar } from "./ConnectObjectsBar/ConnectObjectsBar"
import { ConfirmModal } from "components/ui/modals/ConfirmModal/ConfirmModal"
import { ElevationReferencesSidebar } from "./ElevationReferencesSidebar/ElevationReferencesSidebar"
import type { TEditPagePayload } from "./ElevationReferencesSidebar/ElevationReferencesSidebar"
import { SetPageNameModal } from "./SetPageNameModal/SetPageNameModal"
import { ElevationReferencesToggle } from "./ElevationReferencesSidebar/ElevationReferencesToggle/ElevationReferencesToggle"
import { useModelNumberMismatches } from "components/ui/customWebviewer/shared/ModelNumberMismatchIndicator/useModelNumberMismatches.hook"
import { ReferenceSidebar } from "./ReferenceSidebar/ReferenceSidebar"
import { CreateErefSidebar } from "./CreateErefSidebar/CreateErefSidebar"
import { CreateCountertopSidebar } from "./CreateCountertopSidebar/CreateCountertopSidebar"
import { CreateOtherSidebar } from "./CreateOtherSidebar/CreateOtherSidebar"
import { CreateCustomSidebar } from "./CreateCustomSidebar/CreateCustomSidebar"
import { CreateEcallSidebar } from "./CreateEcallSidebar/CreateEcallSidebar"
import { CreateFurnitureSidebar } from "./CreateFurnitureSidebar/CreateFurnitureSidebar"
import { FurnitureSidebar } from "./FurnitureSidebar/FurnitureSidebar"
import { BatchFurnitureSidebar } from "./BatchFurnitureSidebar/BatchFurnitureSidebar"
import { EcallSidebar } from "./EcallSidebar/EcallSidebar"
import { useAnnotations } from "hooks/annotations.hook"
import type { TSelectedAnnotationEntity } from "hooks/annotations.hook"
import { smartNavDebug } from "./smartNavDebug"
import AnnotationService from "services/annotations.service"
import { StyleProvider } from "@ant-design/cssinjs"
import { ShadowDomProviders } from "./ShadowDomProviders/ShadowDomProviders"
import { safeRender, disposeRoot, type RootEntry } from "./core/shadowPortal"
import { injectAllShadowStyles } from "./core/shadowStyles"
import { createPanelControls } from "./core/panelControls"
import { createMiddlePanelHost } from "./core/middlePanel"
import { usePaneDivider } from "./interactions/usePaneDivider"
import { initWebViewer } from "./core/webviewerInit"
import { fetchDocumentBlob, resolveDocumentIdentity } from "./core/documentBlobCache"
import { useCurrentUserStore } from "store/user.store"
import { useDocumentLoadStore } from "store/documentLoad.store"
import { createOverlayContainers, SHORTCUTS_PANEL_INSET } from "./core/overlayContainers"
import { applyBottomDockInset } from "./core/bottomDockInset"
import { runWhenReady } from "./hotkeys/focus"
import { useShadowMount } from "./core/useShadowMount"
import { KeyboardShortcutsPanel } from "./KeyboardShortcutsPanel/KeyboardShortcutsPanel"
import { applyViewerHeaderAndTools } from "./core/viewerModes"
import { setToolModeOnAllPanes, type TViewerTool } from "./core/viewerTools"
import { resolveViewerPanes, type TPaneBinding } from "./core/viewerPanes"
import { useFurnitureClipboard } from "./interactions/useFurnitureClipboard"
import { useFigureHighlightCreate } from "./interactions/useFigureHighlightCreate"
import { useFreehandHighlightCreate } from "./interactions/useFreehandHighlightCreate"
import { useHighlightSelect } from "./interactions/useHighlightSelect"
import { useFigureHighlightStore } from "store/figureHighlight.store"
import { useBatchOperations } from "./interactions/useBatchOperations"
import { useBatchFurnitureEdit } from "./interactions/useBatchFurnitureEdit"
import { useErefDelete } from "./interactions/useErefDelete"
import { useEcallDelete } from "./interactions/useEcallDelete"
import { useErefStatusChange } from "./interactions/useErefStatusChange"
import { useErefConnectObjects } from "./interactions/useErefConnectObjects"
import type { TConnectObjectsSession } from "./interactions/useErefConnectObjects"
import { useErefCreate } from "./interactions/useErefCreate"
import type { TErefCreateSession } from "./interactions/useErefCreate"
import { useCountertopCreate } from "./interactions/useCountertopCreate"
import { useOtherCreate } from "./interactions/useOtherCreate"
import type { TOtherCreateSession } from "./interactions/useOtherCreate"
import { useCustomObjectCreate } from "./interactions/useCustomObjectCreate"
import type { TCustomCreateSession } from "./interactions/useCustomObjectCreate"
import type { TAreaCreateSession } from "./interactions/useAreaCreate"
import { useEcallCreate } from "./interactions/useEcallCreate"
import type { TEcallCreateSession } from "./interactions/useEcallCreate"
import { useFurnitureCreate } from "./interactions/useFurnitureCreate"
import type { TFurnitureCreateSession } from "./interactions/useFurnitureCreate"
import { usePageNavigation } from "./interactions/usePageNavigation"
import { useContextMenu } from "./interactions/useContextMenu"
import { useJumpNavigation } from "./interactions/useJumpNavigation"
import { useCoordinateCommit } from "./interactions/useCoordinateCommit"
import { useNoteCreate } from "./interactions/useNoteCreate"
import { useNoteEdit } from "./interactions/useNoteEdit"
import { useNoteToolStore } from "store/noteTool.store"
import { useCanvasTools } from "./interactions/useCanvasTools"
import { useRemovePdfLinks } from "./interactions/useRemovePdfLinks"
import { SPLIT_SCREEN_ENABLED, useSplitScreen } from "./interactions/useSplitScreen"
import { POP_OUT_WINDOW_ENABLED, usePopOutWindow } from "./interactions/usePopOutWindow"
import { usePopOutFollowNavigation } from "./interactions/usePopOutFollowNavigation"
import { resolveViewerSearch } from "./core/popOutWindow.helpers"
import { useSmartNavigation } from "./interactions/useSmartNavigation"
import { getSelectionEntityId } from "./interactions/smartNavigation.helpers"
import type { TSmartNavSelection } from "./interactions/smartNavigation.helpers"
import { useEditorHotkeys } from "./hotkeys/useEditorHotkeys"
import { useSplitScreenStore } from "store/splitScreen.store"
import { usePopOutWindowStore } from "store/popOutWindow.store"
import { useViewerMenusStore } from "store/viewerMenus.store"
import type { Core, WebViewerInstance } from "@pdftron/webviewer"
import type { TEcall, TEref, TErefWithFurniture, TFurniture } from "types/elevationReferences.type"
import type { TAnnotationType } from "types/annotations.type"
import type { TViewerMode, TQuotationZoomDirection } from "store/viewer.store"
import { SMART_NAV_MAX_ZOOM } from "services/annotations.service"
import type { TViewerPane } from "services/annotations.service"

// What the split screen's RIGHT pane has open in its own sidebar (review #4 on #1264). It is a
// second, fully independent selection: the left screen keeps its eref docked between the panes
// while the right screen opens that eref's connected ecall beside it. Modelled as one discriminated
// union rather than the primary pane's parallel state/ref pairs — only the render reads it, so it
// needs no mirroring refs.
type TSplitSidebar =
	| { mode: "eref"; eref: TErefWithFurniture }
	| { mode: "furniture"; furniture: TFurniture }
	| { mode: "ecall"; ecall: TEcall }

// macOS Ctrl+click is the OS secondary-click (fires `contextmenu`), so it must NOT be treated as
// an additive-select modifier there. Use Cmd (metaKey) for additive on mac, Ctrl elsewhere.
const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/i.test(navigator.platform)

type Props = {
	fileLink: string | undefined
	previewRef: MutableRefObject<HTMLElement | null>
	isModalOpen: boolean
	mode: TViewerMode
	onClose?: () => void
	// The hosting drawing's status, needed only to open the pop-out window (#1430): the child
	// resolves its OWN document from the ids in its URL, and `useDocuments` picks a different
	// endpoint for an "uploaded" drawing, so the status has to travel with them.
	drawingStatus?: string
}

// Delay (ms) after entering editor mode before re-positioning the floating overlays — long
// enough to outlast the editor modal's open animation, after which getBoundingClientRect
// returns untransformed rects. Ant Design's modal motion is ~300ms.
const MODAL_ANIM_SETTLE_MS = 350

// How long a document download runs before the overlay announces it. Below this, the wait is not
// worth covering the screen for — a Cache Storage hit resolves in milliseconds, and so does a small
// drawing on a fast connection; both would otherwise flash an overlay on every open.
const DOCUMENT_LOAD_ANNOUNCE_DELAY_MS = 250
// Height of a tool-options row — REQ-400's shape row or REQ-395/396's note row, both a second
// .wv-header stacked below the main one (see webviewer-quotation.css's .wv-header-stack). Whichever
// is showing pushes the document area down by this much. Must match that CSS rule's own
// `height: 44px`; same "duplicate the pixel value in JS" convention core/overlayContainers.tsx
// already uses for TOGGLE_TOP_OFFSET / PAGE_NAV_BOTTOM_OFFSET.
const TOOL_OPTIONS_ROW_HEIGHT = 44
const CREATE_AREA_PANEL_DEFAULT_WIDTH = "320px"

// Default width of the two sidebars, per design. Apryse's own default is 264, and for a panel added
// through `UI.addPanel` it renders the container at `width - 14` (it reserves the resize bar's width
// whether or not that bar is drawn), which left both sidebars at 250px. 305 lands the container at
// 291 and our sidebar root at the design's 290 once the panel's 1px divider is taken off. Set once
// at init, so a user's own drag still wins for the rest of the session.
const SIDEBAR_DEFAULT_WIDTH = 305

// Stable empty list for a page that has no elevation references (or no area drawn yet), so the
// create-object panel's dropdown isn't handed a fresh array on every render.
const EMPTY_EREFS: TEref[] = []

export const useQuotationViewer = ({ fileLink, previewRef, isModalOpen, mode, onClose, drawingStatus = "" }: Props) => {
	const instanceRef = useRef<WebViewerInstance | null>(null)
	// Set synchronously the moment init starts. instanceRef.current is only assigned
	// after the async WebViewer(...) promise resolves, so this guards the window in
	// between against a re-run of the init effect mounting a second viewer.
	const isInitializingRef = useRef(false)
	const {
		registerQuotationInstance,
		openQuotationFeedback,
		openQuotationSettings,
		openFeedbackPanel,
		setQuotationViewerOpen,
	} = useViewerStore()
	// Publish whether the fullscreen viewer is open so the hosting FileInfo drawer can stop closing
	// on Escape while it covers the drawer (Escape is owned by the viewer). Reset on unmount.
	useEffect(() => {
		setQuotationViewerOpen(isModalOpen)
		return () => setQuotationViewerOpen(false)
	}, [isModalOpen, setQuotationViewerOpen])
	// Latest mode, readable inside async init / event handlers without re-subscribing.
	const modeRef = useRef<TViewerMode>(mode)
	modeRef.current = mode
	// Guards the one-time "Share feedback" button injection into the default (preview) header.
	const previewFeedbackButtonAddedRef = useRef(false)
	// Guards the one-time registration of the editor's custom header. Both the default (preview)
	// and custom (editor) headers stay registered for the life of the single shared instance;
	// mode switching only toggles their visibility. See applyHeaderAndTools. #1119
	const customHeaderAddedRef = useRef(false)
	const loadingTaskRef = useRef<Promise<unknown> | null>(null)
	const [isInitialized, setIsInitialized] = useState(false)
	const headerEntryRef = useRef<RootEntry | null>(null)
	const sidebarLeftEntryRef = useRef<RootEntry | null>(null)
	const sidebarRightEntryRef = useRef<RootEntry | null>(null)
	// The sidebar docked BETWEEN the two split-screen panes (review #3 on #1264). Its host node is
	// ours, not an Apryse panel — see core/middlePanel.ts for why and how it parents into the left
	// pane. Created lazily on the first split-screen enter and reused for the life of the viewer.
	const middlePanelHostRef = useRef<ReturnType<typeof createMiddlePanelHost> | null>(null)
	const sidebarMiddleEntryRef = useRef<RootEntry | null>(null)
	const toggleEntryRef = useRef<RootEntry | null>(null)
	const jumpingNavEntryRef = useRef<RootEntry | null>(null)
	// The SECOND jumping-nav overlay root — the split-screen pane's own bar (#1311). Smart navigation
	// renders into whichever of the two belongs to the current SECONDARY pane.
	const splitJumpingNavEntryRef = useRef<RootEntry | null>(null)
	const jumpingNavResizeObserverRef = useRef<ResizeObserver | null>(null)
	const jumpingNavDocAreaRef = useRef<HTMLElement | null>(null)
	const jumpingNavTargetContainerRef = useRef<HTMLElement | null>(null)
	// Recomputes the floating-overlay (page-nav + jumping-nav) positions from the live
	// element rects. Stored in a ref so effects can trigger it after a mode switch — the
	// ResizeObserver alone misses it (a modal scale-animation changes getBoundingClientRect
	// but not layout size, so it never re-fires once the animation settles). #1119
	const repositionOverlaysRef = useRef<(() => void) | null>(null)
	// Re-points the overlay ResizeObserver at the panes that exist NOW (split enter/exit replaces
	// the observed scroll elements; an observer on a detached node never fires again). REQ-399.
	const rebindOverlayObserversRef = useRef<(() => void) | null>(null)
	// Live per-pane document-area lookup (see overlayContainers) — used to refresh the jumping-nav
	// reference element after a split-screen layout change.
	const getPaneDocAreaElRef = useRef<((pane: TViewerPane) => HTMLElement | null) | null>(null)
	// Page-nav overlay root (created by createOverlayContainers, owned here for disposal); its
	// state + handlers live in usePageNavigation below.
	const pageNavEntryRef = useRef<RootEntry | null>(null)
	// Second page-nav overlay root — the split-screen pane's own bar (REQ-399).
	const splitPageNavEntryRef = useRef<RootEntry | null>(null)
	const [isPanelOpen, setIsPanelOpen] = useState(false)
	const [isReferencePanelOpen, setIsReferencePanelOpen] = useState(false)
	// Latest open flag, readable inside memoized hotkey callbacks. rightPanelMode is not reset when
	// the sidebar is closed via its X, so navigation must also confirm the panel is actually open.
	const isReferencePanelOpenRef = useRef(isReferencePanelOpen)
	isReferencePanelOpenRef.current = isReferencePanelOpen
	const [isDocumentLoaded, setIsDocumentLoaded] = useState(false)
	// Bumped whenever split screen re-lays-out the viewer; listed in the deps of every effect that
	// binds to a pane's scroll element or measures the document area (REQ-399).
	const layoutEpoch = useSplitScreenStore((state) => state.layoutEpoch)
	// Read from the store (not from useSplitScreen's return) because the split-pane page-nav bar is
	// declared above that hook call.
	const isSplitPaneOpen = useSplitScreenStore((state) => state.isSplitActive)
	// Which pane's selection is currently driving the sidebars (#1311). Written by useSmartNavigation
	// (declared far below) and read here only to hand it to the middle-dock host, which publishes it
	// to the stylesheet — hence the store rather than a value threaded down from the hook.
	const primaryPane = useSplitScreenStore((state) => state.primaryPane)

	// The live viewer panes to bind canvas handlers to: one in single-viewer mode, two while split
	// screen is open (REQ-399). Resolved fresh on every call — the scroll elements are replaced when
	// Apryse re-mounts the document area, so nothing here may be cached across a `layoutEpoch` bump.
	const getViewerPanes = useCallback((): TPaneBinding[] => resolveViewerPanes(instanceRef.current), [])

	// The canvas tools (select / pan / zoom / ruler) + their per-pane pointer behavior. Declared here
	// because the hotkey bindings and the header wiring below both call into it.
	const { onToolChange, onZoomTool, onToggleRulerTool } = useCanvasTools({
		instanceRef,
		mode,
		isInitialized,
		getViewerPanes,
		layoutEpoch,
	})

	useRemovePdfLinks({ instanceRef, isInitialized, layoutEpoch })

	// Is the select tool active in ANY live pane? Never read `Core.documentViewer` for this — that is
	// always pane 1, and the ruler is primary-pane only by design (core/viewerTools.ts), so while pane
	// 1 measures the split pane sits in EDIT. Asking pane 1 there made the right pane inert: its
	// selections were dropped and its drag-moves never reached the backend. A canvas interaction can
	// only originate in a pane whose own tool is EDIT (the other tools capture the pointer), so "some
	// pane is in EDIT" is a safe stand-in until the originating pane is threaded through the
	// selection + coordinate callbacks (#1301).
	const isEditToolActive = useCallback(() => {
		const instance = instanceRef.current
		if (!instance) return false
		const { Tools } = instance.Core
		const isEditing = (viewer: Core.DocumentViewer) => viewer.getToolMode() === viewer.getTool(Tools.ToolNames.EDIT)
		const panes = getViewerPanes()
		if (panes.length === 0) return isEditing(instance.Core.documentViewer)
		return panes.some(({ documentViewer }) => isEditing(documentViewer))
	}, [getViewerPanes])

	const [selectedEref, setSelectedEref] = useState<TErefWithFurniture | undefined>(undefined)
	const selectedErefRef = useRef<TErefWithFurniture | undefined>(undefined)
	const [rightPanelMode, setRightPanelMode] = useState<TRightPanelMode>(null)
	const rightPanelModeRef = useRef<TRightPanelMode>(null)
	rightPanelModeRef.current = rightPanelMode
	const [selectedFurniture, setSelectedFurniture] = useState<TFurniture | undefined>(undefined)
	const selectedFurnitureRef = useRef<TFurniture | undefined>(undefined)
	const [selectedEcall, setSelectedEcall] = useState<TEcall | undefined>(undefined)
	const selectedEcallRef = useRef<TEcall | undefined>(undefined)
	// Fed to useAnnotations so its peer-driven resync (#1430 follow-up) can restore the on-canvas
	// selection after a full rebuild.
	const selectedEntityRef = useRef<TSelectedAnnotationEntity | null>(null)
	// The split screen's right pane keeps its OWN sidebar selection (review #4 on #1264); null when
	// nothing is open there. Cleared whenever split screen closes.
	const [splitSidebar, setSplitSidebar] = useState<TSplitSidebar | null>(null)
	// Which pane the in-flight manual selection came from. Written by the canvas handlers right
	// before they select and reset immediately after (Apryse dispatches `annotationSelected`
	// synchronously), so every OTHER path into that handler — jump navigation, hotkeys, the left
	// sidebar — reads "primary" and keeps driving the left screen's sidebar. Same one-shot trick as
	// additiveSelectRef below.
	const selectSourcePaneRef = useRef<TViewerPane>("primary")
	// Where the selection handlers below publish "entity X was selected in pane Y" for smart
	// navigation (#1311). A ref because those handlers must stay referentially stable (the header and
	// the Apryse panel callbacks capture them once) while `useSmartNavigation` — which is declared
	// hundreds of lines further down, after the jump session it drives — owns the actual closure.
	// `null` as the selection means "an entity smart navigation has no counterpart rule for"
	// (furniture, page): the Primary pane still follows the click, but no session is computed.
	const smartNavNotifyRef = useRef<
		((selection: TSmartNavSelection | null, pane: TViewerPane, hasOwnScroll: boolean) => void) | null
	>(null)
	// Same forward-declaration trick, for usePopOutFollowNavigation (declared far below, after the
	// click handlers it drives) — the cross-window counterpart to smartNavNotifyRef above.
	const popOutFollowNotifyRef = useRef<((selection: TSmartNavSelection | null, pane: TViewerPane) => void) | null>(
		null,
	)
	// Raised by useJumpNavigation while IT is navigating (stepping a session, returning to a source).
	// Those selections are programmatic, so they must not read as "the user clicked in this pane" —
	// otherwise prev/next would hand the Primary role back and forth on every step. Owned here
	// because the two hooks that share it are declared far apart.
	const suppressSmartNavRef = useRef(false)
	// `hasOwnScroll` is true when the caller already issued its own scrollToEref/scrollToEcall/
	// scrollToFurniture for THIS pane (a fresh click with `scroll: true`) — see
	// useSmartNavigation's notifySelection for why that pane must then skip preservePaneViewport.
	const notifySmartNavSelection = useCallback(
		(selection: TSmartNavSelection | null, pane: TViewerPane, hasOwnScroll: boolean) => {
			smartNavDebug("notify", {
				pane,
				kind: selection?.kind ?? null,
				id: selection ? getSelectionEntityId(selection) : null,
				suppressed: suppressSmartNavRef.current,
			})
			if (suppressSmartNavRef.current) return
			smartNavNotifyRef.current?.(selection, pane, hasOwnScroll)
			popOutFollowNotifyRef.current?.(selection, pane)
		},
		[],
	)
	// Run a PROGRAMMATIC selection attributed to `pane`, with smart navigation muted for its
	// duration. Two bugs live here if either half is missing:
	//   • the pane tag — Apryse dispatches `annotationSelected` synchronously inside the select, and
	//     the handler routes the result by `selectSourcePaneRef`. Left at whatever the outer caller
	//     set (the canvas mousedown leaves it at "split" for the whole gesture), a selection meant
	//     for one pane opens the OTHER pane's sidebar too, which is how both sidebars ended up
	//     showing the same counterpart;
	//   • the mute — that same nested dispatch re-enters the click handler, so BOTH invocations would
	//     publish to smart navigation and the plan would be resolved and applied twice per click.
	// Both are saved and restored rather than reset to a constant, so nesting is safe.
	const selectInPaneSilently = useCallback((pane: TViewerPane, select: () => void) => {
		const previousPane = selectSourcePaneRef.current
		const previousSuppress = suppressSmartNavRef.current
		selectSourcePaneRef.current = pane
		suppressSmartNavRef.current = true
		try {
			select()
		} finally {
			selectSourcePaneRef.current = previousPane
			suppressSmartNavRef.current = previousSuppress
		}
	}, [])

	const [furnitureInitialTab, setFurnitureInitialTab] = useState<"Details" | "Connected callouts">("Details")
	const [furnitureMountEpoch, setFurnitureMountEpoch] = useState(0)

	// Apryse's `Panel` returns null while a panel is closed (its Panel.js), so the `CustomElement`
	// wrapping our sidebar UNMOUNTS on every close and remounts on every open — meaning the
	// `addPanel` `render()` callbacks below run again on EVERY open, disposing the shadow root they
	// painted last time and creating a fresh one. The declarative `useShadowMount` calls further down
	// own the real content, but by the time Apryse remounts, every state change that opened the panel
	// has already been committed, so nothing schedules the render that would repaint the new root:
	// the panel keeps whatever `render()` put in it until some unrelated state change happens to
	// re-render this hook (a query settling, a NATS tick, an annotation event) — which is seconds
	// later, or never. That is why the object sidebar's Back button used to appear long after the
	// rest of the panel, and why the left sidebar reopened without its selection highlights.
	//
	// Bumping this epoch from inside `render()` IS that missing render. Deferred to a microtask
	// because Apryse appends the container only after `render()` returns, and `safeRender` skips a
	// container that is not connected yet.
	const [, setShadowRepaintEpoch] = useState(0)
	const scheduleShadowRepaint = useCallback(() => {
		queueMicrotask(() => setShadowRepaintEpoch((epoch) => epoch + 1))
	}, [])

	// Right-click context-menu target refs (written by the contextmenu handler below, read by
	// useContextMenu when building the menu). Position + render + dismiss live in that hook.
	const contextMenuFurnitureRef = useRef<TFurniture | null>(null)
	// Snapshot of the multi-furniture selection a "furniture-batch" right-click menu acts on.
	const contextMenuBatchRef = useRef<TFurniture[]>([])
	// The eref an "eref" right-click menu acts on (REQ-376 Delete).
	const contextMenuErefRef = useRef<TErefWithFurniture | null>(null)
	// The ecall an "ecall" right-click menu acts on (REQ-374).
	const contextMenuEcallRef = useRef<TEcall | null>(null)
	const contextMenuPointRef = useRef<{ pageNumber: number; x: number; y: number } | null>(null)
	const contextMenuEntryRef = useRef<RootEntry | null>(null)
	// Last cursor position over the scroll view, so Ctrl/Cmd+V can paste at the cursor.
	const lastPointerClientRef = useRef<{ x: number; y: number } | null>(null)
	// True while an additive (Shift/Cmd/Ctrl) canvas click is being processed, so the
	// annotationSelected handler suppresses the single-furniture sidebar even on the first click
	// of a batch. Cleared synchronously after the select (Apryse dispatches the event inline).
	const additiveSelectRef = useRef(false)

	// Floating selection toolbar (REQ-369 furniture, REQ-376 eref). `selectionToolbar` holds what
	// the pill acts on — a furniture selection (single or batch) or exactly one eref — and drives
	// its contextual actions; it is rendered into its own shadow-DOM overlay (positioned above the
	// selection's bounding box). The connect/move + delete confirmations are rendered in the same
	// overlay tree and portal out via their own shadow-root containers.
	const [selectionToolbar, setSelectionToolbar] = useState<
		| { kind: "furniture"; furniture: TFurniture[] }
		| { kind: "eref"; eref: TErefWithFurniture }
		| { kind: "ecall"; ecall: TEcall }
		| null
	>(null)
	const [connectModal, setConnectModal] = useState<{
		mode: "connect" | "move"
		furniture: TFurniture[]
		erefs: TEref[]
	} | null>(null)
	const [deleteConfirm, setDeleteConfirm] = useState<TDeleteConfirmTarget | null>(null)
	const [batchPending, setBatchPending] = useState(false)
	const selectionToolbarEntryRef = useRef<RootEntry | null>(null)
	// Floating "delete note" toolbar (REQ-395/396) — its own overlay, rendered by `useNoteEdit`
	// directly rather than from this orchestrator (mirrors PR #1381's `highlightToolbar` in spirit,
	// but the render effect itself lives in the interaction hook per AGENTS.md's "keep the
	// orchestrator thin" guidance — a note has no sidebar/other orchestrator-owned UI to coordinate
	// with, unlike the furniture/eref batch pill above).
	const noteToolbarEntryRef = useRef<RootEntry | null>(null)
	// Floating "delete highlight" toolbar overlay (REQ-400) — its own container, see
	// core/overlayContainers.tsx for why it isn't folded into selectionToolbarEntryRef above.
	const highlightToolbarEntryRef = useRef<RootEntry | null>(null)

	// Eref "Connect objects" mode (REQ-372). The session ref is owned here — not in
	// useErefConnectObjects — because the canvas + sidebar handlers below are declared before that
	// hook runs and must stay inert while the mode owns the document area. Its bar renders into its
	// own top-of-document overlay; the marquee is a plain element the hook positions directly.
	// Keyboard-shortcuts help panel (REQ-359). Open/close only — the panel itself reads the hotkey
	// registry, so there is no data to hold here. Mirrored into a ref because the Escape handler below
	// is bound ONCE by useEditorHotkeys's dispatcher and captured there: a closure over the state
	// would be frozen at `false` and Escape would never dismiss the panel. Same reason the canvas
	// modes in that chain are keyed off session refs.
	const [isShortcutsPanelOpen, setIsShortcutsPanelOpen] = useState(false)
	const isShortcutsPanelOpenRef = useRef(false)
	const shortcutsPanelEntryRef = useRef<RootEntry | null>(null)
	// All three are identity-stable: the header captures its props exactly once (see
	// applyViewerHeaderAndTools) and the hotkey dispatcher its handlers once.
	const openKeyboardShortcuts = useCallback(() => {
		isShortcutsPanelOpenRef.current = true
		setIsShortcutsPanelOpen(true)
	}, [])
	const closeKeyboardShortcuts = useCallback(() => {
		isShortcutsPanelOpenRef.current = false
		setIsShortcutsPanelOpen(false)
	}, [])
	// Escape's first stop while the panel is up, ahead of closing the right sidebar. Returns whether
	// it acted, matching the other steps of that chain.
	const closeShortcutsPanelOnEscape = useCallback(() => {
		if (!isShortcutsPanelOpenRef.current) return false
		closeKeyboardShortcuts()
		return true
	}, [closeKeyboardShortcuts])

	// The header's dropdowns, driven from the keyboard (A / H / 1-6). Every one of these reads or
	// writes viewerMenus.store through getState() rather than a subscription: the hotkey dispatcher
	// captures its handlers once, so a value read at render time would be frozen — the same reason
	// the shortcuts panel above keeps a ref. The store is also what lets these reach INTO the header,
	// which Apryse renders once with the props it captured then.
	const openAddMenu = useCallback(() => useViewerMenusStore.getState().toggleMenu("add"), [])
	const openHelpMenu = useCallback(() => useViewerMenusStore.getState().toggleMenu("help"), [])
	// #1381 review: S now opens the Shapes menu (Rectangle/Ellipse moved off their own bare R/O
	// letters onto this menu's shared 1/2 digit-pick), mirroring A/H exactly.
	const openShapesMenu = useCallback(() => useViewerMenusStore.getState().toggleMenu("shapes"), [])
	const pickHeaderMenuItem = useCallback(
		(position: number) => useViewerMenusStore.getState().pickOpenMenuItem(position),
		[],
	)
	// S opens settings from anywhere, so it also dismisses an open header menu — otherwise the
	// dropdown is left hanging behind the settings modal.
	const openSettingsFromHotkey = useCallback(() => {
		useViewerMenusStore.getState().setOpenMenu(null)
		openQuotationSettings()
	}, [openQuotationSettings])
	const closeHeaderMenuOnEscape = useCallback(() => {
		const { openMenu, setOpenMenu } = useViewerMenusStore.getState()
		if (!openMenu) return false
		setOpenMenu(null)
		return true
	}, [])

	// D (#1383) drives the dual-view control from the keyboard, and mirrors its click exactly rather
	// than inventing a second behaviour for the same affordance. Since #1430 that click is no longer
	// asymmetric — the control always OPENS THE MENU, because the menu's first two rows are the
	// choice between a second view here and a second view in its own window, and one keypress cannot
	// stand for both. So D opens the menu in every state, and the digits above pick the row, exactly
	// as they do for + Add / help / Shapes. It never enters or exits a split by itself any more.
	//
	// Everything is read from the stores with getState() for the reason the A/H/S handlers above do
	// the same — the dispatcher captures its handlers once, so a value read at render time would be
	// frozen. The one condition worth keeping is "could the menu do anything at all": with a
	// single-page document and no pop-out available every row is inert, and opening an all-grey menu
	// on a keypress is worse than the key doing nothing.
	const setSplitRef = useRef<((active: boolean) => void) | null>(null)
	const toggleSplitViewFromHotkey = useCallback(() => {
		// Either feature behind the menu being on is enough — the two flags are independent, so with
		// `VITE_SPLIT_SCREEN=off` and the pop-out on, `D` must still reach the menu that offers it.
		if (!SPLIT_SCREEN_ENABLED && !POP_OUT_WINDOW_ENABLED) return
		const { isSplitActive, isSplitAvailable, isSplitPending } = useSplitScreenStore.getState()
		const { isPopOutOpen, isPopOutAvailable } = usePopOutWindowStore.getState()
		const canMenuAct = isSplitActive || isPopOutOpen || (isSplitAvailable && !isSplitPending) || isPopOutAvailable
		if (!canMenuAct) return
		useViewerMenusStore.getState().toggleMenu("splitView")
	}, [])

	const connectSessionRef = useRef<TConnectObjectsSession | null>(null)
	const connectObjectsEntryRef = useRef<RootEntry | null>(null)
	const connectMarqueeElRef = useRef<HTMLElement | null>(null)
	// True while that mode is open. Guards every canvas/sidebar interaction that would otherwise
	// fight it (opening a sidebar, hijacking the selection, deleting the picked objects).
	const isConnectingObjects = useCallback(() => connectSessionRef.current !== null, [])

	// The "add a detection" modes: elevation reference (REQ-292), elevation callout (REQ-294),
	// countertop (REQ-298), cabinet (REQ-293) and other object (REQ-299). Session refs owned here for the same reason as the
	// connect-objects one above: the canvas + sidebar handlers below are declared before those hooks
	// run and must stay inert while a mode owns the document area. They SHARE one draft box — a plain
	// overlay element the area-create hook positions directly — because only one can be open at a time.
	const createSessionRef = useRef<TErefCreateSession | null>(null)
	const countertopCreateSessionRef = useRef<TAreaCreateSession | null>(null)
	const createFurnitureSessionRef = useRef<TFurnitureCreateSession | null>(null)
	const createOtherSessionRef = useRef<TOtherCreateSession | null>(null)
	const createCustomSessionRef = useRef<TCustomCreateSession | null>(null)
	const closeCreateErefRef = useRef<(() => boolean) | null>(null)
	const closeCountertopCreateRef = useRef<(() => boolean) | null>(null)
	const closeCreateFurnitureRef = useRef<(() => boolean) | null>(null)
	const closeCreateOtherRef = useRef<(() => boolean) | null>(null)
	const closeCreateCustomRef = useRef<(() => boolean) | null>(null)
	// REQ-395/396: `useNoteCreate` is declared further down (after the five "add a detection" modes
	// above, which are read from refs for the same forward-reference reason), so every OTHER mode's
	// own "always takes over" entry point reaches its disarm through this ref instead of a direct
	// call. It is also how the shared tool-change wrappers below (declared once, after
	// `disarmShapeTool` exists) reach the note tool's disarm.
	const disarmNoteToolRef = useRef<(() => boolean) | null>(null)
	// Same indirection for the note SELECTION: a selected note keeps the note tool-options row up on
	// its own (`activeNoteId`), independently of the armed flag, so anything cancelling into another
	// canvas mode has to drop it too or that row is left stranded beside the new mode's.
	const deselectNoteRef = useRef<(() => void) | null>(null)
	// Declared up here, not beside its assignment further down: the `T` hotkey is registered with
	// useEditorHotkeys well before `handleToggleNoteTool` exists, and reads the toggle through this.
	const toggleNoteToolRef = useRef<() => void>(() => {})
	// REQ-400: lets the figure-highlight hooks (declared before useErefConnectObjects below) reach
	// `cancelConnectObjects` without an ordering dependency — same ref-indirection pattern as the
	// four closeCreateXRef above, assigned right after cancelConnectObjects is obtained.
	const cancelConnectObjectsRef = useRef<() => boolean>(() => false)
	// REQ-400: lets cancelOtherCanvasModes reach the select hook's clearHighlightSelection (declared
	// before it, since its selectHighlightById is handed to the create hook) without an ordering
	// dependency — same pattern as cancelConnectObjectsRef above. Assigned right after
	// useHighlightSelect.
	const clearHighlightSelectionRef = useRef<() => boolean>(() => false)
	const erefDraftRectRef = useRef<{ element: HTMLElement; handles: HTMLElement[] } | null>(null)
	const isCreatingEref = useCallback(() => createSessionRef.current !== null, [])
	const isCreatingCountertop = useCallback(() => countertopCreateSessionRef.current !== null, [])
	const isCreatingFurniture = useCallback(() => createFurnitureSessionRef.current !== null, [])
	const isCreatingOther = useCallback(() => createOtherSessionRef.current !== null, [])
	const isCreatingCustom = useCallback(() => createCustomSessionRef.current !== null, [])
	const createEcallSessionRef = useRef<TEcallCreateSession | null>(null)
	const isCreatingEcall = useCallback(() => createEcallSessionRef.current !== null, [])

	// True while ANY canvas mode owns the document area: connect-objects (REQ-372) or any of the six
	// "add a detection" modes. Every canvas/sidebar interaction that would fight a mode — opening a
	// sidebar, hijacking the selection, the context menu, copy/paste — checks this rather than one
	// specific mode, so a new mode is inert everywhere by construction.
	// REQ-395/396: the note tool reads live off `noteTool.store` (getState(), not a subscription) —
	// it has no session ref of its own like the five "add a detection" modes above (a note has no
	// side-panel form to coordinate with), so there's nothing else here for it to stay in sync with.
	const isNoteToolArmed = useCallback(() => useNoteToolStore.getState().isArmed, [])
	// Every canvas mode EXCEPT the REQ-400 highlight tools below — the shape tool and the freehand
	// (Pen) tool (the note tool IS included; from a highlight tool's point of view it is just another
	// mode). Deliberately excludes both so this can be handed to useFigureHighlightCreate /
	// useFreehandHighlightCreate / useHighlightSelect as "is some OTHER mode
	// active" without being self-referential (arming the shape tool, or switching Rectangle <->
	// Ellipse while already armed, must not see ITS OWN state as a blocker).
	const isOtherCanvasModeActive = useCallback(
		() =>
			isConnectingObjects() ||
			isCreatingEref() ||
			isCreatingCountertop() ||
			isCreatingEcall() ||
			isCreatingFurniture() ||
			isCreatingOther() ||
			isCreatingCustom() ||
			isNoteToolArmed(),
		[
			isConnectingObjects,
			isCreatingEref,
			isCreatingCountertop,
			isCreatingEcall,
			isCreatingFurniture,
			isCreatingOther,
			isCreatingCustom,
			isNoteToolArmed,
		],
	)

	// Every canvas mode, INCLUDING the shape tool — the guard the rest of the app (context menu,
	// paste, the entity mousedown handler, etc.) already used before REQ-400 and keeps using
	// unchanged. Read via getState() (not the store hook) since this is a plain callback invoked
	// imperatively from event handlers, not from render — same pattern the header-menu callbacks
	// below already use for viewerMenus.store.
	const isCanvasModeActive = useCallback(() => {
		const { activeShapeTool, isFreehandArmed } = useFigureHighlightStore.getState()
		return isOtherCanvasModeActive() || activeShapeTool !== null || isFreehandArmed
	}, [isOtherCanvasModeActive])
	// How far the connect-objects bar sits below the top of the document area: the jumping-nav bar
	// keeps that slot when a jump session is up, so the picker stacks under it. Measured live on every
	// reposition rather than cached — the height is 0 while that bar is hidden, which keeps this in
	// step with its visibility without duplicating the rule, and it stays correct through the editor
	// modal's open animation (when rects are still transformed).
	const getConnectBarOffset = useCallback(
		() => jumpingNavEntryRef.current?.container.getBoundingClientRect().height ?? 0,
		[],
	)

	// While ANY tool-options row is up, the header grows a second 44px row (see .wv-header-stack in
	// webviewer-quotation.css) that pushes the document area down by that much: REQ-400's shape row
	// (a shape is armed), its freehand row (the Pen tool is armed), or REQ-395/396's note row (the
	// note tool is armed OR a note is selected). The three are mutually exclusive — the header renders
	// them into a SINGLE slot — so this is one row's worth or none, never two. Read live off the
	// stores rather than a React prop: this is called from inside core/overlayContainers.tsx's
	// reposition closure, which lives outside React entirely.
	const getToggleExtraTopOffset = useCallback(() => {
		const { activeShapeTool, isFreehandArmed } = useFigureHighlightStore.getState()
		if (activeShapeTool !== null || isFreehandArmed) return TOOL_OPTIONS_ROW_HEIGHT
		const { isArmed, activeNoteId } = useNoteToolStore.getState()
		return isArmed || activeNoteId !== null ? TOOL_OPTIONS_ROW_HEIGHT : 0
	}, [])

	// "Set page name" modal (REQ-371): the page being edited (null = closed), the in-flight flag,
	// and the last backend rejection to surface inline (e.g. unknown page name / version mismatch).
	const [pageNameEdit, setPageNameEdit] = useState<TEditPagePayload | null>(null)
	const [pageNamePending, setPageNamePending] = useState(false)
	const [pageNameError, setPageNameError] = useState<string | null>(null)

	const queryClient = useQueryClient()
	const { success, error } = useMessage()
	const { formatError } = useApiErrorFormatter()
	const { mutateAsync: duplicateFurnitureAsync } = useDuplicateFurniture()
	const { mutateAsync: moveFurnitureAsync } = useMoveFurnitureToEref()
	const { mutateAsync: disconnectFurnitureAsync } = useDisconnectFurnitureFromEref()
	const { mutateAsync: batchStatusAsync } = useBatchUpdateFurnitureStatus()
	const { mutateAsync: batchDeleteAsync } = useBatchDeleteFurniture()
	const { mutateAsync: updateFurnitureStatusAsync } = useUpdateFurnitureStatus()
	const { mutateAsync: updateFurnitureAsync } = useUpdateFurniture()
	const { mutateAsync: updateErefAsync } = useUpdateEref()
	const { mutateAsync: updateErefStatusAsync } = useUpdateErefStatus()
	const { mutateAsync: deleteErefAsync } = useDeleteEref()
	const { mutateAsync: createErefAsync } = useCreateEref()
	const { mutateAsync: createEcallAsync } = useCreateEcall()
	const { mutateAsync: createFurnitureAsync } = useCreateFurniture()
	const { mutateAsync: updateEcallAsync } = useUpdateEcall()
	const { mutateAsync: deleteEcallAsync } = useDeleteEcall()
	const { mutateAsync: updatePageAsync } = useUpdatePage()
	// Batch furniture edit (REQ-403) — one mutation per endpoint, all consumed by useBatchFurnitureEdit.
	const { mutateAsync: batchUpdateFurnitureAsync } = useBatchUpdateFurniture()
	const { mutateAsync: batchUpdateCategoryAsync } = useBatchUpdateFurnitureCategory()
	const { mutateAsync: batchUpdateCustomModelNumberAsync } = useBatchUpdateFurnitureCustomModelNumber()
	const { mutateAsync: batchSelectArbitraryModelNumberAsync } = useBatchSelectArbitraryModelNumber()
	const { mutateAsync: batchUpdateConfigAsync } = useBatchUpdateFurnitureConfig()
	const { mutateAsync: batchUpdateCountertopAttachmentsAsync } = useBatchUpdateFurnitureCountertopAttachments()
	const { mutateAsync: batchUpdateFixedPriceAsync } = useBatchUpdateFurnitureFixedPrice()
	const { mutateAsync: batchUpdateCommentAsync } = useBatchUpdateFurnitureComment()
	const { mutateAsync: batchDeleteCommentAsync } = useBatchDeleteFurnitureComment()
	const { mutateAsync: batchUpdateCustomizationAsync } = useBatchUpdateFurnitureCustomization()
	const { mutateAsync: batchDeleteCustomizationAsync } = useBatchDeleteFurnitureCustomization()

	const [searchParams] = useSearchParams()
	// Falls back to this window's stored ids when the URL no longer carries them — the pop-out
	// window strips its own query after boot (#1430), and a RELOAD of it therefore starts with a
	// bare URL. Without the fallback the reloaded editor mounts and renders the PDF while
	// `quotationId` is undefined, so `useGetQuotation` stays disabled and the whole detection layer
	// is silently empty. No-op in the main app: `resolveViewerSearch()` returns the live search
	// there, and the router's own params are preferred whenever they are present.
	const quotationId = searchParams.get("quotationID") ?? new URLSearchParams(resolveViewerSearch()).get("quotationID")
	const numericQuotationId = quotationId ? Number(quotationId) : undefined
	const numericQuotationIdRef = useRef(numericQuotationId)
	numericQuotationIdRef.current = numericQuotationId
	const { data: quotationDetail } = useGetQuotation(quotationId ? Number(quotationId) : undefined)
	const quotationDetailRef = useRef(quotationDetail)
	quotationDetailRef.current = quotationDetail
	// The "add custom object" panel's model-number picker shows the quotation's own configured
	// series read-only (#1371) — resolved here since `useGetSeries` is a flat id/name list with no
	// per-quotation filter.
	const { data: seriesList } = useGetSeries()
	const customObjectSeriesName =
		seriesList?.find((series) => series.id === quotationDetail?.configuration?.series_id)?.series_name ?? null
	const relatedObjects = quotationDetail?.related_objects
	const relatedObjectsRef = useRef(relatedObjects)
	relatedObjectsRef.current = relatedObjects

	// Page id/version live in a separate top-level `page_infos[]`, keyed to related_objects[] by
	// page_number (page_name repeats across pages, so it can't be the key). Join them so the left
	// sidebar's "Set page name" affordance has the page-info id + optimistic-lock version the PATCH
	// needs. Only used for the sidebar — the plain relatedObjects still drives annotations/nav.
	const pageInfoByNumber = useMemo(() => {
		const map = new Map<number, { id: number; version: number }>()
		for (const info of quotationDetail?.page_infos ?? []) {
			map.set(info.page_number, { id: info.id, version: info.version })
		}
		return map
	}, [quotationDetail?.page_infos])
	const relatedObjectsForSidebar = useMemo(() => {
		if (!relatedObjects) return relatedObjects
		return relatedObjects.map((page) => {
			const info = pageInfoByNumber.get(page.page_number)
			return info ? { ...page, id: info.id, version: info.version } : page
		})
	}, [relatedObjects, pageInfoByNumber])
	// The Apryse addPanel render callback (initial left-sidebar paint) reads from refs to avoid stale
	// closures — mirror the id/version-joined data here so that first paint already carries the "Set
	// page name" affordance, not only the later declarative useShadowMount repaint.
	const relatedObjectsForSidebarRef = useRef(relatedObjectsForSidebar)
	relatedObjectsForSidebarRef.current = relatedObjectsForSidebar
	const allEcalls = useMemo(() => relatedObjects?.flatMap((page) => page.ecalls) ?? [], [relatedObjects])
	const mismatchedFurnitureIds = useModelNumberMismatches(relatedObjects, quotationDetail?.configuration?.series_id ?? null)
	const allEcallsRef = useRef(allEcalls)
	allEcallsRef.current = allEcalls

	const connectedErefIds = useMemo<number[]>(() => {
		if (!relatedObjects) return []
		const ids: number[] = []
		for (const page of relatedObjects) {
			const linkedIds = new Set<number>()
			for (const f of page.furniture) {
				if (f.eref_id != null) linkedIds.add(f.eref_id)
			}
			// Erefs are sorted by elevation number for navigation; furniture order is
			// handled server-side.
			const sortedConnected = page.erefs
				.filter((eref) => linkedIds.has(eref.id))
				.sort((a, b) =>
					(a.elevation_number ?? "").localeCompare(b.elevation_number ?? "", undefined, { numeric: true }),
				)
			for (const eref of sortedConnected) {
				ids.push(eref.id)
			}
		}
		return ids
	}, [relatedObjects])

	// Read by useAnnotations' peer-driven resync to restore the on-canvas selection after a rebuild.
	// Assigned inline during render (same pattern as `rightPanelModeRef` above) so it's always
	// current without being an effect dependency.
	selectedEntityRef.current =
		rightPanelMode === "eref" && selectedErefRef.current
			? { type: "eref", id: selectedErefRef.current.id }
			: rightPanelMode === "furniture" && selectedFurnitureRef.current
				? { type: "furniture", id: selectedFurnitureRef.current.id }
				: rightPanelMode === "ecall" && selectedEcallRef.current
					? { type: "ecall", id: selectedEcallRef.current.id }
					: null

	// Bypasses this component's own relatedObjects/page_infos props for the peer-driven redraw — see
	// getFreshAnnotationsData's own comment in annotations.hook.ts for why the query cache, not the
	// props, is what's read at redraw time.
	const getFreshAnnotationsData = useCallback(() => {
		const cached = numericQuotationId
			? queryClient.getQueryData<AxiosResponse<TQuotationDetail>>(["quotation", numericQuotationId])?.data
			: undefined
		if (!cached) return undefined
		return { relatedObjects: cached.related_objects ?? [], pageInfos: cached.page_infos ?? [] }
	}, [numericQuotationId, queryClient])

	// The peer-driven redraw's selection restore has no pane of its own to name (a pop-out window
	// never has a "split" pane), so this always runs it through "primary" — the same pane every
	// programmatic reselection in this file uses outside of an actual in-window split.
	const selectSilentlyOnPrimary = useCallback(
		(select: () => void) => selectInPaneSilently("primary", select),
		[selectInPaneSilently],
	)

	// Only render/track annotations in editor mode. Preview is read-only with no highlights.
	useAnnotations(
		mode === "editor" ? relatedObjects : undefined,
		mode === "editor" ? quotationDetail?.page_infos : undefined,
		selectedEntityRef,
		getFreshAnnotationsData,
		selectSilentlyOnPrimary,
	)

	// Keep selectedFurniture in sync with refreshed relatedObjects after a refetch
	useEffect(() => {
		const currentId = selectedFurnitureRef.current?.id
		if (!currentId || !relatedObjects) return
		for (const page of relatedObjects) {
			const next = page.furniture.find((f) => f.id === currentId)
			if (next) {
				if (next !== selectedFurnitureRef.current) {
					selectedFurnitureRef.current = next
					setSelectedFurniture(next)
				}
				return
			}
		}
		// Not found anywhere — deleted (a peer window can now do that). Close rather than leave
		// the sidebar pointing at a dead id.
		selectedFurnitureRef.current = undefined
		setSelectedFurniture(undefined)
		if (rightPanelModeRef.current === "furniture") setRightPanelMode(null)
		setIsReferencePanelOpen(false)
	}, [relatedObjects])

	// Keep selectedEref in sync with refreshed relatedObjects after a refetch
	useEffect(() => {
		const currentId = selectedErefRef.current?.id
		if (!currentId || !relatedObjects) return
		for (const page of relatedObjects) {
			const eref = page.erefs.find((e) => e.id === currentId)
			if (eref) {
				const furniture = page.furniture.filter((f) => f.eref_id === currentId)
				const next: TErefWithFurniture = { ...eref, furniture }
				selectedErefRef.current = next
				setSelectedEref(next)
				return
			}
		}
		selectedErefRef.current = undefined
		setSelectedEref(undefined)
		if (rightPanelModeRef.current === "eref") setRightPanelMode(null)
		setIsReferencePanelOpen(false)
	}, [relatedObjects])

	// Keep selectedEcall in sync with refreshed relatedObjects after a refetch
	useEffect(() => {
		const currentId = selectedEcallRef.current?.id
		if (!currentId || !relatedObjects) return
		for (const page of relatedObjects) {
			const ecall = page.ecalls.find((e) => e.id === currentId)
			if (ecall) {
				if (ecall !== selectedEcallRef.current) {
					selectedEcallRef.current = ecall
					setSelectedEcall(ecall)
				}
				return
			}
		}
		selectedEcallRef.current = undefined
		setSelectedEcall(undefined)
		if (rightPanelModeRef.current === "ecall") setRightPanelMode(null)
		setIsReferencePanelOpen(false)
	}, [relatedObjects])

	const getShadowRoot = useCallback(
		() => (previewRef.current as unknown as HTMLElement)?.querySelector("apryse-webviewer")?.shadowRoot ?? undefined,
		[previewRef],
	)

	const { open: openPanel, close: closePanel } = useMemo(
		() => createPanelControls(instanceRef, "elevation-references-panel", setIsPanelOpen),
		[],
	)
	// The right sidebar's open state is now declared rather than applied on the spot: which SLOT the
	// primary pane's sidebar lives in depends on split screen (Apryse's right panel normally, the
	// middle dock while split — review #3), and while split is open the Apryse panel belongs to the
	// right pane's own sidebar instead (review #4). The reconcile effect below is the single place
	// that opens/closes the Apryse panel, so those two owners can never fight over it.
	const openReferencePanel = useCallback(() => setIsReferencePanelOpen(true), [])
	const closeReferencePanel = useCallback(() => setIsReferencePanelOpen(false), [])
	const setCreateAreaPanelDefaultWidth = useCallback(() => {
		let attemptsLeft = 5
		const applyWidth = () => {
			const panel = sidebarRightEntryRef.current?.container.closest<HTMLElement>('[data-element="reference-panel"]')
			const panelContainer = panel?.closest<HTMLElement>(".ModularPanel-container")
			if (panelContainer) {
				panelContainer.style.width = CREATE_AREA_PANEL_DEFAULT_WIDTH
				panelContainer.style.minWidth = CREATE_AREA_PANEL_DEFAULT_WIDTH
				return
			}
			attemptsLeft -= 1
			if (attemptsLeft > 0) requestAnimationFrame(applyWidth)
		}
		requestAnimationFrame(applyWidth)
	}, [])
	const clearCreateAreaPanelMinWidth = useCallback(() => {
		const panel = sidebarRightEntryRef.current?.container.closest<HTMLElement>('[data-element="reference-panel"]')
		const panelContainer = panel?.closest<HTMLElement>(".ModularPanel-container")
		if (panelContainer) panelContainer.style.minWidth = ""
	}, [])
	// The widened min-width belongs to the create-area panels and must not outlive them — left on the
	// shared `.ModularPanel-container` it would stop every other sidebar from being resized down for
	// the rest of the session (#1319 review). Every mode that sets it has to be listed here.
	useEffect(() => {
		if (
			rightPanelMode === "create-countertop" ||
			rightPanelMode === "create-ecall" ||
			rightPanelMode === "create-furniture" ||
			rightPanelMode === "create-other" ||
			rightPanelMode === "create-custom"
		) {
			return
		}
		clearCreateAreaPanelMinWidth()
	}, [clearCreateAreaPanelMinWidth, rightPanelMode])

	// Who owns the Apryse right panel right now: the split pane's sidebar while split screen is
	// open, the primary pane's otherwise.
	const isRightPanelOpen = isSplitPaneOpen ? splitSidebar !== null : isReferencePanelOpen
	useEffect(() => {
		const instance = instanceRef.current
		if (!instance || !isInitialized) return
		if (isRightPanelOpen) instance.UI.openElements(["reference-panel"])
		else instance.UI.closeElements(["reference-panel"])
	}, [isRightPanelOpen, isInitialized])

	// Split screen is the only thing that gives the right pane a sidebar — leaving it must not
	// strand that selection (or hold the Apryse panel open with nothing to show).
	useEffect(() => {
		if (!isSplitPaneOpen) setSplitSidebar(null)
	}, [isSplitPaneOpen])

	// Floating page-navigation bar (pills + page-name jump). Self-contained editor affordance;
	// registerPageNavListeners is called synchronously from init so its documentLoaded listener
	// is attached before the first document load. isPageNavVisible feeds the overlay reposition.
	const { isPageNavVisible, registerPageNavListeners } = usePageNavigation({
		instanceRef,
		mode,
		isModalOpen,
		isDocumentLoaded,
		relatedObjects,
		relatedObjectsRef,
		getShadowRoot,
		pageNavEntryRef,
		layoutEpoch,
	})

	// Second page-navigation bar, for the split-screen pane (REQ-399). Each pane reports and
	// navigates its OWN document viewer, so the pills track that pane's scrolling/zooming
	// independently. Disabled (listeners detached, bar hidden) whenever split screen is closed.
	const getSplitDocViewer = useCallback(() => instanceRef.current?.Core.getDocumentViewers?.()[1] ?? null, [])
	usePageNavigation({
		instanceRef,
		mode,
		isModalOpen,
		isDocumentLoaded,
		relatedObjects,
		relatedObjectsRef,
		getShadowRoot,
		pageNavEntryRef: splitPageNavEntryRef,
		getDocViewer: getSplitDocViewer,
		isPrimaryPane: false,
		layoutEpoch,
		isEnabled: isSplitPaneOpen,
	})

	// `pane` picks which sidebar the entity opens in: "primary" (the default, and the only option in
	// single-viewer mode) drives the left screen's sidebar — the middle dock while split screen is
	// open, Apryse's right panel otherwise. "split" drives the right screen's own sidebar and
	// focuses that pane, which is what makes an eref+ecall pair viewable side by side (#1264 #4).
	// `additive` marks a selection made BY smart navigation as the Secondary pane's counterpart: it is
	// added to the user's selection instead of replacing it (AnnotationService.selectEntityAsCounterpart),
	// so both panes show handles and the eref pill / Delete keep acting on what the user clicked.
	type TSelectOptions = { scroll?: boolean; pane?: TViewerPane; additive?: boolean }
	const closeCreateEcallRef = useRef<() => boolean>(() => false)

	const handleErefClick = useCallback(
		(eref: TErefWithFurniture, { scroll = true, pane = "primary", additive = false }: TSelectOptions = {}) => {
			smartNavDebug("erefClick", { erefId: eref.id, pane, scroll, additive })
			if (createSessionRef.current !== null) {
				if (closeCreateErefRef.current?.() !== true) return
			}
			if (countertopCreateSessionRef.current !== null) {
				if (closeCountertopCreateRef.current?.() !== true) return
			}
			if (createFurnitureSessionRef.current !== null) {
				if (closeCreateFurnitureRef.current?.() !== true) return
			}
			if (createOtherSessionRef.current !== null) {
				if (closeCreateOtherRef.current?.() !== true) return
			}
			if (createCustomSessionRef.current !== null) {
				if (closeCreateCustomRef.current?.() !== true) return
			}
			if (rightPanelModeRef.current === "create-ecall") {
				closeCreateEcallRef.current()
			}
			if (isCanvasModeActive()) {
				return
			}
			if (pane === "split") {
				setSplitSidebar({ mode: "eref", eref })
				if (scroll) {
					AnnotationService.scrollToEref(eref.id, "split", additive ? SMART_NAV_MAX_ZOOM : undefined)
					selectInPaneSilently("split", () =>
						additive
							? AnnotationService.selectEntityAsCounterpart("eref", eref.id, "primary")
							: AnnotationService.selectEref(eref.id),
					)
				}
				notifySmartNavSelection({ kind: "eref", eref }, "split", scroll)
				return
			}
			selectedErefRef.current = eref
			setSelectedEref(eref)
			setRightPanelMode("eref")
			openReferencePanel()
			if (scroll) {
				AnnotationService.scrollToEref(eref.id, "primary", additive ? SMART_NAV_MAX_ZOOM : undefined)
				selectInPaneSilently("primary", () =>
					additive
						? AnnotationService.selectEntityAsCounterpart("eref", eref.id, "split")
						: AnnotationService.selectEref(eref.id),
				)
			}
			notifySmartNavSelection({ kind: "eref", eref }, "primary", scroll)
		},
		[openReferencePanel, isCanvasModeActive, notifySmartNavSelection, selectInPaneSilently],
	)

	const handleFurnitureClick = useCallback(
		(furniture: TFurniture, { scroll = true, pane = "primary" }: TSelectOptions = {}) => {
			if (createSessionRef.current !== null) {
				if (closeCreateErefRef.current?.() !== true) return
			}
			if (countertopCreateSessionRef.current !== null) {
				if (closeCountertopCreateRef.current?.() !== true) return
			}
			if (createFurnitureSessionRef.current !== null) {
				if (closeCreateFurnitureRef.current?.() !== true) return
			}
			if (createOtherSessionRef.current !== null) {
				if (closeCreateOtherRef.current?.() !== true) return
			}
			if (createCustomSessionRef.current !== null) {
				if (closeCreateCustomRef.current?.() !== true) return
			}
			if (rightPanelModeRef.current === "create-ecall") {
				closeCreateEcallRef.current()
			}
			if (isCanvasModeActive()) {
				return
			}
			if (pane === "split") {
				setSplitSidebar({ mode: "furniture", furniture })
				if (scroll) {
					AnnotationService.scrollToFurniture(furniture.id, "split")
					selectInPaneSilently("split", () => AnnotationService.selectFurniture(furniture.id))
				}
				// A furniture's counterpart is its parent eref's ecalls (PR #1347 review) — same machine
				// erefs already drive, just a third source kind. See smartNavigation.helpers.ts.
				notifySmartNavSelection({ kind: "furniture", furniture }, "split", scroll)
				return
			}
			selectedFurnitureRef.current = furniture
			setSelectedFurniture(furniture)
			setRightPanelMode("furniture")
			setFurnitureInitialTab("Details")
			openReferencePanel()
			if (scroll) {
				AnnotationService.scrollToFurniture(furniture.id)
				selectInPaneSilently("primary", () => AnnotationService.selectFurniture(furniture.id))
			}
			notifySmartNavSelection({ kind: "furniture", furniture }, "primary", scroll)
		},
		[openReferencePanel, isCanvasModeActive, notifySmartNavSelection, selectInPaneSilently],
	)

	// Closing the object sidebar CLOSES the right panel — it never falls back to the eref sidebar
	// (#1212). `selectedEref` outlives the panel it was opened from (the left sidebar's eref group
	// header both expands the group and selects the eref, and nothing clears it afterwards), so the
	// old fallback made the two sidebars behave like a stack: open an elevation, open one of its
	// objects on top, close the object — and an elevation sidebar the user had already moved on from
	// came back. The explicit way up to the parent elevation is the sidebar's own Back control
	// (`onNavigateToEref`), which is offered whenever the object has one.
	const handleFurnitureClose = useCallback(() => {
		setSelectedFurniture(undefined)
		selectedFurnitureRef.current = undefined
		setRightPanelMode(null)
		closeReferencePanel()
	}, [closeReferencePanel])

	// Same contract for the elevation sidebar's own close: the panel and the selection behind it go
	// away together. Leaving `selectedEref` set kept a dismissed sidebar half-open — it still drove
	// the left sidebar's page highlight and the Alt+↑/↓ stepping cursor, and any later close of
	// another sidebar resurfaced it (#1212). Deliberate re-entry (an eref row, an eref highlight,
	// an object's Back control) sets the selection again on its way in.
	const handleErefClose = useCallback(() => {
		setSelectedEref(undefined)
		selectedErefRef.current = undefined
		setRightPanelMode(null)
		closeReferencePanel()
	}, [closeReferencePanel])

	const handleEcallClick = useCallback(
		(ecall: TEcall, { scroll = true, pane = "primary", additive = false }: TSelectOptions = {}) => {
			smartNavDebug("ecallClick", { ecallId: ecall.id, pane, scroll, additive })
			if (createSessionRef.current !== null) {
				if (closeCreateErefRef.current?.() !== true) return
			}
			if (countertopCreateSessionRef.current !== null) {
				if (closeCountertopCreateRef.current?.() !== true) return
			}
			if (createFurnitureSessionRef.current !== null) {
				if (closeCreateFurnitureRef.current?.() !== true) return
			}
			if (createOtherSessionRef.current !== null) {
				if (closeCreateOtherRef.current?.() !== true) return
			}
			if (createCustomSessionRef.current !== null) {
				if (closeCreateCustomRef.current?.() !== true) return
			}
			if (rightPanelModeRef.current === "create-ecall") {
				closeCreateEcallRef.current()
			}
			if (isCanvasModeActive()) return
			if (pane === "split") {
				setSplitSidebar({ mode: "ecall", ecall })
				if (scroll) {
					AnnotationService.scrollToEcall(ecall.id, "split", additive ? SMART_NAV_MAX_ZOOM : undefined)
					selectInPaneSilently("split", () =>
						additive
							? AnnotationService.selectEntityAsCounterpart("ecall", ecall.id, "primary")
							: AnnotationService.selectEcall(ecall.id),
					)
				}
				notifySmartNavSelection({ kind: "ecall", ecall }, "split", scroll)
				return
			}
			selectedEcallRef.current = ecall
			setSelectedEcall(ecall)
			setRightPanelMode("ecall")
			openReferencePanel()
			setCreateAreaPanelDefaultWidth()
			if (scroll) {
				AnnotationService.scrollToEcall(ecall.id, "primary", additive ? SMART_NAV_MAX_ZOOM : undefined)
				selectInPaneSilently("primary", () =>
					additive
						? AnnotationService.selectEntityAsCounterpart("ecall", ecall.id, "split")
						: AnnotationService.selectEcall(ecall.id),
				)
			}
			notifySmartNavSelection({ kind: "ecall", ecall }, "primary", scroll)
		},
		[
			openReferencePanel,
			isCanvasModeActive,
			setCreateAreaPanelDefaultWidth,
			notifySmartNavSelection,
			selectInPaneSilently,
		],
	)

	// Closing the right screen's sidebar only clears ITS selection — the left screen's sidebar,
	// docked between the panes, is a separate surface and stays exactly as it was.
	const handleSplitSidebarClose = useCallback(() => setSplitSidebar(null), [])

	// Close whichever detail sidebar a PANE owns. Smart navigation (#1311) opens the Secondary pane's
	// sidebar to show the counterpart entity, so ending the session has to take it back down again —
	// otherwise it is left on screen describing an entity that nothing is selecting any more, which
	// is a state the design never draws. Which surface that is depends on the pane: the split pane
	// has its own `splitSidebar` state, the primary pane uses the shared right panel.
	const closePaneSidebar = useCallback(
		(pane: TViewerPane) => {
			if (pane === "split") {
				setSplitSidebar(null)
				return
			}
			selectedEcallRef.current = undefined
			setSelectedEcall(undefined)
			selectedErefRef.current = undefined
			setSelectedEref(undefined)
			setRightPanelMode(null)
			closeReferencePanel()
		},
		[closeReferencePanel],
	)

	// Post-delete cleanup for the split screen's sidebar. The primary right panel is closed inside
	// runBatchDelete, but that hook doesn't know about this second surface — without this it would
	// keep editing an object the backend has already removed.
	const handleFurnitureDeleted = useCallback((furnitureIds: number[]) => {
		setSplitSidebar((current) =>
			current?.mode === "furniture" && furnitureIds.includes(current.furniture.id) ? null : current,
		)
	}, [])

	const handleEcallClose = useCallback(() => {
		setSelectedEcall(undefined)
		selectedEcallRef.current = undefined
		setRightPanelMode(null)
		closeReferencePanel()
	}, [closeReferencePanel])

	// Escape dismisses the right panel from OUTSIDE any sidebar, so it has to pick the close handler
	// of whatever is showing — otherwise the keyboard path leaves exactly the stale selection the
	// three handlers above exist to clear. The create-* modes never reach this: Escape offers them
	// to onCancelCanvasMode first (see the hotkey's precedence comment), which cancels the session.
	const handleRightPanelDismiss = useCallback(() => {
		switch (rightPanelModeRef.current) {
			case "eref":
				handleErefClose()
				return
			case "furniture":
				handleFurnitureClose()
				return
			case "ecall":
				handleEcallClose()
				return
			default:
				closeReferencePanel()
		}
	}, [handleErefClose, handleFurnitureClose, handleEcallClose, closeReferencePanel])

	// Open the "Set page name" modal for a page from the left sidebar's hover Edit affordance.
	const handleEditPage = useCallback((payload: TEditPagePayload) => {
		setPageNameError(null)
		setPageNameEdit(payload)
	}, [])

	// Persist a new page name (REQ-371). Sends ONLY `name` + the current `version`; on success the
	// backend returns the updated page_info + affected erefs, which we patch straight into the
	// quotation cache so the fresh names show everywhere without a refetch. Backend rejections
	// (unknown page name, version mismatch) are surfaced inline in the modal and keep it open for a retry.
	const handleSavePageName = useCallback(
		async (name: string) => {
			const target = pageNameEdit
			const quotationIdValue = numericQuotationIdRef.current
			if (!target || quotationIdValue == null) return
			// The PATCH is keyed by the page-info record's id + version (joined from page_infos[]).
			// If either is somehow absent the edit can't be persisted — surface that inline instead
			// of firing a malformed request. (Defensive: the backend normally returns both.)
			if (target.pageInfoId == null || target.version == null) {
				setPageNameError("Renaming isn't available for this page yet — the server response is missing its page id.")
				return
			}
			setPageNamePending(true)
			setPageNameError(null)
			try {
				const res = await updatePageAsync({
					quotationId: quotationIdValue,
					pageInfoId: target.pageInfoId,
					body: { version: target.version, name },
				})
				// Patch the cache directly from the response instead of refetching the heavy GET
				// quotation — the PATCH returns the updated page_info + affected erefs, which is all
				// the UI reads (page label, eref rows, eref-detail Page field).
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(
					["quotation", quotationIdValue],
					(cached) => applyPageUpdateToCache(cached, res.data),
				)
				success("Page name updated")
				setPageNameEdit(null)
			} catch (err) {
				// Only a version-mismatch needs fresh page data before retrying (the modal stays
				// open) — the refetch re-seeds the open modal's version via the sync effect so an
				// immediate retry uses the current value. Name-validation errors leave the version
				// valid, so skip the heavy GET quotation refetch for them.
				if (isVersionConflictError(err)) {
					await queryClient.invalidateQueries({ queryKey: ["quotation", quotationIdValue] })
				}
				setPageNameError(extractApiErrorMessage(err))
			} finally {
				setPageNamePending(false)
			}
		},
		[pageNameEdit, updatePageAsync, queryClient, success],
	)

	// Keep the open "Set page name" modal's version in sync with refreshed page_infos. After a
	// version-mismatch refetch this re-seeds pageNameEdit.version so an immediate retry sends the
	// current version rather than the stale one the modal opened with. Only `version` is synced (not
	// `pageName`) so the user's in-progress input isn't clobbered by the modal's prefill effect. The
	// equality guard makes this converge (no update loop).
	useEffect(() => {
		if (pageNameEdit?.pageInfoId == null) return
		const info = quotationDetail?.page_infos?.find((p) => p.id === pageNameEdit.pageInfoId)
		if (info == null || info.version === pageNameEdit.version) return
		setPageNameEdit((prev) => (prev ? { ...prev, version: info.version } : prev))
	}, [quotationDetail?.page_infos, pageNameEdit])

	const findEcallById = useCallback((ecallId: number): TEcall | undefined => {
		const pages = relatedObjectsRef.current
		if (!pages) return undefined
		for (const page of pages) {
			const ecall = page.ecalls.find((e) => e.id === ecallId)
			if (ecall) return ecall
		}
		return undefined
	}, [])
	const findErefWithFurnitureById = useCallback((erefId: number): TErefWithFurniture | undefined => {
		const pages = relatedObjectsRef.current
		if (!pages) return undefined
		for (const page of pages) {
			const eref = page.erefs.find((e) => e.id === erefId)
			if (eref) {
				const furniture = page.furniture.filter((f) => f.eref_id === erefId)
				return { ...eref, furniture }
			}
		}
		return undefined
	}, [])

	const findFurnitureById = useCallback((furnitureId: number): TFurniture | undefined => {
		const pages = relatedObjectsRef.current
		if (!pages) return undefined
		for (const page of pages) {
			const furniture = page.furniture.find((f) => f.id === furnitureId)
			if (furniture) return furniture
		}
		return undefined
	}, [])

	// Delegates to handleErefClick (default options: primary pane, scroll, non-additive) instead of
	// its own hand-rolled scroll+select, which used to skip both notifySmartNavSelection AND the
	// selectInPaneSilently re-entrancy wrapper — so Alt+↑/↓ eref stepping (handleStepConnectedEref)
	// and Alt+← (handleNavigateFurnitureToEref) never drove the Secondary pane under Auto Open Floor
	// Plan, even though an identical eref-sidebar row CLICK (wired straight to handleErefClick) did.
	const openErefInSidebar = useCallback(
		(eref: TErefWithFurniture) => {
			handleErefClick(eref)
		},
		[handleErefClick],
	)

	// Returns whether it navigated, so the Alt+← hotkey knows to move keyboard focus into the
	// reopened eref sidebar. No-ops (and returns false) when the furniture has no connected eref.
	const handleNavigateFurnitureToEref = useCallback(
		(furniture: TFurniture): boolean => {
			if (furniture.eref_id == null) return false
			const eref = findErefWithFurnitureById(furniture.eref_id)
			if (!eref) return false
			openErefInSidebar(eref)
			return true
		},
		[findErefWithFurnitureById, openErefInSidebar],
	)

	// Navigation INSIDE the right screen's sidebar stays in the right screen (review #4 on #1264):
	// opening a connected callout, or stepping from a furniture up to its eref, re-targets that
	// pane's own sidebar and focuses that pane. The jump sessions and their floating bar remain a
	// left-screen affordance — they drive the shared selection the whole editor reads.
	const handleSplitEcallView = useCallback(
		(ecall: TEcall) => handleEcallClick(ecall, { pane: "split" }),
		[handleEcallClick],
	)

	const handleSplitFurnitureView = useCallback(
		(furniture: TFurniture) => handleFurnitureClick(furniture, { pane: "split" }),
		[handleFurnitureClick],
	)

	const handleSplitNavigateFurnitureToEref = useCallback(
		(furniture: TFurniture): boolean => {
			if (furniture.eref_id == null) return false
			const eref = findErefWithFurnitureById(furniture.eref_id)
			if (!eref) return false
			handleErefClick(eref, { pane: "split" })
			return true
		},
		[findErefWithFurnitureById, handleErefClick],
	)

	const handleSplitEcallErefView = useCallback(
		(erefId: number) => {
			const eref = findErefWithFurnitureById(erefId)
			if (eref) handleErefClick(eref, { pane: "split" })
		},
		[findErefWithFurnitureById, handleErefClick],
	)

	// Open an entity in a NAMED pane's sidebar. The pane-aware jump sessions of smart navigation
	// (#1311) apply their items through these rather than writing the primary selection state
	// directly, so a Secondary-pane target lands in that pane's sidebar and scrolls that pane.
	const openErefInPane = useCallback(
		(eref: TErefWithFurniture, pane: TViewerPane, additive = false) => handleErefClick(eref, { pane, additive }),
		[handleErefClick],
	)
	const openEcallInPane = useCallback(
		(ecall: TEcall, pane: TViewerPane, additive = false) => handleEcallClick(ecall, { pane, additive }),
		[handleEcallClick],
	)
	// Furniture is a jump SOURCE only (never a counterpart), so `backToJumpSourceInner` is this one's
	// only caller — but it needs the same pane-aware routing as the eref/ecall pair above to return a
	// split-pane-sourced session to the split pane instead of always reopening on primary.
	const openFurnitureInPane = useCallback(
		(furniture: TFurniture, pane: TViewerPane) => handleFurnitureClick(furniture, { pane }),
		[handleFurnitureClick],
	)

	// Live per-pane document-area lookup, resolved through the ref the overlay containers fill in at
	// init. Never cache the element: entering/leaving split screen replaces it (see viewerPanes).
	const getPaneDocAreaEl = useCallback((pane: TViewerPane) => getPaneDocAreaElRef.current?.(pane) ?? null, [])

	// Keep the right screen's sidebar fresh across refetches, the same way the primary pane's
	// selection is re-resolved above. Re-resolving by id is what makes an edit made in either
	// sidebar show up in the other when both have the same entity open.
	useEffect(() => {
		if (!relatedObjects) return
		setSplitSidebar((current) => {
			if (!current) return current
			if (current.mode === "eref") {
				const next = findErefWithFurnitureById(current.eref.id)
				return next ? { mode: "eref", eref: next } : current
			}
			if (current.mode === "furniture") {
				const next = findFurnitureById(current.furniture.id)
				return next && next !== current.furniture ? { mode: "furniture", furniture: next } : current
			}
			const next = findEcallById(current.ecall.id)
			return next && next !== current.ecall ? { mode: "ecall", ecall: next } : current
		})
	}, [relatedObjects, findErefWithFurnitureById, findFurnitureById, findEcallById])

	// Steps through the displayed eref list (connectedErefIds — same order as the left sidebar)
	// with wrap-around. Returns whether it navigated so the Alt+↓/↑ hotkeys can move focus.
	const handleStepConnectedEref = useCallback(
		(direction: "next" | "prev"): boolean => {
			if (connectedErefIds.length === 0) return false
			const currentId = selectedErefRef.current?.id
			const currentIdx = currentId != null ? connectedErefIds.indexOf(currentId) : -1
			const delta = direction === "next" ? 1 : -1
			const nextIdx =
				currentIdx === -1
					? direction === "next"
						? 0
						: connectedErefIds.length - 1
					: (currentIdx + delta + connectedErefIds.length) % connectedErefIds.length
			const eref = findErefWithFurnitureById(connectedErefIds[nextIdx])
			if (!eref) return false
			openErefInSidebar(eref)
			return true
		},
		[connectedErefIds, findErefWithFurnitureById, openErefInSidebar],
	)

	// Alt+↓ / Alt+↑: next / previous eref. Scoped to eref mode only — a no-op (returns false)
	// while a furniture or ecall is selected, per REQ-360 task 2.
	const navigateAdjacentEref = useCallback(
		(direction: "next" | "prev"): boolean => {
			if (!isReferencePanelOpenRef.current || rightPanelModeRef.current !== "eref") return false
			return handleStepConnectedEref(direction)
		},
		[handleStepConnectedEref],
	)
	const navigateNextEref = useCallback(() => navigateAdjacentEref("next"), [navigateAdjacentEref])
	const navigatePrevEref = useCallback(() => navigateAdjacentEref("prev"), [navigateAdjacentEref])

	// Alt+←: from a furniture, open its connected eref. Scoped to furniture mode only — a no-op
	// (returns false) while an eref or ecall is selected, per REQ-360 task 3.
	const navigateFurnitureToParentEref = useCallback((): boolean => {
		if (!isReferencePanelOpenRef.current || rightPanelModeRef.current !== "furniture") return false
		const furniture = selectedFurnitureRef.current
		if (!furniture) return false
		return handleNavigateFurnitureToEref(furniture)
	}, [handleNavigateFurnitureToEref])

	const { copyFurnitureToClipboard, pasteFurnitureAtPoint, duplicateFurnitureInPlace } = useFurnitureClipboard({
		numericQuotationIdRef,
		relatedObjectsRef,
		duplicateFurnitureAsync,
		queryClient,
		success,
		formatError,
		handleFurnitureClick,
		findFurnitureById,
		findErefWithFurnitureById,
	})

	// ---- Batch selection toolbar (REQ-369) ---------------------------------------------------

	const {
		getSelectedFurniture,
		positionSelectionToolbar,
		syncSelectionToolbar,
		openConnectModal,
		runConnectOrMove,
		runDetach,
		runBatchStatus,
		runSingleFurnitureStatus,
		runBatchDelete,
		getReviewToggle,
	} = useBatchOperations({
		numericQuotationIdRef,
		relatedObjectsRef,
		selectionToolbarEntryRef,
		selectedFurnitureRef,
		modeRef,
		setSelectionToolbar,
		setConnectModal,
		setDeleteConfirm,
		setBatchPending,
		moveFurnitureAsync,
		disconnectFurnitureAsync,
		batchStatusAsync,
		updateFurnitureStatusAsync,
		batchDeleteAsync,
		findFurnitureById,
		findErefWithFurnitureById,
		findEcallById,
		handleFurnitureClose,
		onFurnitureDeleted: handleFurnitureDeleted,
		queryClient,
		success,
		error,
		formatError,
	})

	// ---- Batch furniture edit (REQ-403) -----------------------------------------------------
	// The `furniture-batch` right panel's behaviour: which objects it edits, and the seven batch
	// endpoints its fields commit through. Declared here (after handleFurnitureClick, which it hands
	// over to when a batch shrinks to one object) so the annotationSelected handler below can call
	// its selection sync.
	const {
		batchFurniture,
		isBatchEditPending,
		skipCategoryConfirm,
		setSkipCategoryConfirm,
		syncBatchFurnitureSelection,
		closeBatchFurniture,
		commitDimension: commitBatchDimension,
		commitDeliveryDate: commitBatchDeliveryDate,
		commitModelNumber: commitBatchModelNumber,
		commitCategory: commitBatchCategory,
		commitConfig: commitBatchConfig,
		commitCountertopAttachments: commitBatchCountertopAttachments,
		commitPrice: commitBatchPrice,
		commitName: commitBatchName,
		commitNotes: commitBatchNotes,
		commitCustomization: commitBatchCustomization,
		removeCustomization: removeBatchCustomization,
	} = useBatchFurnitureEdit({
		numericQuotationIdRef,
		relatedObjects,
		rightPanelModeRef,
		setRightPanelMode,
		openReferencePanel,
		closeReferencePanel,
		isCanvasModeActive,
		handleFurnitureClick,
		batchUpdateFurnitureAsync,
		batchUpdateCategoryAsync,
		batchUpdateCustomModelNumberAsync,
		batchSelectArbitraryModelNumberAsync,
		batchUpdateConfigAsync,
		batchUpdateCountertopAttachmentsAsync,
		batchUpdateFixedPriceAsync,
		batchUpdateCommentAsync,
		batchDeleteCommentAsync,
		batchUpdateCustomizationAsync,
		batchDeleteCustomizationAsync,
		queryClient,
		formatError,
	})

	// The elevation link of a whole selection: reuse the batch toolbar's own transactional flows —
	// `move` reassigns every object regardless of its current link, `detach` clears them.
	const handleBatchErefChange = useCallback(
		(erefId: number | null) => {
			if (batchFurniture.length === 0) return
			if (erefId === null) {
				void runDetach(batchFurniture)
				return
			}
			void runConnectOrMove(erefId, batchFurniture)
		},
		[batchFurniture, runDetach, runConnectOrMove],
	)

	// ---- Eref connect objects (REQ-372) -----------------------------------------------------
	// Opened from the eref context menu. Owns the top bar, the drag/click object picking and the
	// commit, which reuses runConnectOrMove above so an object on another eref is reassigned and the
	// cache/version/recolor handling is identical to the furniture-side connect.
	const clearSelectionToolbar = useCallback(() => setSelectionToolbar(null), [])

	// ---- Figure highlight tool (REQ-400) -----------------------------------------------------
	// Declared alongside connect-objects/eref-create below: all are mutually exclusive canvas
	// modes. cancelOtherCanvasModes is built first (pure ref-indirection, no ordering dependency)
	// so both the select hook's picking-mode toggle and the create hook's shape-tool arm can use it
	// to unconditionally take over from whatever else is open (#1323 review) — mirrors
	// handleAddElevationReference / handleAddCabinet / etc. below, none of which silently no-op.
	const cancelOtherCanvasModes = useCallback(() => {
		cancelConnectObjectsRef.current()
		closeCreateErefRef.current?.()
		closeCountertopCreateRef.current?.()
		closeCreateEcallRef.current?.()
		closeCreateFurnitureRef.current?.()
		closeCreateOtherRef.current?.()
		clearHighlightSelectionRef.current?.()
		// The note tool counts as another canvas mode here: arming a shape while a note was armed —
		// or while one was merely SELECTED — otherwise left the note tool-options row up alongside
		// the shape's, two stacked bars in the header.
		disarmNoteToolRef.current?.()
		deselectNoteRef.current?.()
	}, [])

	// The select hook is declared first so its `selectHighlightById` can be handed to the create
	// hook for decision 2 (a freshly-drawn highlight is immediately selected, showing the trash
	// toolbar right away).
	const {
		selectedHighlight,
		trySelectHighlightFromMouseEvent,
		clearHighlightSelection,
		deleteSelectedHighlight,
		isDeletingHighlight,
		selectHighlightById,
	} = useHighlightSelect({
		instanceRef,
		getViewerPanes,
		layoutEpoch,
		mode,
		isInitialized,
		numericQuotationIdRef,
		queryClient,
		formatError,
		success,
		highlightToolbarEntryRef,
	})
	clearHighlightSelectionRef.current = clearHighlightSelection

	// activeShapeTool / the chosen color are read by CustomWebViewerHeader directly from
	// figureHighlight.store (its render() captures props once — see the store's own comment), so
	// only the two stable action callbacks are needed here.
	const { armShapeTool, disarmShapeTool } = useFigureHighlightCreate({
		instanceRef,
		mode,
		layoutEpoch,
		numericQuotationIdRef,
		queryClient,
		formatError,
		cancelOtherCanvasModes,
		clearSelectionToolbar,
		onHighlightCreated: selectHighlightById,
	})

	// REQ-400 #1324. Same shape as the figure tool above; armed state is read by the header straight
	// from figureHighlight.store, so only the action callbacks come back here.
	const { disarmFreehandTool, toggleFreehandTool } = useFreehandHighlightCreate({
		instanceRef,
		mode,
		isInitialized,
		layoutEpoch,
		numericQuotationIdRef,
		queryClient,
		formatError,
		cancelOtherCanvasModes,
		clearSelectionToolbar,
		onHighlightCreated: selectHighlightById,
	})

	// Both highlight tools in one call. Every "take over the canvas" entry point below (the plain
	// viewer tools, each + Add flow, Escape) has to clear whichever is armed, and adding the second
	// tool to eight separate call sites is exactly how one gets missed. They are mutually exclusive,
	// so at most one of the two actually does anything. Returns whether it disarmed something, which
	// is the contract the Escape chain needs.
	const disarmHighlightTools = useCallback((): boolean => {
		const wasShapeArmed = disarmShapeTool()
		const wasFreehandArmed = disarmFreehandTool()
		return wasShapeArmed || wasFreehandArmed
	}, [disarmShapeTool, disarmFreehandTool])

	// REQ-400 review (#1381) + REQ-395/396: switching to Pan/Select/Zoom/Ruler from the header buttons
	// or their hotkeys left an armed highlight tool's tool-options row stuck open (and, for the same
	// reason, the note tool armed with its button stuck blue) — `onToolChange`/`onToggleRulerTool`
	// (from useCanvasTools, declared before disarmShapeTool exists) change the real Apryse tool mode
	// directly and have no idea any of those three stores exist. Every OTHER canvas-mode entry point
	// already disarms all three at its own start (see the handleAddX functions below); the plain
	// viewer tools were the one gap. ONE set of wrappers disarms all of them — a second competing set
	// per tool would mean whichever one the header happened to be wired to left the others armed.
	// Wrapped here rather than in useCanvasTools itself, which has no reason to know about any of
	// them — these wrapped versions are what the header/hotkeys use from here on, while the
	// create-flow hooks above (already declared, already disarming through their own entry points)
	// keep using the raw ones they captured before this existed. The note tool goes through
	// `disarmNoteToolRef` because `useNoteCreate` is declared further down still.
	const disarmCanvasTools = useCallback(() => {
		disarmHighlightTools()
		disarmNoteToolRef.current?.()
	}, [disarmHighlightTools])
	const handleToolChange = useCallback(
		(tool: TViewerTool, zoomDirection?: TQuotationZoomDirection) => {
			disarmCanvasTools()
			onToolChange(tool, zoomDirection)
		},
		[disarmCanvasTools, onToolChange],
	)
	const handleZoomTool = useCallback(
		(event: KeyboardEvent) => {
			disarmCanvasTools()
			onZoomTool(event)
		},
		[disarmCanvasTools, onZoomTool],
	)
	const handleToggleRulerTool = useCallback(() => {
		disarmCanvasTools()
		onToggleRulerTool()
	}, [disarmCanvasTools, onToggleRulerTool])

	const { connectSession, selectedConnectIds, startConnectObjects, cancelConnectObjects, runConnectObjects } =
		useErefConnectObjects({
			instanceRef,
			connectSessionRef,
			connectMarqueeElRef,
			appContainerRef: jumpingNavTargetContainerRef,
			relatedObjects,
			findFurnitureById,
			findErefWithFurnitureById,
			runConnectOrMove,
			clearSelectionToolbar,
		})
	cancelConnectObjectsRef.current = cancelConnectObjects

	// ---- Add elevation reference (REQ-292) --------------------------------------------------
	// Opened from the header's + Add menu. Owns the draw-the-area gesture on the canvas and the
	// create request; the right panel's create form is rendered further below, driven by the session
	// this returns. Declared right after connect-objects because the two are mutually exclusive
	// canvas modes and share the same guards (isCanvasModeActive).
	const handleErefCreated = useCallback(
		(created: TEref) => {
			// Hand the panel over to the normal eref sidebar with the new elevation selected, exactly as
			// if it had been clicked on the canvas. It has no furniture yet, so its list starts empty.
			openErefInSidebar({ ...created, furniture: [] })
		},
		[openErefInSidebar],
	)

	const { createSession, isCreatePending, startCreateEref, cancelCreateEref, runCreateEref } = useErefCreate({
		createSessionRef,
		erefDraftRectRef,
		appContainerRef: jumpingNavTargetContainerRef,
		getViewerPanes,
		layoutEpoch,
		numericQuotationIdRef,
		createErefAsync,
		queryClient,
		onErefCreated: handleErefCreated,
		onToolChange,
		clearSelectionToolbar,
		success,
		formatError,
	})

	// Entering a mode from the header. Must stay referentially stable: the custom header's Apryse
	// render() callback captures these once (see core/viewerModes).
	//
	// Each entry point closes EVERY other canvas mode, not just the one it happens to know about:
	// they all share the single draft box and bind mousedown on the same pane scroll elements, so
	// two live sessions would fight over one drag (#1309 review). The closers declared further down
	// are reached through refs, which is what keeps the declaration order free.
	const handleAddElevationReference = useCallback(() => {
		disarmHighlightTools()
		clearHighlightSelectionRef.current?.()
		cancelConnectObjects()
		closeCreateEcallRef.current?.()
		closeCountertopCreateRef.current?.()
		closeCreateFurnitureRef.current?.()
		closeCreateOtherRef.current?.()
		closeCreateCustomRef.current?.()
		disarmNoteToolRef.current?.()
		startCreateEref()
		setRightPanelMode("create-eref")
		openReferencePanel()
	}, [disarmHighlightTools, cancelConnectObjects, startCreateEref, openReferencePanel])

	// Leaving the mode, from Cancel / the panel's X / Escape. Also drops the right panel, which has
	// nothing left to show once the create form is gone. Returns whether a session was actually
	// closed, which is what lets Escape stop here instead of falling through.
	const closeCreateEref = useCallback((): boolean => {
		if (!cancelCreateEref()) return false
		if (rightPanelModeRef.current === "create-eref") {
			setRightPanelMode(null)
			closeReferencePanel()
		}
		return true
	}, [cancelCreateEref, closeReferencePanel])
	closeCreateErefRef.current = closeCreateEref

	// ---- Add countertop (REQ-298) -----------------------------------------------------------
	const handleCountertopCreated = useCallback(
		(created: TFurniture) => {
			handleFurnitureClick(created, { scroll: false })
		},
		[handleFurnitureClick],
	)

	const {
		createSession: countertopCreateSession,
		isCreatePending: isCountertopCreatePending,
		startCreateCountertop,
		cancelCreateCountertop,
		runCreateCountertop,
	} = useCountertopCreate({
		createSessionRef: countertopCreateSessionRef,
		draftRectRef: erefDraftRectRef,
		appContainerRef: jumpingNavTargetContainerRef,
		getViewerPanes,
		layoutEpoch,
		numericQuotationIdRef,
		queryClient,
		onFurnitureCreated: handleCountertopCreated,
		onToolChange,
		clearSelectionToolbar,
		success,
		formatError,
	})

	// ---- Add cabinet (REQ-293) --------------------------------------------------------------
	// The same area-create gesture one level down: the panel collects the object's details and
	// Create posts them with the drawn area to POST /quotations/{id}/furniture/.
	const handleFurnitureCreated = useCallback(
		(created: TFurniture) => {
			// Hand the panel over to the normal furniture sidebar with the new object selected, exactly
			// as if it had been clicked on the canvas. No scroll — the user drew it, it is on screen.
			handleFurnitureClick(created, { scroll: false })
		},
		[handleFurnitureClick],
	)

	const {
		createSession: createFurnitureSession,
		isCreatePending: isCreateFurniturePending,
		startCreateFurniture,
		cancelCreateFurniture,
		runCreateFurniture,
	} = useFurnitureCreate({
		createSessionRef: createFurnitureSessionRef,
		erefDraftRectRef,
		appContainerRef: jumpingNavTargetContainerRef,
		getViewerPanes,
		layoutEpoch,
		numericQuotationIdRef,
		createFurnitureAsync,
		queryClient,
		onFurnitureCreated: handleFurnitureCreated,
		onToolChange,
		clearSelectionToolbar,
		success,
		formatError,
	})

	// ---- Add elevation callout (REQ-294) ----------------------------------------------------
	const handleEcallCreated = useCallback(
		(created: TEcall) => {
			handleEcallClick(created, { scroll: false })
		},
		[handleEcallClick],
	)

	const {
		createSession: createEcallSession,
		isCreatePending: isCreateEcallPending,
		startCreateEcall,
		cancelCreateEcall,
		runCreateEcall,
	} = useEcallCreate({
		createSessionRef: createEcallSessionRef,
		draftRectRef: erefDraftRectRef,
		appContainerRef: jumpingNavTargetContainerRef,
		getViewerPanes,
		layoutEpoch,
		numericQuotationIdRef,
		createEcallAsync,
		queryClient,
		onEcallCreated: handleEcallCreated,
		onToolChange,
		clearSelectionToolbar,
		success,
		formatError,
	})

	const handleAddCountertop = useCallback(() => {
		disarmHighlightTools()
		clearHighlightSelectionRef.current?.()
		cancelConnectObjects()
		closeCreateEref()
		closeCreateEcallRef.current?.()
		closeCreateFurnitureRef.current?.()
		closeCreateOtherRef.current?.()
		closeCreateCustomRef.current?.()
		disarmNoteToolRef.current?.()
		startCreateCountertop()
		rightPanelModeRef.current = "create-countertop"
		setRightPanelMode("create-countertop")
		openReferencePanel()
		setCreateAreaPanelDefaultWidth()
	}, [
		disarmHighlightTools,
		cancelConnectObjects,
		closeCreateEref,
		openReferencePanel,
		setCreateAreaPanelDefaultWidth,
		startCreateCountertop,
	])

	const closeCreateCountertop = useCallback((): boolean => {
		clearCreateAreaPanelMinWidth()
		if (!cancelCreateCountertop()) return false
		if (rightPanelModeRef.current === "create-countertop") {
			setRightPanelMode(null)
			closeReferencePanel()
		}
		return true
	}, [cancelCreateCountertop, clearCreateAreaPanelMinWidth, closeReferencePanel])
	closeCountertopCreateRef.current = closeCreateCountertop

	const handleAddElevationCallout = useCallback(() => {
		disarmHighlightTools()
		clearHighlightSelectionRef.current?.()
		cancelConnectObjects()
		cancelCreateEref()
		closeCountertopCreateRef.current?.()
		closeCreateFurnitureRef.current?.()
		closeCreateOtherRef.current?.()
		closeCreateCustomRef.current?.()
		disarmNoteToolRef.current?.()
		startCreateEcall()
		setRightPanelMode("create-ecall")
		openReferencePanel()
		setCreateAreaPanelDefaultWidth()
	}, [
		disarmHighlightTools,
		cancelConnectObjects,
		cancelCreateEref,
		openReferencePanel,
		setCreateAreaPanelDefaultWidth,
		startCreateEcall,
	])

	const closeCreateEcall = useCallback((): boolean => {
		clearCreateAreaPanelMinWidth()
		if (!cancelCreateEcall()) return false
		if (rightPanelModeRef.current === "create-ecall") {
			setRightPanelMode(null)
			closeReferencePanel()
		}
		return true
	}, [cancelCreateEcall, clearCreateAreaPanelMinWidth, closeReferencePanel])
	closeCreateEcallRef.current = closeCreateEcall

	const handleAddCabinet = useCallback(() => {
		disarmHighlightTools()
		clearHighlightSelectionRef.current?.()
		cancelConnectObjects()
		cancelCreateEref()
		cancelCreateEcall()
		closeCountertopCreateRef.current?.()
		closeCreateOtherRef.current?.()
		closeCreateCustomRef.current?.()
		disarmNoteToolRef.current?.()
		startCreateFurniture()
		rightPanelModeRef.current = "create-furniture"
		setRightPanelMode("create-furniture")
		openReferencePanel()
		setCreateAreaPanelDefaultWidth()
	}, [
		disarmHighlightTools,
		cancelConnectObjects,
		cancelCreateEref,
		cancelCreateEcall,
		startCreateFurniture,
		openReferencePanel,
		setCreateAreaPanelDefaultWidth,
	])

	const closeCreateFurniture = useCallback((): boolean => {
		clearCreateAreaPanelMinWidth()
		if (!cancelCreateFurniture()) return false
		if (rightPanelModeRef.current === "create-furniture") {
			setRightPanelMode(null)
			closeReferencePanel()
		}
		return true
	}, [cancelCreateFurniture, clearCreateAreaPanelMinWidth, closeReferencePanel])
	closeCreateFurnitureRef.current = closeCreateFurniture

	// ---- Add other object (REQ-299) ---------------------------------------------------------
	// The same area-create gesture as its siblings; what differs is the payload — a null category,
	// which is what makes the new object an "Other" one — and the name/price the panel collects.
	const handleOtherCreated = useCallback(
		(created: TFurniture) => {
			// Hand the panel over to the normal furniture sidebar with the new object selected, exactly
			// as if it had been clicked on the canvas. No scroll — the user drew it, it is on screen.
			handleFurnitureClick(created, { scroll: false })
		},
		[handleFurnitureClick],
	)

	const {
		createSession: createOtherSession,
		isCreatePending: isCreateOtherPending,
		startCreateOther,
		cancelCreateOther,
		runCreateOther,
	} = useOtherCreate({
		createSessionRef: createOtherSessionRef,
		draftRectRef: erefDraftRectRef,
		appContainerRef: jumpingNavTargetContainerRef,
		getViewerPanes,
		layoutEpoch,
		numericQuotationIdRef,
		queryClient,
		onFurnitureCreated: handleOtherCreated,
		onToolChange,
		clearSelectionToolbar,
		success,
		formatError,
	})

	const handleAddOther = useCallback(() => {
		disarmHighlightTools()
		clearHighlightSelectionRef.current?.()
		cancelConnectObjects()
		cancelCreateEref()
		cancelCreateEcall()
		closeCountertopCreateRef.current?.()
		closeCreateFurnitureRef.current?.()
		closeCreateCustomRef.current?.()
		disarmNoteToolRef.current?.()
		startCreateOther()
		rightPanelModeRef.current = "create-other"
		setRightPanelMode("create-other")
		openReferencePanel()
		setCreateAreaPanelDefaultWidth()
	}, [
		disarmHighlightTools,
		cancelConnectObjects,
		cancelCreateEref,
		cancelCreateEcall,
		startCreateOther,
		openReferencePanel,
		setCreateAreaPanelDefaultWidth,
	])

	const closeCreateOther = useCallback((): boolean => {
		clearCreateAreaPanelMinWidth()
		if (!cancelCreateOther()) return false
		if (rightPanelModeRef.current === "create-other") {
			setRightPanelMode(null)
			closeReferencePanel()
		}
		return true
	}, [cancelCreateOther, clearCreateAreaPanelMinWidth, closeReferencePanel])
	closeCreateOtherRef.current = closeCreateOther

	// ---- Add custom object (#1371) -----------------------------------------------------------
	// The same area-create gesture as its siblings; what differs is the payload — a null category
	// (no `"custom"` create shortcut yet, backend #2111) — and that the object's name/price come
	// from a picked custom model number rather than typed fields, linked with a single follow-up
	// PATCH that also converts the object (see useCustomObjectCreate).
	const handleCustomCreated = useCallback(
		(created: TFurniture) => {
			// Hand the panel over to the normal furniture sidebar with the new object selected, exactly
			// as if it had been clicked on the canvas. No scroll — the user drew it, it is on screen.
			handleFurnitureClick(created, { scroll: false })
		},
		[handleFurnitureClick],
	)

	const {
		createSession: createCustomSession,
		isCreatePending: isCreateCustomPending,
		startCreateCustom,
		cancelCreateCustom,
		runCreateCustom,
	} = useCustomObjectCreate({
		createSessionRef: createCustomSessionRef,
		draftRectRef: erefDraftRectRef,
		appContainerRef: jumpingNavTargetContainerRef,
		getViewerPanes,
		layoutEpoch,
		numericQuotationIdRef,
		queryClient,
		onFurnitureCreated: handleCustomCreated,
		onToolChange,
		clearSelectionToolbar,
		success,
		formatError,
	})

	const handleAddCustom = useCallback(() => {
		cancelConnectObjects()
		cancelCreateEref()
		cancelCreateEcall()
		closeCountertopCreateRef.current?.()
		closeCreateFurnitureRef.current?.()
		closeCreateOtherRef.current?.()
		disarmNoteToolRef.current?.()
		startCreateCustom()
		rightPanelModeRef.current = "create-custom"
		setRightPanelMode("create-custom")
		openReferencePanel()
		setCreateAreaPanelDefaultWidth()
	}, [
		cancelConnectObjects,
		cancelCreateEref,
		cancelCreateEcall,
		startCreateCustom,
		openReferencePanel,
		setCreateAreaPanelDefaultWidth,
	])

	const closeCreateCustom = useCallback((): boolean => {
		clearCreateAreaPanelMinWidth()
		if (!cancelCreateCustom()) return false
		if (rightPanelModeRef.current === "create-custom") {
			setRightPanelMode(null)
			closeReferencePanel()
		}
		return true
	}, [cancelCreateCustom, clearCreateAreaPanelMinWidth, closeReferencePanel])
	closeCreateCustomRef.current = closeCreateCustom

	// Jump-session navigation (view-connected → step prev/next → back-to-source) with its floating
	// bar. Drives the shared selection state through the ctx setters/refs below.
	//
	// Declared AFTER the connect-objects mode on purpose: the two bars share the top of the document
	// area and the jump bar now survives a connect session (stacking below it), so it needs that
	// session to grey out its navigation. Nothing above this line depends on the jump session, and
	// the connect mode no longer closes it.
	const {
		jumpSession,
		jumpSessionRef,
		closeJumpSession,
		handleConnectedCalloutView,
		handleErefCalloutView,
		handleEcallErefView,
		startEcallJumpSession,
		startErefJumpSession,
	} = useJumpNavigation({
		isModalOpen,
		isDocumentLoaded,
		isNavigationDisabled: !!connectSession,
		relatedObjects,
		allEcallsRef,
		getShadowRoot,
		openReferencePanel,
		findErefWithFurnitureById,
		setSelectedEref,
		selectedErefRef,
		setSelectedFurniture,
		selectedFurnitureRef,
		setSelectedEcall,
		selectedEcallRef,
		setRightPanelMode,
		setFurnitureInitialTab,
		setFurnitureMountEpoch,
		jumpingNavEntryRef,
		jumpingNavDocAreaRef,
		jumpingNavTargetContainerRef,
		splitJumpingNavEntryRef,
		getPaneDocAreaEl,
		openErefInPane,
		openEcallInPane,
		openFurnitureInPane,
		closePaneSidebar,
		selectInPaneSilently,
		suppressSmartNavRef,
	})

	// Smart navigation (#1311): the Primary/Secondary machine that drives the OTHER pane from the
	// pane the user just clicked in. Declared right after the jump session because a smart-nav
	// session IS a jump session — one whose items land in the secondary pane — and because the whole
	// machine is gated on "Auto open floor plan" being checked while split screen is open.
	useSmartNavigation({
		isModalOpen,
		instanceRef,
		selectInPaneSilently,
		allEcallsRef,
		findErefWithFurnitureById,
		jumpSession,
		jumpSessionRef,
		closeJumpSession,
		startEcallJumpSession,
		startErefJumpSession,
		notifyRef: smartNavNotifyRef,
	})

	// Delete / Backspace: raise the shared delete confirmation for whatever the canvas selection
	// resolves to — the selected furniture (single or batch, REQ-369) or exactly one eref
	// (REQ-376) — mirroring the context-menu and toolbar Delete actions. Returns whether it acted
	// so the hotkey only swallows the key then. Declared in hotkeys/hotkeyRegistry and bound by
	// useEditorHotkeys, which also keeps it inert while a text field has focus.
	const handleDeleteSelection = useCallback((): boolean => {
		// While connect-objects is picking, the canvas selection belongs to that mode.
		if (isCanvasModeActive()) return false
		const selected = getSelectedFurniture()
		if (selected.length > 0) {
			setDeleteConfirm({ kind: "furniture", furniture: selected })
			return true
		}
		const entities = AnnotationService.getSelectedEntities()
		if (entities.length !== 1 || entities[0].entityType !== "eref") return false
		const eref = findErefWithFurnitureById(entities[0].entityId)
		if (!eref) return false
		setDeleteConfirm({ kind: "eref", eref })
		return true
	}, [getSelectedFurniture, findErefWithFurnitureById, isCanvasModeActive])

	// REQ-296: the furniture sidebar's ⋯ → Delete. A fourth entry point into the SAME confirmation
	// + runBatchDelete flow the toolbar, context menu and Delete key already use — it acts on the
	// object the sidebar is editing, not on the canvas selection.
	const handleFurnitureDelete = useCallback((furniture: TFurniture) => {
		setDeleteConfirm({ kind: "furniture", furniture: [furniture] })
	}, [])

	// REQ-295: the eref sidebar's ⋯ → Delete. A fourth entry point into the SAME confirmation +
	// runErefDelete flow the toolbar, context menu and Delete key already use (REQ-376) — it acts on
	// the eref the sidebar is editing, not on the canvas selection.
	const handleErefDelete = useCallback((eref: TErefWithFurniture) => {
		setDeleteConfirm({ kind: "eref", eref })
	}, [])

	// REQ-297: the ecall sidebar's ⋯ → Delete. The only entry point into the ecall delete flow —
	// unlike an eref, a callout has no toolbar pill, context-menu entry or Delete-key binding — and
	// it acts on the callout the sidebar is editing, not on the canvas selection.
	const handleEcallDelete = useCallback((ecall: TEcall) => {
		setDeleteConfirm({ kind: "ecall", ecall })
	}, [])

	// Keyboard hotkeys for the editor (Alt+1 focus, Tab trap, Enter activation, the Alt+Arrow
	// right-sidebar navigation above, and Delete/Backspace). All hotkey logic + the tinykeys
	// dispatcher live in useEditorHotkeys, next to the hotkey registry. Placed after the navigation
	// and selection callbacks so it can receive them.
	useEditorHotkeys({
		mode,
		isInitialized,
		isModalOpen,
		isPanelOpen,
		isReferencePanelOpen,
		openPanel,
		closePanel,
		// Escape has to clear the selection behind the panel too, so it goes through the mode-aware
		// dismiss rather than the bare open-flag setter.
		closeReferencePanel: handleRightPanelDismiss,
		leftPanelEntryRef: sidebarLeftEntryRef,
		// The hotkeys move focus into the sidebar that shows the CURRENT selection, which in split
		// screen is the middle dock rather than Apryse's right panel (review #3 on #1264).
		rightPanelEntryRef: isSplitPaneOpen ? sidebarMiddleEntryRef : sidebarRightEntryRef,
		// Tab is trapped inside the shortcuts panel while it is open (REQ-359).
		shortcutsPanelEntryRef,
		rightPanelMode,
		selectedEref,
		selectedFurniture,
		selectedEcall,
		onNavigateNextEref: navigateNextEref,
		onNavigatePrevEref: navigatePrevEref,
		onNavigateFurnitureToEref: navigateFurnitureToParentEref,
		onDeleteSelection: handleDeleteSelection,
		onSelectTool: () => handleToolChange("select"),
		onPanTool: () => handleToolChange("pan"),
		onZoomTool: handleZoomTool,
		onToggleRulerTool: handleToggleRulerTool,
		onToggleTextTool: () => toggleNoteToolRef.current(),
		onToggleFreehandTool: toggleFreehandTool,
		// Escape's first stop: dismiss the keyboard-shortcuts panel if it is up (REQ-359).
		onCloseShortcutsPanel: closeShortcutsPanelOnEscape,
		// The header's + Add / Shapes / help dropdowns and the settings modal, from the keyboard.
		// #1381 review: Rectangle/Ellipse no longer arm directly off bare R/O — they're entries of the
		// Shapes menu S opens, picked via the shared 1-6 digit-pick same as every other menu here.
		onOpenAddMenu: openAddMenu,
		onOpenHelpMenu: openHelpMenu,
		onOpenShapesMenu: openShapesMenu,
		onToggleSplitView: toggleSplitViewFromHotkey,
		onOpenSettings: openSettingsFromHotkey,
		onPickMenuItem: pickHeaderMenuItem,
		onCloseHeaderMenu: closeHeaderMenuOnEscape,
		// Escape belongs to whichever canvas mode is open: it tears that mode down and leaves the
		// sidebars alone (returning true stops the "close the right sidebar" fall-through). Every mode
		// must be in this chain — one left out is a mode the user cannot escape from (#1309 review).
		onCancelCanvasMode: () =>
			closeCreateCountertop() ||
			closeCreateEcall() ||
			closeCreateEref() ||
			closeCreateFurniture() ||
			closeCreateOther() ||
			cancelConnectObjects() ||
			// REQ-395/396: disarms the note tool while it's armed but nothing has been drawn yet.
			// Reached through a ref (see its own declaration) since `useNoteCreate` is declared further
			// down. Text-edit-in-progress / a currently-selected note are left to Apryse's own Escape
			// handling for its rich-text editor and native selection — live verification this session
			// found Escape does NOT commit an in-progress text edit, so this deliberately doesn't try to
			// layer app-level "cancel" semantics on top of that native behavior.
			(disarmNoteToolRef.current?.() ?? false) ||
			// REQ-400: Escape leaves whichever highlight tool is armed (Shapes or Pen), otherwise
			// deselects a selected highlight (from a click, or from the auto-select right after
			// drawing) — a stray floating toolbar must not be left up when the user backs out.
			disarmHighlightTools() ||
			clearHighlightSelection(),
	})

	// Post-delete cleanup for an eref (REQ-376): drop the now-stale right sidebar if it was showing
	// the deleted eref, and cancel a jump session built around it (as its source or one of its
	// steps). The cache splice handles everything data-driven (left sidebar rows, detached objects).
	const handleErefDeleted = useCallback(
		(erefId: number) => {
			// The split screen's sidebar is a second surface with its own selection, and the
			// reconciliation effect below deliberately KEEPS its entity when the id vanishes from the
			// cache — so without this it would go on editing a deleted eref, exactly as the furniture
			// and ecall paths already guard against.
			setSplitSidebar((current) => (current?.mode === "eref" && current.eref.id === erefId ? null : current))
			if (selectedErefRef.current?.id === erefId) {
				selectedErefRef.current = undefined
				setSelectedEref(undefined)
				if (rightPanelModeRef.current === "eref") {
					setRightPanelMode(null)
					closeReferencePanel()
				}
			}
			const session = jumpSessionRef.current
			if (!session) return
			const wasSource = session.source.kind === "eref" && session.source.eref.id === erefId
			const wasItem = session.itemKind === "eref" && session.items.some((item) => item.id === erefId)
			if (wasSource || wasItem) closeJumpSession()
		},
		[closeReferencePanel, closeJumpSession, jumpSessionRef],
	)

	// Eref state change (REQ-373) — forward transitions from the toolbar, backward ones from the
	// context menu. No cascade: only the eref's own status changes.
	const { runErefStatus } = useErefStatusChange({
		numericQuotationIdRef,
		setBatchPending,
		updateErefStatusAsync,
		queryClient,
		success,
		formatError,
	})

	// Eref delete (REQ-376) — shared by the toolbar, the context menu and the Delete/Backspace key.
	const { runErefDelete } = useErefDelete({
		numericQuotationIdRef,
		setSelectionToolbar,
		setDeleteConfirm,
		setBatchPending,
		deleteErefAsync,
		onErefDeleted: handleErefDeleted,
		queryClient,
		success,
		formatError,
	})

	// Post-delete cleanup for an ecall (REQ-297): drop the right sidebar if it was editing the
	// deleted callout, and the split screen's own sidebar too — that second surface has a separate
	// selection and would otherwise keep editing a callout the backend has already removed. The
	// cache splice handles everything data-driven.
	const handleEcallDeleted = useCallback(
		(ecallId: number) => {
			setSplitSidebar((current) => (current?.mode === "ecall" && current.ecall.id === ecallId ? null : current))
			if (selectedEcallRef.current?.id !== ecallId) return
			selectedEcallRef.current = undefined
			setSelectedEcall(undefined)
			if (rightPanelModeRef.current === "ecall") {
				setRightPanelMode(null)
				closeReferencePanel()
			}
		},
		[closeReferencePanel],
	)

	// Ecall delete (REQ-297) — raised from the ecall sidebar's ⋯ menu, confirmed in the shared modal.
	const { runEcallDelete } = useEcallDelete({
		numericQuotationIdRef,
		setDeleteConfirm,
		setBatchPending,
		deleteEcallAsync,
		onEcallDeleted: handleEcallDeleted,
		queryClient,
		success,
		formatError,
	})

	const buildReviewAction = useCallback(
		(furniture: TFurniture[]): TToolbarAction | null => {
			const toggle = getReviewToggle(furniture)
			if (!toggle) return null
			return {
				key: toggle.key,
				label: toggle.label,
				icon: <CheckIcon />,
				disabled: batchPending,
				onSelect: () => void runBatchStatus(furniture, toggle.targetStatus),
			}
		},
		[batchPending, getReviewToggle, runBatchStatus],
	)

	// Pill actions for the current selection (Figma states). Unconnected → a labeled "Connect to
	// elevation" button. Connected → the review toggle (labeled) plus a small "Disconnect" icon
	// (no text). Move/reconnect for a connected selection lives in the right-click menu. Connect
	// requires a single-page selection (erefs are page-bound). Delete is always last.
	const buildToolbarActions = useCallback(
		(furniture: TFurniture[]): TToolbarAction[] => {
			const anyUnconnected = furniture.some((f) => f.eref_id == null)
			const anyConnected = furniture.some((f) => f.eref_id != null)
			const samePage = new Set(furniture.map((f) => f.page_number)).size === 1

			const connectAction: TToolbarAction = {
				key: "connect",
				label: "Connect to elevation",
				icon: <ConnectElevationIcon />,
				disabled: batchPending || !samePage,
				title: samePage ? undefined : "Select objects on a single page to connect",
				onSelect: () => openConnectModal(furniture),
			}
			// Review toggle only moves objects between "needs review" (work_in_progress) and
			// "reviewed", and only sends the subset that actually changes — so `in_quotation` objects
			// are never disturbed (marking them reviewed would pull them out of quotation). Null when
			// there is nothing to review/unreview (e.g. every object is already in_quotation).
			const reviewAction = buildReviewAction(furniture)

			const labeled: TToolbarAction[] = []
			const icons: TToolbarAction[] = []

			if (anyUnconnected && anyConnected) {
				// Mixed selection: Connect (unifies everything onto one eref) + review toggle; the
				// disconnect icon is hidden since it would only affect the connected subset.
				labeled.push(connectAction)
				if (reviewAction) labeled.push(reviewAction)
			} else if (anyUnconnected) {
				// All unconnected — Connect only (Figma "not connected" state).
				labeled.push(connectAction)
			} else {
				// All connected. `single` enables the single-object quotation control that mirrors the
				// right sidebar (the batch status endpoint can't set `in_quotation`, so it's single-only).
				const single = furniture.length === 1 ? furniture[0] : null
				if (single?.status === "in_quotation") {
					// In quotation → only "Remove from quotation" (no review toggle; getReviewToggle is null).
					labeled.push({
						key: "removeQuotation",
						label: "Remove from quotation",
						icon: <MinusIcon />,
						disabled: batchPending,
						onSelect: () => void runSingleFurnitureStatus(single, "reviewed"),
					})
				} else {
					if (reviewAction) labeled.push(reviewAction)
					if (single) {
						const ready = isFurnitureQuotationReady(single)
						labeled.push({
							key: "addQuotation",
							label: "Add to quotation",
							icon: <PlusIcon />,
							disabled: batchPending || !ready,
							title: ready ? undefined : "Fill in all required fields to add to quotation",
							onSelect: () => void runSingleFurnitureStatus(single, "in_quotation"),
						})
					}
				}
				icons.push({
					key: "disconnect",
					label: "Disconnect from elevation",
					icon: <DisconnectElevationIcon />,
					iconOnly: true,
					disabled: batchPending,
					onSelect: () => void runDetach(furniture),
				})
			}

			icons.push({
				key: "delete",
				label: "Delete",
				icon: <TrashIcon />,
				iconOnly: true,
				disabled: batchPending,
				onSelect: () => setDeleteConfirm({ kind: "furniture", furniture }),
			})

			return [...labeled, ...icons.map((a, i) => (i === 0 && labeled.length > 0 ? { ...a, separatorBefore: true } : a))]
		},
		[batchPending, openConnectModal, buildReviewAction, runDetach, runSingleFurnitureStatus],
	)

	// Pill actions for a single selected eref. Same shape as the furniture pill: the labeled forward
	// state transitions (REQ-373) first, then the icon-only Delete (REQ-376) behind a separator.
	// work_in_progress → "Mark as reviewed" + "Add to quotation"; reviewed → "Add to quotation";
	// in_quotation → no forward transition (its backward one lives in the context menu). An eref with
	// no connected objects gets no state action at all — just Delete, which is available in every
	// state.
	const buildErefToolbarActions = useCallback(
		(eref: TErefWithFurniture): TToolbarAction[] => {
			const status = eref.status ?? "work_in_progress"
			const labeled: TToolbarAction[] = []

			if (eref.furniture.length > 0) {
				if (status === "work_in_progress") {
					labeled.push({
						key: "review",
						label: "Mark as reviewed",
						icon: <CheckIcon />,
						disabled: batchPending,
						onSelect: () => void runErefStatus(eref, "reviewed"),
					})
				}
				if (status !== "in_quotation") {
					// Not gated client-side: the endpoint's rules (an object already in quotation, a
					// delivery date, case / countertop colors) are enforced server-side and rejected with
					// a message that names the missing piece — which formatError surfaces. Mirrors the
					// eref sidebar's own "Add to quotation" button, which is likewise always enabled.
					labeled.push({
						key: "addQuotation",
						label: "Add to quotation",
						icon: <PlusIcon />,
						disabled: batchPending,
						onSelect: () => void runErefStatus(eref, "in_quotation"),
					})
				}
			}

			return [
				...labeled,
				{
					key: "delete",
					label: "Delete",
					icon: <TrashIcon />,
					iconOnly: true,
					separatorBefore: labeled.length > 0,
					disabled: batchPending,
					onSelect: () => setDeleteConfirm({ kind: "eref", eref }),
				},
			]
		},
		[batchPending, runErefStatus],
	)

	// Single-action pill for a lone selected ecall (REQ-374): icon-only Delete. Goes through the
	// shared confirm modal, exactly like the eref/furniture pills' own Delete — not a direct call to
	// runEcallDelete.
	const buildEcallToolbarActions = useCallback(
		(ecall: TEcall): TToolbarAction[] => [
			{
				key: "delete",
				label: "Delete",
				icon: <TrashIcon />,
				iconOnly: true,
				disabled: batchPending,
				onSelect: () => setDeleteConfirm({ kind: "ecall", ecall }),
			},
		],
		[batchPending, setDeleteConfirm],
	)

	// The ecall menu's "Navigate to elevation" submenu reuses the exact handler the ecall sidebar's
	// own elevation rows call, chosen by which pane the right-click landed in — same split as
	// openEcallInPane / openErefInPane above.
	const handleContextMenuNavigateToEref = useCallback(
		(erefId: number, pane: TViewerPane) => {
			if (pane === "split") handleSplitEcallErefView(erefId)
			else handleEcallErefView(erefId)
		},
		[handleSplitEcallErefView, handleEcallErefView],
	)

	// Right-click context menu (single furniture, batch selection, and paste-on-empty). Owns its
	// position state, render, and dismiss; the contextmenu handler below writes the target refs
	// and calls openContextMenuAt.
	const { closeContextMenu, openContextMenuAt } = useContextMenu({
		getShadowRoot,
		instanceRef,
		jumpingNavTargetContainerRef,
		contextMenuEntryRef,
		contextMenuFurnitureRef,
		contextMenuBatchRef,
		contextMenuErefRef,
		contextMenuEcallRef,
		contextMenuPointRef,
		copyFurnitureToClipboard,
		duplicateFurnitureInPlace,
		pasteFurnitureAtPoint,
		openConnectModal,
		getReviewToggle,
		runBatchStatus,
		runDetach,
		runErefStatus,
		startConnectObjects,
		findErefWithFurnitureById,
		onNavigateToEref: handleContextMenuNavigateToEref,
		setDeleteConfirm,
		batchPending,
	})

	useEffect(() => {
		if (mode !== "editor" || !isInitialized || !instanceRef.current) return
		const { annotationManager } = instanceRef.current.Core

		const handler = (annotations: unknown, action: string) => {
			// The connect-objects mode (REQ-372) drives the selection itself: its picks must not open
			// a sidebar, cancel a jump session, or raise the batch pill.
			if (isCanvasModeActive()) return
			// Keep the floating toolbar in sync on any selection change (select + deselect).
			syncSelectionToolbar()
			// Same for the batch edit panel (REQ-403): two or more selected objects open it, one hands
			// over to the single-object sidebar. Runs before the `selected`-only guard below because a
			// deselect can shrink a batch just as a select can grow it.
			syncBatchFurnitureSelection()

			if (action !== "selected") return
			// Selecting is only possible with the select tool — but "the select tool" means any pane's
			// (see isEditToolActive): with the ruler measuring in pane 1, the split pane is still in
			// EDIT and its clicks must land.
			if (!isEditToolActive()) {
				annotationManager.deselectAllAnnotations()
				syncSelectionToolbar()
				return
			}
			const list = annotations as { Id: string }[]
			if (!list || list.length === 0) return

			const metadata = AnnotationService.getMetadata(list[0].Id)
			if (!metadata) return

			// A click in the split screen's RIGHT pane opens that pane's own sidebar; everything
			// else — the left sidebar, jump navigation, hotkeys, the primary pane's canvas — drives
			// the left screen's. Selection itself stays shared (a hit in either pane selects the
			// entity in both), so only the sidebar the result lands in differs. Review #4 on #1264.
			const pane = selectSourcePaneRef.current

			// Page boxes are selectable only to expose their drag/resize handles — they have no
			// sidebar and are never jump-session items. Bail before the session-close check below so
			// grabbing a page box to correct its bbox doesn't cancel an active eref/ecall jump session
			// (which an eref/ecall selection outside the session still does, by design).
			if (metadata.entityType === "page") return

			const session = jumpSessionRef.current
			if (session) {
				const sessionItemType = session.itemKind === "ecall" ? "ecall" : "eref"
				const isInSession =
					metadata.entityType === sessionItemType && session.items.some((it) => it.id === metadata.entityId)
				if (!isInSession) {
					closeJumpSession()
				}
			}

			if (metadata.entityType === "furniture") {
				// A batch selection is driven by the floating toolbar — don't hijack it by opening the
				// single-furniture sidebar. Suppress on an additive click (even the first, 0→1) and on
				// any existing multi-selection.
				if (additiveSelectRef.current || AnnotationService.getSelectedFurnitureIds().length > 1) return
				const furniture = findFurnitureById(metadata.entityId)
				if (furniture) {
					handleFurnitureClick(furniture, { scroll: false, pane })
				}
				return
			}

			if (metadata.entityType === "eref") {
				const eref = findErefWithFurnitureById(metadata.entityId)
				if (eref) {
					handleErefClick(eref, { scroll: false, pane })
				}
				return
			}

			if (metadata.entityType === "ecall") {
				const ecall = findEcallById(metadata.entityId)
				if (ecall) {
					handleEcallClick(ecall, { scroll: false, pane })
				}
			}
		}

		annotationManager.addEventListener("annotationSelected", handler)
		return () => {
			annotationManager.removeEventListener("annotationSelected", handler)
		}
	}, [
		mode,
		isInitialized,
		findErefWithFurnitureById,
		findFurnitureById,
		findEcallById,
		handleErefClick,
		handleFurnitureClick,
		handleEcallClick,
		syncSelectionToolbar,
		syncBatchFurnitureSelection,
		closeJumpSession,
		jumpSessionRef,
		isCanvasModeActive,
		isEditToolActive,
	])

	// Click-to-select bypass (#1119). Once a second Apryse WebViewer instance has existed in the
	// page (e.g. the Documents-tab preview viewer on this drawer), Apryse's edit tool silently
	// stops firing `annotationSelected` on click — so clicking a highlight no longer opens the
	// reference sidebar (it only worked after a left-sidebar select forced a re-render). Disposing
	// that other instance does NOT cure it (Apryse teardown is async and leaves residue). But
	// hit-testing (getAnnotationByMouseEvent) and programmatic selection both still work, so we
	// stand in for the edit tool: hit-test the click and select the entity ourselves. The
	// programmatic selectAnnotation fires `annotationSelected`, which the handler above turns into
	// a sidebar open. We only act while the edit tool is active (leave the ruler etc. alone).
	//
	// Propagation handling depends on the click kind (REQ-350 coordinate editing changed this):
	//   • ADDITIVE (Shift/Cmd/Ctrl) hit — the batch toggle is entirely ours, so we stopPropagation
	//     so Apryse's tool can't also process it and fight the toggle.
	//   • PLAIN hit — we must NOT stopPropagation: the edit tool needs this mousedown to begin a
	//     drag-to-move / resize on the highlight. The trade-off: in the multi-instance (#1119) state
	//     the inert tool treats the same click as empty space and deselects what we just selected
	//     (the old flash-and-vanish). That regression is acceptable only if the built-in drag works
	//     in that state; it is the open item to confirm in-browser, and if the drag proves dead
	//     there we escalate to a self-owned drag (and can restore the plain-hit stopPropagation).
	// Capture phase so we run before Apryse's own handlers; no preventDefault so empty-space clicks
	// still pan/focus.
	//
	// Split screen (REQ-399): bound to EVERY pane, and re-bound on `layoutEpoch` — entering split
	// re-mounts the document area, so the element this was attached to is detached by then (which is
	// why the canvas handlers went silent in BOTH panes after entering split). Each pane's handler
	// tags itself as the active pane so the floating overlays measure against the right viewport.
	useEffect(() => {
		if (mode !== "editor" || !isInitialized || !instanceRef.current) return
		const { Tools } = instanceRef.current.Core
		const panes = getViewerPanes()
		if (panes.length === 0) return

		const bindings = panes.map(({ pane, documentViewer, scrollEl }) => {
			const onMouseDown = (e: MouseEvent) => {
				// Connect-objects (REQ-372) owns press/drag/release on the canvas while it is open.
				if (isCanvasModeActive()) return
				if (documentViewer.getToolMode() !== documentViewer.getTool(Tools.ToolNames.EDIT)) return
				// Secondary-click (right button, or macOS Ctrl+click) is handled by onContextMenu, which
				// preserves an existing multi-selection — don't let mousedown collapse the batch here.
				if (e.button !== 0 || (IS_MAC && e.ctrlKey)) return
				AnnotationService.setActivePane(pane)
				// Shift / Cmd (mac) / Ctrl (win-linux) + click toggles a furniture into the batch
				// selection (REQ-369); a plain click replaces the selection with the single hit.
				const additive = e.shiftKey || e.metaKey || (!IS_MAC && e.ctrlKey)
				additiveSelectRef.current = additive
				// Tell the annotationSelected handler which screen this select came from, so a hit in
				// the right pane opens ITS sidebar instead of the left screen's (review #4 on #1264).
				selectSourcePaneRef.current = pane
				const hit =
					pane === "split"
						? AnnotationService.selectSplitAnnotationFromMouseEvent(e, { additive })
						: AnnotationService.selectAnnotationFromMouseEvent(e, { additive })
				// In the split pane, Apryse's own tool only clears ITS manager's selection on an
				// empty-space click; the primary manager (the one the sidebar and pill read) would
				// keep the stale selection. Clear it explicitly so both panes agree.
				if (pane === "split" && !hit && !additive) AnnotationService.deselectAll()
				// annotationSelected is dispatched synchronously inside the select above, so its
				// sidebar-suppression guard and its pane routing have already read these flags; clear
				// them so later programmatic (non-additive, left-screen) selections behave normally.
				additiveSelectRef.current = false
				selectSourcePaneRef.current = "primary"
				// Only swallow the event for an ADDITIVE (batch) hit — see the block comment above.
				// A PLAIN hit must reach Apryse's edit tool so a drag-to-move / resize can start (REQ-350).
				if (hit && additive) {
					e.stopPropagation()
					e.stopImmediatePropagation()
				}
				// REQ-400 "select highlights" mode (decision 3): a PLAIN click that missed every
				// eref/ecall/furniture/page falls through to a highlight underneath it — highlights are
				// the bottom-most layer, so an entity always wins over one when both are hit. Primary
				// pane only: highlights aren't mirrored into the split pane (#1378 scope). Always
				// stopPropagation on an actual highlight hit (unlike the plain-entity case above) — a
				// highlight has no move/resize affordance, so the mousedown must not reach Apryse's edit
				// tool and start a drag on the permissions this just lifted.
				if (!hit && !additive && pane === "primary") {
					const highlightHit = trySelectHighlightFromMouseEvent(e, pane)
					if (highlightHit) {
						e.stopPropagation()
						e.stopImmediatePropagation()
					}
				}
			}

			// REQ-400: pointer cursor while hovering a highlight — same reachability as the mousedown
			// click-to-select above (plain Select tool, no other canvas mode, primary pane only,
			// highlights aren't mirrored into the split pane per #1378 scope), so the cursor never
			// promises a click that wouldn't actually select anything. A pure hit-test
			// (getHighlightAtMouseEvent, no side effects) — only writes the DOM class on an actual
			// change, not on every mousemove, since this fires far more often than the click handler.
			let isHoveringHighlight = false
			const onHoverMove = (e: MouseEvent) => {
				if (
					pane !== "primary" ||
					isCanvasModeActive() ||
					documentViewer.getToolMode() !== documentViewer.getTool(Tools.ToolNames.EDIT)
				) {
					if (isHoveringHighlight) {
						isHoveringHighlight = false
						scrollEl.classList.remove("wv-highlight-hover-cursor")
					}
					return
				}
				const hovering = Boolean(AnnotationService.getHighlightAtMouseEvent(e, pane))
				if (hovering === isHoveringHighlight) return
				isHoveringHighlight = hovering
				scrollEl.classList.toggle("wv-highlight-hover-cursor", hovering)
			}

			scrollEl.addEventListener("mousedown", onMouseDown, true)
			scrollEl.addEventListener("mousemove", onHoverMove, { passive: true })
			return () => {
				scrollEl.removeEventListener("mousedown", onMouseDown, true)
				scrollEl.removeEventListener("mousemove", onHoverMove)
				scrollEl.classList.remove("wv-highlight-hover-cursor")
			}
		})

		return () => bindings.forEach((unbind) => unbind())
	}, [
		mode,
		isInitialized,
		isDocumentLoaded,
		getViewerPanes,
		layoutEpoch,
		isCanvasModeActive,
		trySelectHighlightFromMouseEvent,
	])

	// Persist highlight coordinate edits (drag-move / resize) to the backend (REQ-350). Lives in
	// interactions/useCoordinateCommit alongside its own mutation-queue plumbing: it wires
	// AnnotationService.onAnnotationCoordinatesChanged and PATCHes furniture / eref / ecall moves
	// through the shared per-entity queue, reverting the highlight on any failure.
	useCoordinateCommit({
		mode,
		isInitialized,
		queryClient,
		formatError,
		numericQuotationIdRef,
		quotationDetailRef,
		isCoordinateEditAllowed: isEditToolActive,
		updateFurnitureAsync,
		updateErefAsync,
		updateEcallAsync,
		updatePageAsync,
	})

	// ---- Note tool (REQ-395/396) -------------------------------------------------------------
	// Tears down every OTHER canvas mode before arming the note tool — mirrors every `handleAddX`
	// above's own "always takes over" behavior. Composed from the same closers those already use
	// (each stored in a ref so this stays stable without listing all five as dependencies).
	const cancelOtherCanvasModesForNote = useCallback(() => {
		cancelConnectObjects()
		closeCreateErefRef.current?.()
		closeCountertopCreateRef.current?.()
		closeCreateEcallRef.current?.()
		closeCreateFurnitureRef.current?.()
		closeCreateOtherRef.current?.()
		// Mirror of the highlight tools' own cancel: without this, arming the note tool while a shape
		// or the Pen tool was armed left two tool-options rows fighting over the header's single row
		// slot. Both highlight tools go through `disarmHighlightTools`, not `disarmShapeTool` alone —
		// the store's arm actions make shape/freehand exclusive of EACH OTHER, but neither knows about
		// the note tool, so this direction has to be spelled out here.
		disarmHighlightTools()
		clearHighlightSelection()
	}, [cancelConnectObjects, disarmHighlightTools, clearHighlightSelection])

	// Declared BEFORE useNoteCreate: its `onNoteCreated` hand-off calls `scheduleCommitForNote`, and
	// hooks run top-to-bottom in one render, so this must exist first.
	const { deselectNote, scheduleCommitForNote } = useNoteEdit({
		instanceRef,
		getViewerPanes,
		layoutEpoch,
		mode,
		isInitialized,
		numericQuotationIdRef,
		queryClient,
		formatError,
		success,
		getShadowRoot,
		noteToolbarEntryRef,
	})
	deselectNoteRef.current = deselectNote

	const { armNoteTool, disarmNoteTool } = useNoteCreate({
		instanceRef,
		mode,
		numericQuotationIdRef,
		queryClient,
		formatError,
		cancelOtherCanvasModes: cancelOtherCanvasModesForNote,
		onNoteCreated: (note, annot) => scheduleCommitForNote(note.id, annot),
	})
	disarmNoteToolRef.current = disarmNoteTool

	// Header button click: arm if not armed, disarm if armed. Reads the flag via `getState()` (like
	// `openAddMenu` above) rather than a subscription — the header captures its props once, so a
	// subscribed value would be frozen, and a changing identity re-runs `applyHeaderAndTools`, whose
	// `setToolModeOnAllPanes(EDIT)` then disarms the tool that was just armed (the "two clicks" bug).
	//
	// Both handlers are handed out through a live ref for the same reason `handleToggleSplit` below
	// is: `disarmNoteTool` closes over `mode` (it only resets Apryse's tool mode in editor mode), so
	// its identity changes on a preview -> editor switch. Passing that straight through made
	// `applyHeaderAndTools` — and therefore the whole init effect that lists it as a dependency —
	// re-run on the mode switch, which is exactly the second-`WebViewer()`-construction hazard #1119
	// exists to prevent (`useQuotationViewer.test.tsx`'s "reuses the single instance across a
	// preview -> editor mode switch" caught it). The ref keeps the identity fixed while the closure
	// behind it stays current.
	toggleNoteToolRef.current = () => {
		if (useNoteToolStore.getState().isArmed) disarmNoteTool()
		else armNoteTool()
	}
	const handleToggleNoteTool = useCallback(() => toggleNoteToolRef.current(), [])

	// The tool-options row's close (X) button: disarms the tool while drawing, or deselects the
	// currently-selected note when the row is up because of a selection instead (the same bar
	// doubles as both — see `noteTool.store`'s own comment).
	const closeNoteToolbarRef = useRef<() => void>(() => {})
	closeNoteToolbarRef.current = () => {
		if (!disarmNoteTool()) deselectNote()
	}
	const handleCloseNoteToolbar = useCallback(() => closeNoteToolbarRef.current(), [])

	// The current document's bytes, fetched once (below) and cached so entering split screen
	// doesn't make the second DocumentViewer re-download the whole file over the network (PR #1347
	// review — a 15MB+ document was a "considerable delay" every time). Read by useSplitScreen's
	// enterSplit, which falls back to the URL if this is still null (fetch in flight, or it
	// failed). Reset to null at the top of every reload, before the new fetch starts, so it can
	// never hold a blob for a document that isn't the current one.
	//
	// Deliberately NOT sharing the primary viewer's Core.Document object with the second viewer —
	// that was tried first (it skips the fetch too) and corrupted rendering on any split-screen
	// exit (blank canvas, confirmed by reverting and retesting live). Each viewer here still gets
	// its own independent Document instance via loadDocument, parsed from the SAME already-fetched
	// bytes — the network round-trip is what's expensive for a large file, not the parse.
	const documentBlobRef = useRef<Blob | null>(null)

	// The in-flight request for those bytes, started the moment the viewer begins OPENING rather
	// than after it has finished initializing. `initialDoc` used to give that overlap for free —
	// Apryse fetched the PDF while its own UI bundle and wasm worker were still booting — so
	// dropping it (to stop the file being downloaded and parsed twice) accidentally serialised the
	// two: boot, THEN download, THEN parse. On a fast machine that is a second; on a slow CI runner
	// with a 15MB drawing it pushed the first render past the point where the editor looks broken,
	// which is what took out every quotation E2E test. Holding the promise here restores the
	// overlap while keeping the single download.
	//
	// Keyed by identity so the load effect can tell "the bytes I asked for" from "bytes for a
	// document that has since been replaced".
	const documentRequestRef = useRef<{ identity: string | undefined; promise: Promise<Blob> } | null>(null)

	// Split-screen / side-by-side view (REQ-399). Owns Apryse MultiViewerMode enter/exit, loads the
	// same document into the second viewer, and mirrors the detection layer into its annotation
	// manager. Both panes stay projections of the one quotation cache, so a coordinate edit in
	// either one commits through the same per-entity mutation queue (no version mismatch).
	const { isSplitActive, canSplit, setSplit, toggleSplit, closeLeftView, closeRightView } = useSplitScreen({
		mode,
		isInitialized,
		isDocumentLoaded,
		fileLink,
		instanceRef,
		documentBlobRef,
	})

	// The divider between the two panes (#1415). Ours, not Apryse's — its own bar maps a drag against
	// the whole viewer's left edge and over-commits the row, so it is hidden and this takes its place.
	// Everything it does lives in the hook, core/paneDivider.ts and paneDivider.helpers.ts.
	usePaneDivider({ instanceRef, isInitialized, isSplitActive, layoutEpoch, getShadowRoot })

	// The custom header's `render()` callback captures its props ONCE (Apryse owns that lifecycle),
	// so the header is handed a referentially-stable wrapper that always calls the latest closure.
	// Its ACTIVE / PENDING / AVAILABLE states are read from splitScreen.store inside the header, not
	// passed as props — a prop captured at render time would never update.
	//
	// The header sets split screen EXPLICITLY, not by toggling: since decision D10 the layout menu's
	// `Single Page` / `Side-by-side` rows are what turn it off and on, and a row must land on its own
	// state rather than flip whatever the current one is. Both go through `setSplit`, the single
	// guarded entry point in useSplitScreen.
	// Declared up with the header-menu hotkey handlers, which need it too (the D shortcut) and run
	// before this line does.
	setSplitRef.current = setSplit
	const handleSetSplitActive = useCallback((active: boolean) => setSplitRef.current?.(active), [])
	// Omitting the prop is what HIDES the split control (and disables the two pane-count rows), so it
	// may only be gated on the feature flag — which is constant for the session. Gating on `canSplit`
	// here would capture `false` at init (the document has not loaded yet) and hide it permanently.
	const onSetSplitActive = SPLIT_SCREEN_ENABLED ? handleSetSplitActive : undefined

	// Split-view menu's "Close left view" / "Close right view" rows — same ref-wrapper pattern as
	// onSetSplitActive, and for the same reason (the header's render() callback captures its props
	// once).
	const closeLeftViewRef = useRef(closeLeftView)
	closeLeftViewRef.current = closeLeftView
	const handleCloseLeftView = useCallback(() => closeLeftViewRef.current(), [])
	const onCloseLeftView = SPLIT_SCREEN_ENABLED ? handleCloseLeftView : undefined

	const closeRightViewRef = useRef(closeRightView)
	closeRightViewRef.current = closeRightView
	const handleCloseRightView = useCallback(() => closeRightViewRef.current(), [])
	const onCloseRightView = SPLIT_SCREEN_ENABLED ? handleCloseRightView : undefined

	// The second view in its OWN browser window (#1430) — the only form of it that can be dragged
	// onto another monitor. Owns `window.open`, the placement, the closed-poll and the control
	// channel the two windows agree over; the menu rows it feeds are built in the header from
	// popOutWindow.store, not from props.
	//
	// It takes the in-window split's own entry points rather than reaching into Apryse itself:
	// popping out hands over FROM a live split (collapsing it onto the left pane, viewport intact),
	// and returning hands back TO one.
	const { popOut, returnViewBack, isPopOutOpen, openerId } = usePopOutWindow({
		mode,
		isDocumentLoaded,
		drawingStatus,
		isSplitActive,
		setSplit,
		closeRightView,
	})

	// Cross-window "Auto open floor plan" (#1430 review) — the pop-out's counterpart to
	// useSmartNavigation, wired through popOutFollowNotifyRef (declared near notifySmartNavSelection,
	// well above the click handlers this depends on).
	const { notifyFollowSelection } = usePopOutFollowNavigation({
		openerId,
		isPeerWindowOpen: isPopOutOpen,
		allEcallsRef,
		findErefWithFurnitureById,
		findFurnitureById,
		findEcallById,
		handleErefClick,
		handleEcallClick,
	})
	popOutFollowNotifyRef.current = notifyFollowSelection

	// Same ref-wrapper pattern as onSetSplitActive, and for the same reason — the header's `render()`
	// callback captures its props once, so the header must be handed a stable wrapper that always
	// calls the latest closure.
	const popOutRef = useRef(popOut)
	popOutRef.current = popOut
	const handlePopOut = useCallback(() => popOutRef.current(), [])
	// Gated on the feature flag alone, which is constant for the session: omitting the prop REMOVES
	// the row, so gating it on anything dynamic (availability, say) would capture `false` at init —
	// before the document has loaded — and remove the row for good.
	const onPopOut = POP_OUT_WINDOW_ENABLED ? handlePopOut : undefined

	const returnViewBackRef = useRef(returnViewBack)
	returnViewBackRef.current = returnViewBack
	const handleReturnViewBack = useCallback(() => returnViewBackRef.current(), [])
	const onReturnViewBack = POP_OUT_WINDOW_ENABLED ? handleReturnViewBack : undefined

	// Copy/paste wiring (REQ-368): right-click a furniture → Copy menu; right-click empty
	// space → Paste menu (only when a furniture was copied for this quotation); Ctrl/Cmd+C
	// copies the selected furniture; Ctrl/Cmd+V pastes at the last cursor position. Editor only.
	//
	// Split screen (REQ-399): bound per pane and re-bound on `layoutEpoch` (the pre-split scroll
	// element is detached once the mode re-mounts the document area — that is why the menu stopped
	// opening in BOTH panes). Each pane hit-tests through its OWN annotation manager and tags itself
	// as the active pane, so the menu opens at the cursor over the pane that was right-clicked and
	// Paste resolves its page point in that same pane.
	useEffect(() => {
		if (mode !== "editor" || !isInitialized || !instanceRef.current) return
		const panes = getViewerPanes()
		if (panes.length === 0) return

		const bindings = panes.map(({ pane, documentViewer, scrollEl }) => {
			const annotationManager = documentViewer.getAnnotationManager()

			const onPointerMove = (e: MouseEvent) => {
				lastPointerClientRef.current = { x: e.clientX, y: e.clientY }
				AnnotationService.setActivePane(pane)
			}

			const onContextMenu = (e: MouseEvent) => {
				// Suppress the native browser menu inside the document area.
				e.preventDefault()
				lastPointerClientRef.current = { x: e.clientX, y: e.clientY }
				AnnotationService.setActivePane(pane)

				// No entity menus while connect-objects is picking — its own bar is the only affordance.
				if (isCanvasModeActive()) return

				const annot = annotationManager.getAnnotationByMouseEvent(e)
				const metadata = annot ? AnnotationService.getMetadata(annot.Id) : undefined
				if (metadata?.entityType === "furniture") {
					const furniture = findFurnitureById(metadata.entityId)
					if (furniture) {
						// Right-clicking a furniture that is part of a batch keeps the multi-selection and
						// shows the batch menu; otherwise it single-selects and shows Copy / Duplicate.
						const selectedIds = AnnotationService.getSelectedFurnitureIds()
						if (selectedIds.length > 1 && selectedIds.includes(furniture.id)) {
							contextMenuBatchRef.current = getSelectedFurniture()
							contextMenuFurnitureRef.current = null
							contextMenuPointRef.current = null
							openContextMenuAt(e.clientX, e.clientY, "furniture-batch")
							return
						}
						AnnotationService.selectFurniture(furniture.id)
						contextMenuFurnitureRef.current = furniture
						contextMenuBatchRef.current = []
						contextMenuPointRef.current = null
						openContextMenuAt(e.clientX, e.clientY, "furniture")
						return
					}
				}

				// Right-clicking an elevation reference selects it (which opens its sidebar and shows the
				// pill, per REQ-367) and opens the single-eref Delete menu (REQ-376).
				if (metadata?.entityType === "eref") {
					const eref = findErefWithFurnitureById(metadata.entityId)
					if (eref) {
						AnnotationService.selectAnnotationFromMouseEvent(e)
						contextMenuFurnitureRef.current = null
						contextMenuBatchRef.current = []
						contextMenuErefRef.current = eref
						contextMenuPointRef.current = null
						openContextMenuAt(e.clientX, e.clientY, "eref")
						return
					}
				}

				// Right-clicking an elevation callout selects it (which opens its sidebar) and opens the
				// single-ecall menu (REQ-374): Navigate to elevation (when it has connected erefs) + Delete.
				if (metadata?.entityType === "ecall") {
					const ecall = findEcallById(metadata.entityId)
					if (ecall) {
						// Right-clicking an ecall that is already part of a multi-entity selection must not
						// collapse it down to a single pick — REQ-374 shows neither the toolbar nor the menu
						// once more than one ecall is selected, so leave the selection untouched and show
						// nothing (mirrors the furniture-batch check above, applied one level up here since
						// there is no ecall batch menu to show instead).
						const priorEntities = AnnotationService.getSelectedEntities()
						const isPartOfMultiSelection =
							priorEntities.length > 1 &&
							priorEntities.some((entity) => entity.entityId === ecall.id && entity.entityType === "ecall")
						if (isPartOfMultiSelection) {
							closeContextMenu()
							return
						}
						// Same one-shot pane tag the plain-click path sets — a right-click in the right pane
						// must open that pane's sidebar too, not the left screen's.
						selectSourcePaneRef.current = pane
						if (pane === "split") AnnotationService.selectSplitAnnotationFromMouseEvent(e)
						else AnnotationService.selectAnnotationFromMouseEvent(e)
						selectSourcePaneRef.current = "primary"
						contextMenuFurnitureRef.current = null
						contextMenuBatchRef.current = []
						contextMenuErefRef.current = null
						contextMenuEcallRef.current = ecall
						openContextMenuAt(e.clientX, e.clientY, "ecall", pane)
						return
					}
				}

				// A page box has no menu action, and neither does an eref/ecall that has already left the
				// cache (the branches above only `return` once `findErefWithFurnitureById` /
				// `findEcallById` actually resolves) — so suppress the native menu and show nothing for
				// any of the three here. No Paste over them either, otherwise a right-click on one would
				// fall through to the empty-space Paste branch and drop furniture at that point. The
				// mousedown handler bails on secondary-click, so select the entity here (a page just shows
				// its handles), matching the plain-click behavior above.
				if (metadata?.entityType === "page" || metadata?.entityType === "eref" || metadata?.entityType === "ecall") {
					// Same one-shot pane tag the plain-click path sets — a right-click in the right pane
					// must open that pane's sidebar too, not the left screen's.
					selectSourcePaneRef.current = pane
					if (pane === "split") AnnotationService.selectSplitAnnotationFromMouseEvent(e)
					else AnnotationService.selectAnnotationFromMouseEvent(e)
					selectSourcePaneRef.current = "primary"
					closeContextMenu()
					return
				}

				// Empty space → Paste, only when a copy exists for this quotation.
				const qid = numericQuotationIdRef.current
				if (qid != null && hasClipboardFor(qid)) {
					const point = AnnotationService.getPagePointFromClient(e.clientX, e.clientY, pane)
					if (point) {
						contextMenuFurnitureRef.current = null
						contextMenuPointRef.current = point
						openContextMenuAt(e.clientX, e.clientY, "free")
						return
					}
				}
				closeContextMenu()
			}

			scrollEl.addEventListener("mousemove", onPointerMove, { passive: true })
			scrollEl.addEventListener("contextmenu", onContextMenu, true)
			return () => {
				scrollEl.removeEventListener("mousemove", onPointerMove)
				scrollEl.removeEventListener("contextmenu", onContextMenu, true)
			}
		})

		// Keyboard shortcuts are window-level (one binding regardless of pane count). Paste resolves
		// its page point in whichever pane the cursor was last over — AnnotationService's active pane.
		const onKeyDown = (e: KeyboardEvent) => {
			// Copy / paste / duplicate act on a normal selection; connect-objects owns its own.
			if (isCanvasModeActive()) return
			// Don't hijack typing in inputs (event.target is retargeted to the host in shadow DOM).
			const target = (e.composedPath?.()[0] as HTMLElement | undefined) ?? (e.target as HTMLElement | null)
			const tag = target?.tagName
			if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) return

			// Backspace / Delete lives in the hotkey registry (see handleDeleteSelection above), not
			// here — this listener only owns the clipboard combos.
			if (!(e.metaKey || e.ctrlKey)) return
			if (e.code !== "KeyC" && e.code !== "KeyV" && e.code !== "KeyD") return

			if (e.code === "KeyC") {
				// Leave an active text selection to the browser's own copy.
				if ((window.getSelection?.()?.toString() ?? "").trim() !== "") return
				// Copy is furniture-only and single-item. Drive it from the live selection: more
				// than one furniture selected is rejected with a message; nothing copyable
				// (no furniture / only an eref or ecall selected) is silently ignored.
				const selectedFurnitureIds = AnnotationService.getSelectedFurnitureIds()
				if (selectedFurnitureIds.length > 1) {
					e.preventDefault()
					error("You can only copy one object at a time")
					return
				}
				if (selectedFurnitureIds.length === 0) return
				const furniture = findFurnitureById(selectedFurnitureIds[0])
				if (!furniture) return
				e.preventDefault()
				copyFurnitureToClipboard(furniture)
				return
			}

			// Ctrl/Cmd+D → duplicate the selected furniture in place (overrides browser bookmark).
			// Furniture-only and single-item, driven by the live selection — same rules as Copy.
			if (e.code === "KeyD") {
				const selectedFurnitureIds = AnnotationService.getSelectedFurnitureIds()
				if (selectedFurnitureIds.length > 1) {
					e.preventDefault()
					error("You can only duplicate one object at a time")
					return
				}
				if (selectedFurnitureIds.length === 0) return
				const furniture = findFurnitureById(selectedFurnitureIds[0])
				if (!furniture) return
				e.preventDefault()
				void duplicateFurnitureInPlace(furniture)
				return
			}

			// Ctrl/Cmd+V → paste at the last cursor position over the document.
			const qid = numericQuotationIdRef.current
			if (qid == null || !hasClipboardFor(qid)) return
			const last = lastPointerClientRef.current
			if (!last) return
			const point = AnnotationService.getPagePointFromClient(last.x, last.y)
			if (!point) return
			e.preventDefault()
			void pasteFurnitureAtPoint(point)
		}

		window.addEventListener("keydown", onKeyDown)
		return () => {
			bindings.forEach((unbind) => unbind())
			window.removeEventListener("keydown", onKeyDown)
		}
	}, [
		mode,
		isInitialized,
		isDocumentLoaded,
		getViewerPanes,
		layoutEpoch,
		findFurnitureById,
		findErefWithFurnitureById,
		findEcallById,
		getSelectedFurniture,
		openContextMenuAt,
		closeContextMenu,
		copyFurnitureToClipboard,
		pasteFurnitureAtPoint,
		duplicateFurnitureInPlace,
		error,
		isCanvasModeActive,
	])

	// Render the floating batch toolbar + its confirmation modals into the shadow-DOM overlay.
	// The pill is positioned above the selection; the modals portal out via their own shadow-root
	// containers, so their placement is independent of the pill's.
	useEffect(() => {
		const entry = selectionToolbarEntryRef.current
		if (!entry) return
		const shadowRoot = getShadowRoot()
		const hasContent = !!(selectionToolbar || connectModal || deleteConfirm || pageNameEdit)
		if (!shadowRoot || !hasContent) {
			entry.container.style.display = "none"
			safeRender(selectionToolbarEntryRef, null)
			return
		}

		// Hide the pill while a modal is open — the pill overlay sits above the AntD modal, so it
		// would otherwise float over the dialog. The selection is preserved, so the pill returns
		// when the modal is dismissed.
		const modalOpen = !!(connectModal || deleteConfirm || pageNameEdit)
		const showPill = !!selectionToolbar && !modalOpen
		// Anchor to whatever the pill is acting on — the eref/ecall box for that kind of selection.
		if (showPill) {
			positionSelectionToolbar(
				selectionToolbar?.kind === "eref" ? ["eref"] : selectionToolbar?.kind === "ecall" ? ["ecall"] : ["furniture"],
			)
		} else entry.container.style.display = "none"

		const actions = !selectionToolbar
			? []
			: selectionToolbar.kind === "eref"
				? buildErefToolbarActions(selectionToolbar.eref)
				: selectionToolbar.kind === "ecall"
					? buildEcallToolbarActions(selectionToolbar.ecall)
					: buildToolbarActions(selectionToolbar.furniture)

		safeRender(
			selectionToolbarEntryRef,
			<ShadowDomProviders shadowRoot={shadowRoot}>
				{showPill ? <SelectionToolbar actions={actions} /> : null}
				{connectModal ? (
					<ConnectToElevationModal
						isOpen
						erefs={connectModal.erefs}
						isPending={batchPending}
						confirmLabel="Connect"
						{...buildConnectModalCopy(connectModal.mode, connectModal.furniture, connectModal.erefs)}
						onClose={() => {
							if (!batchPending) setConnectModal(null)
						}}
						onConfirm={(erefId) => void runConnectOrMove(erefId, connectModal.furniture)}
					/>
				) : null}
				{deleteConfirm ? (
					<ConfirmModal
						isOpen
						{...buildDeleteConfirmText(deleteConfirm)}
						confirmLabel="Delete"
						isPending={batchPending}
						onClose={() => {
							if (!batchPending) setDeleteConfirm(null)
						}}
						onConfirm={() => {
							if (deleteConfirm.kind === "eref") void runErefDelete(deleteConfirm.eref)
							else if (deleteConfirm.kind === "ecall") void runEcallDelete(deleteConfirm.ecall)
							else void runBatchDelete(deleteConfirm.furniture)
						}}
					/>
				) : null}
				{pageNameEdit ? (
					<SetPageNameModal
						isOpen
						currentName={pageNameEdit.pageName}
						isPending={pageNamePending}
						errorMessage={pageNameError}
						onClose={() => {
							if (!pageNamePending) {
								setPageNameEdit(null)
								setPageNameError(null)
							}
						}}
						onConfirm={(name) => void handleSavePageName(name)}
					/>
				) : null}
			</ShadowDomProviders>,
		)
	}, [
		selectionToolbar,
		connectModal,
		deleteConfirm,
		pageNameEdit,
		pageNamePending,
		pageNameError,
		handleSavePageName,
		batchPending,
		getShadowRoot,
		positionSelectionToolbar,
		buildToolbarActions,
		buildErefToolbarActions,
		buildEcallToolbarActions,
		runConnectOrMove,
		runBatchDelete,
		runErefDelete,
		runEcallDelete,
	])

	// Render the floating "delete highlight" toolbar (REQ-400 decision 2/3) — trash icon only; the
	// color/opacity/thickness controls the Figma "Selected" state shows have no PATCH endpoint to
	// act on (there is no update endpoint for a highlight, only create/delete), so they're not
	// built. Position/visibility is owned by useHighlightSelect's own
	// positionHighlightToolbar + its scroll/zoom/resize effect; this effect only owns what's
	// painted into the container, mirroring how the batch pill above splits the same two concerns.
	useEffect(() => {
		const entry = highlightToolbarEntryRef.current
		if (!entry) return
		const shadowRoot = getShadowRoot()
		if (!shadowRoot || !selectedHighlight) {
			safeRender(highlightToolbarEntryRef, null)
			return
		}
		const deleteAction: TToolbarAction = {
			key: "delete",
			label: "Delete",
			icon: <TrashIcon />,
			iconOnly: true,
			disabled: isDeletingHighlight,
			onSelect: () => void deleteSelectedHighlight(),
		}
		safeRender(
			highlightToolbarEntryRef,
			<ShadowDomProviders shadowRoot={shadowRoot}>
				<SelectionToolbar actions={[deleteAction]} />
			</ShadowDomProviders>,
		)
	}, [selectedHighlight, isDeletingHighlight, deleteSelectedHighlight, getShadowRoot])

	// Keep the toolbar anchored to the selection as the document scrolls / zooms / resizes. Bound to
	// EVERY pane (either can scroll the highlight the pill is tracking) and re-bound on layoutEpoch.
	useEffect(() => {
		if (!selectionToolbar) return
		const entityTypes: TAnnotationType[] =
			selectionToolbar.kind === "eref" ? ["eref"] : selectionToolbar.kind === "ecall" ? ["ecall"] : ["furniture"]
		const onMove = () => positionSelectionToolbar(entityTypes)
		const bindings = getViewerPanes().map(({ documentViewer, scrollEl }) => {
			scrollEl.addEventListener("scroll", onMove, { passive: true })
			documentViewer.addEventListener("zoomUpdated", onMove)
			return () => {
				scrollEl.removeEventListener("scroll", onMove)
				documentViewer.removeEventListener("zoomUpdated", onMove)
			}
		})
		window.addEventListener("resize", onMove)
		onMove()
		return () => {
			bindings.forEach((unbind) => unbind())
			window.removeEventListener("resize", onMove)
		}
	}, [selectionToolbar, positionSelectionToolbar, getViewerPanes, layoutEpoch])

	// After any cache change (mutation / refetch) rebuild what the toolbar acts on from the live
	// selection so the pill's contextual actions reflect the new versions + statuses. An eref that
	// left the cache (deleted) drops the pill. Functional update → no dependency on the current
	// toolbar value (no feedback loop).
	useEffect(() => {
		setSelectionToolbar((prev) => {
			if (!prev) return prev
			if (prev.kind === "eref") {
				const eref = findErefWithFurnitureById(prev.eref.id)
				return eref ? { kind: "eref", eref } : null
			}
			if (prev.kind === "ecall") {
				const ecall = findEcallById(prev.ecall.id)
				return ecall ? { kind: "ecall", ecall } : null
			}
			const furniture = getSelectedFurniture()
			return furniture.length > 0 ? { kind: "furniture", furniture } : null
		})
	}, [relatedObjects, getSelectedFurniture, findErefWithFurnitureById, findEcallById])

	// Preview mode has no selection affordances — drop the toolbar + any open batch modals, and end
	// an in-progress connect-objects session (which also unfreezes the highlights it locked).
	useEffect(() => {
		if (mode !== "editor") {
			setSelectionToolbar(null)
			setConnectModal(null)
			setDeleteConfirm(null)
			setPageNameEdit(null)
			setPageNameError(null)
			cancelConnectObjects()
			closeCreateEcall()
			closeCreateEref()
			closeCreateCountertop()
			closeCreateFurniture()
			closeCreateOther()
		}
	}, [
		mode,
		cancelConnectObjects,
		closeCreateEref,
		closeCreateEcall,
		closeCreateCountertop,
		closeCreateFurniture,
		closeCreateOther,
	])

	// Closing the viewer modal leaves the instance alive (#1119), so the mode has to be torn down
	// explicitly — otherwise a reopen comes back with a stale bar and frozen highlights.
	useEffect(() => {
		if (!isModalOpen) {
			cancelConnectObjects()
			closeCreateEcall()
			closeCreateEref()
			closeCreateCountertop()
			closeCreateFurniture()
			closeCreateOther()
			// The help panel and an open header dropdown are transient too — reopening the editor should
			// not resume either (REQ-359).
			closeKeyboardShortcuts()
			useViewerMenusStore.getState().setOpenMenu(null)
		}
	}, [
		isModalOpen,
		cancelConnectObjects,
		closeCreateEref,
		closeCreateEcall,
		closeCreateCountertop,
		closeCreateFurniture,
		closeCreateOther,
		closeKeyboardShortcuts,
	])

	// "Connect objects" bar (REQ-372) — mounted only while the mode is open, into its own overlay at
	// the top of the document area. The count mirrors the live canvas selection the mode maintains.
	const connectObjectsShadowRoot = getShadowRoot()
	useShadowMount(
		connectObjectsEntryRef,
		connectSession && connectObjectsShadowRoot ? (
			<StyleProvider container={connectObjectsShadowRoot}>
				<ConnectObjectsBar
					elevationLabel={buildErefDisplayLabel(connectSession.eref)}
					selectedCount={selectedConnectIds.length}
					isPending={batchPending}
					onConnect={() => void runConnectObjects()}
					onCancel={cancelConnectObjects}
				/>
			</StyleProvider>
		) : null,
		mode === "editor" && !!connectSession,
	)

	// Keyboard-shortcuts help panel (REQ-359) — an editor affordance, opened from the header's help
	// menu. Mounted into its own bottom-docked overlay only while open.
	const shortcutsPanelShadowRoot = getShadowRoot()
	useShadowMount(
		shortcutsPanelEntryRef,
		mode === "editor" && isShortcutsPanelOpen && shortcutsPanelShadowRoot ? (
			<StyleProvider container={shortcutsPanelShadowRoot}>
				<KeyboardShortcutsPanel onClose={closeKeyboardShortcuts} />
			</StyleProvider>
		) : null,
		mode === "editor" && isShortcutsPanelOpen,
	)

	// While the panel is docked over the bottom of the drawing, give the scroller that much extra room
	// at the end so nothing stays trapped underneath it — see core/bottomDockInset. Measured rather
	// than hardcoded, and retried per frame because the panel's height only exists once React has
	// painted it into the overlay (the same async-mount problem runWhenReady exists for). Re-runs on
	// layoutEpoch: entering or leaving split screen replaces the scroll elements this writes to.
	useEffect(() => {
		const instance = instanceRef.current
		if (!instance) return
		if (mode !== "editor" || !isShortcutsPanelOpen) {
			applyBottomDockInset(instance, 0)
			return
		}
		const cancel = runWhenReady(() => {
			const height = shortcutsPanelEntryRef.current?.container?.getBoundingClientRect().height ?? 0
			if (!height) return false
			applyBottomDockInset(instance, height + SHORTCUTS_PANEL_INSET)
			return true
		})
		return () => {
			cancel()
			applyBottomDockInset(instance, 0)
		}
	}, [mode, isShortcutsPanelOpen, layoutEpoch])

	// Re-stack the top bars whenever either appears or disappears: the ResizeObserver only fires on an
	// actual layout change, which showing an absolutely-positioned overlay isn't. Runs after
	// useJumpNavigation's own render effect (declared above), so the jump bar is already shown and
	// positioned by the time getConnectBarOffset measures it.
	useEffect(() => {
		repositionOverlaysRef.current?.()
	}, [connectSession, jumpSession])

	// The inverse case: the tool-options row DOES change layout, so the observer fires — but a frame
	// late, so the overlays jumped a second time right after the document area resized. The header
	// calls this from a layout effect in its own commit instead. Stable identity: the header captures
	// its props once.
	const repositionOverlaysOnToolRowToggle = useCallback(() => {
		repositionOverlaysRef.current?.()
	}, [])

	// Full-width bars currently stacked across the top of the document area: the jumping-nav bar, and
	// the connect-objects picker below it. Drives the clearance of anything that would otherwise hide
	// underneath them.
	// The elevation-references toggle lives in the PRIMARY pane, so a jumping bar rendered over the
	// SPLIT pane (#1311 smart navigation) must not push it down.
	const topBarCount = ((jumpSession && jumpSession.pane === "primary" ? 1 : 0) + (connectSession ? 1 : 0)) as 0 | 1 | 2

	// Toggle button — an editor affordance, hidden in preview and while the left panel is open. The
	// wrapper's visibility is toggled via `visible`; the button itself drops below whatever top bars
	// are up so it stays reachable rather than sitting underneath them.
	const toggleShadowRoot = getShadowRoot()
	useShadowMount(
		toggleEntryRef,
		mode === "editor" && toggleShadowRoot ? (
			<StyleProvider container={toggleShadowRoot}>
				<ElevationReferencesToggle onOpen={openPanel} topBarCount={topBarCount} />
			</StyleProvider>
		) : null,
		mode === "editor" && isDocumentLoaded && !isPanelOpen,
	)

	// Furniture selected on the canvas (single or batch) → highlight the matching left-sidebar rows.
	// Sourced from the same live selection that drives the floating toolbar.
	const selectedFurnitureIds = useMemo(
		() => new Set((selectionToolbar?.kind === "furniture" ? selectionToolbar.furniture : []).map((f) => f.id)),
		[selectionToolbar],
	)

	// Left elevation-references sidebar — kept in sync declaratively (the addPanel callback owns
	// the initial paint at root-creation time; this keeps it fresh as selection/data change).
	const leftShadowRoot = getShadowRoot()
	useShadowMount(
		sidebarLeftEntryRef,
		leftShadowRoot ? (
			<StyleProvider container={leftShadowRoot}>
				<ElevationReferencesSidebar
					onClose={closePanel}
					onErefClick={handleErefClick}
					onFurnitureClick={handleFurnitureClick}
					onEditPage={handleEditPage}
					relatedObjects={relatedObjectsForSidebar}
					selectedErefPageNumber={selectedEref?.page_number ?? null}
					selectedFurnitureIds={selectedFurnitureIds}
					mismatchedFurnitureIds={mismatchedFurnitureIds}
				/>
			</StyleProvider>
		) : null,
	)

	// The read-only Page value and the pre-filled elevation number of the create panel, both derived
	// from the page the area was drawn on (null / "1" until there is one).
	const createAreaPageNumber = createSession?.area?.pageNumber ?? createEcallSession?.area?.pageNumber
	const createPageName = useMemo(
		() =>
			createAreaPageNumber == null
				? null
				: findPageName(quotationDetail?.page_infos, relatedObjects, createAreaPageNumber),
		[createAreaPageNumber, quotationDetail?.page_infos, relatedObjects],
	)
	const createSuggestedElevationNumber = useMemo(
		() => (createAreaPageNumber == null ? "1" : suggestNextElevationNumber(relatedObjects, createAreaPageNumber)),
		[createAreaPageNumber, relatedObjects],
	)
	const createPageNames = useMemo(() => {
		const namesByPage = new Map<number, string>()
		quotationDetail?.page_infos?.forEach((page) => {
			if (page.page_name) namesByPage.set(page.page_number, page.page_name)
		})
		relatedObjects?.forEach((page) => {
			if (page.page_name && !namesByPage.has(page.page_number)) {
				namesByPage.set(page.page_number, page.page_name)
			}
		})
		return [...namesByPage.entries()].sort(([a], [b]) => a - b).map(([, name]) => name)
	}, [quotationDetail?.page_infos, relatedObjects])

	// The elevations the create-object panel (REQ-293) may link to: erefs are page-bound and the
	// endpoint rejects a cross-page link, so only the drawn page's own are offered.
	const createFurnitureAreaPageNumber = createFurnitureSession?.area?.pageNumber
	const createFurniturePageErefs = useMemo<TEref[]>(() => {
		if (createFurnitureAreaPageNumber == null) return EMPTY_EREFS
		return relatedObjects?.find((page) => page.page_number === createFurnitureAreaPageNumber)?.erefs ?? EMPTY_EREFS
	}, [relatedObjects, createFurnitureAreaPageNumber])

	// Same rule for the other-object panel (REQ-299): erefs are page-bound, so only the drawn page's
	// own are offered.
	const createOtherAreaPageNumber = createOtherSession?.area?.pageNumber
	const createOtherPageErefs = useMemo<TEref[]>(() => {
		if (createOtherAreaPageNumber == null) return EMPTY_EREFS
		return relatedObjects?.find((page) => page.page_number === createOtherAreaPageNumber)?.erefs ?? EMPTY_EREFS
	}, [relatedObjects, createOtherAreaPageNumber])

	// Same rule for the custom-object panel (#1371): erefs are page-bound, so only the drawn page's
	// own are offered.
	const createCustomAreaPageNumber = createCustomSession?.area?.pageNumber
	const createCustomPageErefs = useMemo<TEref[]>(() => {
		if (createCustomAreaPageNumber == null) return EMPTY_EREFS
		return relatedObjects?.find((page) => page.page_number === createCustomAreaPageNumber)?.erefs ?? EMPTY_EREFS
	}, [relatedObjects, createCustomAreaPageNumber])

	const connectedEcallsForSelectedFurniture = useMemo(
		() => getEcallsForErefId(allEcalls, selectedFurniture?.eref_id),
		[selectedFurniture, allEcalls],
	)

	// Batch furniture panel (REQ-403). Built once per render so BOTH panel branches consume the SAME
	// element: the declarative repaint below, and the `addPanel` render() callback's first frame,
	// which reaches it through the ref because Apryse calls render() outside React's render pass.
	const batchPanelContent: ReactNode =
		batchFurniture.length > 0 ? (
			<BatchFurnitureSidebar
				furniture={batchFurniture}
				relatedObjects={relatedObjects}
				ecalls={allEcalls}
				deliveryDates={quotationDetail?.configuration?.delivery_dates}
				isPending={isBatchEditPending || batchPending}
				skipCategoryConfirm={skipCategoryConfirm}
				onSkipCategoryConfirmChange={setSkipCategoryConfirm}
				onClose={closeBatchFurniture}
				onNavigateToEref={(erefId) => {
					const eref = findErefWithFurnitureById(erefId)
					if (eref) openErefInSidebar(eref)
				}}
				onDelete={() => setDeleteConfirm({ kind: "furniture", furniture: batchFurniture })}
				onView={handleConnectedCalloutView}
				onErefChange={handleBatchErefChange}
				onCommitDimension={commitBatchDimension}
				onCommitDeliveryDate={commitBatchDeliveryDate}
				onCommitModelNumber={commitBatchModelNumber}
				onCommitConfig={commitBatchConfig}
				onCommitCountertopAttachments={commitBatchCountertopAttachments}
				onCommitNotes={commitBatchNotes}
				onCommitName={commitBatchName}
				onCommitPrice={commitBatchPrice}
				onCommitCategory={commitBatchCategory}
				onCommitCustomization={commitBatchCustomization}
				onRemoveCustomization={removeBatchCustomization}
				onSetStatus={(status) => void runBatchStatus(batchFurniture, status)}
			/>
		) : null
	const batchPanelContentRef = useRef<ReactNode>(null)
	batchPanelContentRef.current = batchPanelContent

	// Right reference/furniture/ecall sidebar — content chosen by the current panel mode, kept in
	// sync declaratively. The addPanel callback owns the initial paint at root-creation time.
	const rightShadowRoot = getShadowRoot()
	let rightPanelContent: ReactNode = null
	if (isReferencePanelOpen) {
		if (rightPanelMode === "furniture" && selectedFurniture) {
			rightPanelContent = (
				<FurnitureSidebar
					key={`${selectedFurniture.id}-${furnitureMountEpoch}`}
					furniture={selectedFurniture}
					relatedObjects={relatedObjects}
					connectedEcalls={connectedEcallsForSelectedFurniture}
					initialTab={furnitureInitialTab}
					configuration={quotationDetail?.configuration}
					onView={handleConnectedCalloutView}
					onClose={handleFurnitureClose}
					onNavigateToEref={handleNavigateFurnitureToEref}
					onDelete={handleFurnitureDelete}
				/>
			)
		} else if (rightPanelMode === "furniture-batch") {
			rightPanelContent = batchPanelContent
		} else if (rightPanelMode === "eref" && selectedEref) {
			rightPanelContent = (
				<ReferenceSidebar
					onClose={handleErefClose}
					eref={selectedEref}
					ecalls={allEcalls}
					configuration={quotationDetail?.configuration}
					onEcallClick={handleErefCalloutView}
					onFurnitureClick={handleFurnitureClick}
					onPrevConnectedEref={connectedErefIds.length >= 2 ? () => handleStepConnectedEref("prev") : undefined}
					onNextConnectedEref={connectedErefIds.length >= 2 ? () => handleStepConnectedEref("next") : undefined}
					onDelete={() => handleErefDelete(selectedEref)}
					mismatchedFurnitureIds={mismatchedFurnitureIds}
				/>
			)
		} else if (rightPanelMode === "create-eref" && createSession) {
			rightPanelContent = (
				<CreateErefSidebar
					area={createSession.area}
					pageName={createPageName}
					suggestedElevationNumber={createSuggestedElevationNumber}
					isPending={isCreatePending}
					onCancel={closeCreateEref}
					onClose={closeCreateEref}
					onCreate={(elevationNumber) => void runCreateEref({ elevationNumber, pageName: createPageName ?? "" })}
				/>
			)
		} else if (rightPanelMode === "create-countertop" && countertopCreateSession) {
			rightPanelContent = (
				<CreateCountertopSidebar
					area={countertopCreateSession.area}
					relatedObjects={relatedObjects}
					isPending={isCountertopCreatePending}
					onCancel={closeCreateCountertop}
					onClose={closeCreateCountertop}
					onCreate={(values) => void runCreateCountertop(values)}
				/>
			)
		} else if (rightPanelMode === "create-ecall" && createEcallSession) {
			rightPanelContent = (
				<CreateEcallSidebar
					area={createEcallSession.area}
					pageName={createPageName}
					pageNames={createPageNames}
					isPending={isCreateEcallPending}
					onCancel={closeCreateEcall}
					onClose={closeCreateEcall}
					onCreate={(roomName, elevations, pageName) =>
						void runCreateEcall({
							room_name: roomName,
							elevations,
							page_name: pageName,
						})
					}
				/>
			)
		} else if (rightPanelMode === "create-furniture" && createFurnitureSession) {
			rightPanelContent = (
				<CreateFurnitureSidebar
					area={createFurnitureSession.area}
					pageErefs={createFurniturePageErefs}
					isPending={isCreateFurniturePending}
					onCancel={closeCreateFurniture}
					onClose={closeCreateFurniture}
					onCreate={(details) => void runCreateFurniture(details)}
				/>
			)
		} else if (rightPanelMode === "create-other" && createOtherSession) {
			rightPanelContent = (
				<CreateOtherSidebar
					area={createOtherSession.area}
					pageErefs={createOtherPageErefs}
					isPending={isCreateOtherPending}
					onCancel={closeCreateOther}
					onClose={closeCreateOther}
					onCreate={(details) => void runCreateOther(details)}
				/>
			)
		} else if (rightPanelMode === "create-custom" && createCustomSession) {
			rightPanelContent = (
				<CreateCustomSidebar
					area={createCustomSession.area}
					pageErefs={createCustomPageErefs}
					drawingId={quotationDetail?.parent_drawing_id ?? null}
					seriesName={customObjectSeriesName}
					isPending={isCreateCustomPending}
					onCancel={closeCreateCustom}
					onClose={closeCreateCustom}
					onCreate={(details) => void runCreateCustom(details)}
				/>
			)
		} else if (rightPanelMode === "ecall" && selectedEcall) {
			rightPanelContent = (
				<EcallSidebar
					key={selectedEcall.id}
					ecall={selectedEcall}
					onClose={handleEcallClose}
					onNavigateToEref={handleEcallErefView}
					onDelete={() => handleEcallDelete(selectedEcall)}
				/>
			)
		}
	}
	// The split screen's RIGHT pane sidebar (review #4 on #1264) — the same three sidebars, driven
	// by their own selection and focusing their own pane. It deliberately omits the affordances that
	// belong to the shared/left-screen selection: the connected-eref stepper (which drives the
	// jump-session bar) and the batch toolbar's targets.
	let splitPanelContent: ReactNode = null
	if (isSplitPaneOpen && splitSidebar) {
		if (splitSidebar.mode === "furniture") {
			splitPanelContent = (
				<FurnitureSidebar
					key={`split-${splitSidebar.furniture.id}`}
					furniture={splitSidebar.furniture}
					relatedObjects={relatedObjects}
					connectedEcalls={getEcallsForErefId(allEcalls, splitSidebar.furniture.eref_id)}
					configuration={quotationDetail?.configuration}
					onView={handleSplitEcallView}
					onClose={handleSplitSidebarClose}
					onNavigateToEref={handleSplitNavigateFurnitureToEref}
					onDelete={handleFurnitureDelete}
				/>
			)
		} else if (splitSidebar.mode === "eref") {
			splitPanelContent = (
				<ReferenceSidebar
					onClose={handleSplitSidebarClose}
					eref={splitSidebar.eref}
					ecalls={allEcalls}
					configuration={quotationDetail?.configuration}
					onEcallClick={handleSplitEcallView}
					onFurnitureClick={handleSplitFurnitureView}
					onDelete={() => handleErefDelete(splitSidebar.eref)}
				/>
			)
		} else {
			splitPanelContent = (
				<EcallSidebar
					key={`split-${splitSidebar.ecall.id}`}
					ecall={splitSidebar.ecall}
					onClose={handleSplitSidebarClose}
					onNavigateToEref={handleSplitEcallErefView}
					onDelete={() => handleEcallDelete(splitSidebar.ecall)}
				/>
			)
		}
	}

	// Whether the middle dock has anything to show. Drives both the pane's reserved gutter and the
	// host's visibility, so an empty dock never eats PDF width.
	const hasMiddlePanelContent = isSplitPaneOpen && rightPanelContent !== null

	// Park the middle dock inside the pane that exists NOW. Declared BEFORE the mounts below because
	// effects fire in declaration order — the host must be attached by the time useShadowMount
	// paints into it. Re-runs on every layoutEpoch: entering/leaving split screen rebuilds the pane
	// element, and a host left parented to the old one would silently vanish.
	useEffect(() => {
		if (!isInitialized) return
		if (!middlePanelHostRef.current) middlePanelHostRef.current = createMiddlePanelHost()
		const host = middlePanelHostRef.current
		const attached = host.sync({
			shadowRoot: getShadowRoot(),
			isSplitActive: isSplitPaneOpen,
			isOpen: hasMiddlePanelContent,
		})
		// The root is created once over the (stable) host container and survives detach/re-attach.
		if (attached && !sidebarMiddleEntryRef.current) {
			sidebarMiddleEntryRef.current = { root: createRoot(host.container), container: host.container }
		}

		// Showing/hiding the dock adds or removes a flex child, which resizes BOTH panes. Apryse
		// only re-renders pages at the new viewport when it is told the scroll view moved, so
		// without this each pane keeps painting at its previous width until the next scroll or
		// zoom. Deferred a frame so the browser has laid the new flex line out first.
		const raf = requestAnimationFrame(() => {
			const viewers = instanceRef.current?.Core.getDocumentViewers?.() ?? []
			for (const viewer of viewers) viewer?.scrollViewUpdated?.()
		})
		return () => cancelAnimationFrame(raf)
	}, [isInitialized, isSplitPaneOpen, hasMiddlePanelContent, layoutEpoch, getShadowRoot])

	// Publish the Primary pane onto the middle dock's host node, which is where the stylesheet's
	// `:has()` rule picks up which pane gets the 2px active border (#1311 AC7). Kept out of the sync
	// effect above because this changes on every selection while that one also asks Apryse to
	// re-lay-out both viewers. Re-asserted on `layoutEpoch`: the attribute survives (the node is
	// ours) but the pane the selector lands on is rebuilt when Apryse re-mounts the document area.
	useEffect(() => {
		middlePanelHostRef.current?.setPrimaryPane(primaryPane)
	}, [primaryPane, layoutEpoch, isSplitPaneOpen, isInitialized])

	// The primary pane's sidebar renders into the middle dock while split screen is open (review #3)
	// and into Apryse's right panel otherwise; the right panel then hosts the split pane's own
	// sidebar. Exactly one of the two roots holds `rightPanelContent` at any time.
	useShadowMount(
		sidebarMiddleEntryRef,
		rightShadowRoot && isSplitPaneOpen ? (
			<ShadowDomProviders shadowRoot={rightShadowRoot}>{rightPanelContent}</ShadowDomProviders>
		) : null,
	)
	useShadowMount(
		sidebarRightEntryRef,
		rightShadowRoot ? (
			<ShadowDomProviders shadowRoot={rightShadowRoot}>
				{isSplitPaneOpen ? splitPanelContent : rightPanelContent}
			</ShadowDomProviders>
		) : null,
	)

	// On-demand result CSV from the burger menu (REQ-420). The drawing is read for its name only —
	// the download is named after the original PDF and the quotation — and comes from the cache the
	// file-info drawer already populated.
	const { data: drawing } = useDrawing(quotationDetail?.parent_drawing_id ?? null)
	const { downloadResultCsv } = useResultCsvDownload({
		quotationIdRef: numericQuotationIdRef,
		quotationName: quotationDetail?.name,
		drawingName: drawing?.name,
	})
	// On-demand annotated result PDF from the burger menu (#1401). Reuses the same drawing/quotation
	// name lookups above.
	const { downloadResultPdf } = useResultPdfDownload({
		quotationIdRef: numericQuotationIdRef,
		quotationName: quotationDetail?.name,
		drawingName: drawing?.name,
	})

	// On-demand Quotation Details Word file from the burger menu (#1448). Same drawing/quotation
	// names as the CSV download above — the download is named the same way.
	const { downloadDetailsWord } = useDetailsWordDownload({
		quotationIdRef: numericQuotationIdRef,
		quotationName: quotationDetail?.name,
		drawingName: drawing?.name,
	})

	// Configure the header + tools for the active mode. Called once at init and again whenever
	// `mode` flips, so the SINGLE instance can switch between the editor's custom header (edit
	// tool, sidebars enabled) and the read-only preview (default Apryse header + Share feedback,
	// edit toolbars hidden). The panels/overlays themselves are built once at init and simply
	// stay closed/hidden in preview (their render effects are gated by mode). See #1119.
	const applyHeaderAndTools = useCallback(
		(instance: WebViewerInstance) => {
			applyViewerHeaderAndTools(instance, {
				modeRef,
				customHeaderAddedRef,
				previewFeedbackButtonAddedRef,
				headerEntryRef,
				onClose,
				openQuotationFeedback,
				openQuotationSettings,
				openFeedbackPanel,
				openKeyboardShortcuts,
				downloadResultCsv,
				downloadResultPdf,
				downloadDetailsWord,
				setIsPanelOpen,
				setIsReferencePanelOpen,
				onSetSplitActive,
				onCloseLeftView,
				onCloseRightView,
				onPopOut,
				onReturnViewBack,
				onToolChange: handleToolChange,
				onToggleRulerTool: handleToggleRulerTool,
				onAddElevationReference: handleAddElevationReference,
				onAddCountertop: handleAddCountertop,
				onAddElevationCallout: handleAddElevationCallout,
				onAddCabinet: handleAddCabinet,
				onAddOther: handleAddOther,
				onAddCustom: handleAddCustom,
				onToggleNoteTool: handleToggleNoteTool,
				onCloseNoteToolbar: handleCloseNoteToolbar,
				onToolOptionsRowToggle: repositionOverlaysOnToolRowToggle,
				onArmShapeTool: armShapeTool,
				onDisarmShapeTool: disarmShapeTool,
				onToggleFreehandTool: toggleFreehandTool,
			})
		},
		[
			onClose,
			openQuotationFeedback,
			openQuotationSettings,
			openFeedbackPanel,
			openKeyboardShortcuts,
			downloadResultCsv,
			downloadResultPdf,
			downloadDetailsWord,
			onSetSplitActive,
			onCloseLeftView,
			onCloseRightView,
			onPopOut,
			onReturnViewBack,
			handleToolChange,
			handleToggleRulerTool,
			handleAddElevationReference,
			handleAddCountertop,
			handleAddElevationCallout,
			handleAddCabinet,
			handleAddOther,
			handleAddCustom,
			handleToggleNoteTool,
			handleCloseNoteToolbar,
			repositionOverlaysOnToolRowToggle,
			armShapeTool,
			disarmShapeTool,
			toggleFreehandTool,
		],
	)

	// Re-apply header/tools AND re-register services when the mode flips on the already-initialized
	// instance. registerQuotationInstance(…, "editor") attaches AnnotationService (rendering +
	// selection); (…, "preview") runs AnnotationService.cleanup() which also clears any highlights
	// rendered during a prior editor session on this same instance.
	useEffect(() => {
		if (!isInitialized || !instanceRef.current) return
		registerQuotationInstance(instanceRef.current, mode)
		applyHeaderAndTools(instanceRef.current)
	}, [mode, isInitialized, applyHeaderAndTools, registerQuotationInstance])

	// Initialize WebViewer once
	useEffect(() => {
		// The host is a persistent, React-detached <div> (see QuotationViewerModal). It exists
		// before it is attached to the DOM, so guard on isConnected — WebViewer must initialize
		// into an in-document element. The layout effect that parents the host into the active
		// slot runs before this passive effect, and isModalOpen (a dep) re-runs this once open.
		if (!previewRef.current?.isConnected || instanceRef.current || isInitializingRef.current) {
			return
		}

		// Claim the init slot synchronously so a re-run of this effect during the
		// async WebViewer load can't spin up a second viewer on the same container.
		isInitializingRef.current = true

		const initializeViewer = async () => {
			const container = previewRef.current
			if (!container) return

			try {
				loadingTaskRef.current = initWebViewer(container)
					.then((instance) => {
						// Expose the WebViewer instance globally so automated tests (e.g. Playwright)
						// can verify annotation selection/highlight after navigation. No effect on app
						// behavior — see https://docs.apryse.com/web/faq/react-global-instance
						const globalWindow = window as unknown as {
							__wvInstance?: typeof instance
							__wvClickAnnotation?: (entityId: string) => void
						}
						globalWindow.__wvInstance = instance

						// Test helper: programmatically select the annotation tagged with this entityId,
						// so tests can drive selection by domain id instead of pixel-clicking the canvas.
						// entityId is stored as a string (see AnnotationService.setCustomData), so callers
						// must pass a string (e.g. "501", not 501). No effect on app behavior.
						globalWindow.__wvClickAnnotation = (entityId: string) => {
							const manager = instance.Core.annotationManager
							const annot = manager
								.getAnnotationsList()
								// Exclude furniture info labels (REQ-401): they carry their owner's
								// entityId too, and matching one would "select" an inert annotation
								// that is immediately deselected — an order-dependent test flake.
								.find(
									(a) => a.getCustomData("entityId") === entityId && a.getCustomData("entityType") !== "furnitureLabel",
								)
							if (annot) manager.selectAnnotation(annot)
						}

						registerQuotationInstance(instance, modeRef.current)
						const { UI } = instance

						// Inject sidebar styles into the web component's shadow root
						const webComponent = (container as unknown as HTMLElement).querySelector("apryse-webviewer")
						const shadowRoot = webComponent?.shadowRoot
						if (shadowRoot) {
							injectAllShadowStyles(shadowRoot)
						}

						// Configure header + tools for the current mode (re-applied on mode change).
						applyHeaderAndTools(instance)

						// Add left panel for elevation references sidebar
						UI.addPanel({
							dataElement: "elevation-references-panel",
							location: "left",
							render: () => {
								const panelContainer = document.createElement("div")
								panelContainer.style.height = "100%"
								if (rightPanelModeRef.current === "create-countertop") {
									requestAnimationFrame(() => {
										const panel = panelContainer.closest<HTMLElement>('[data-element="reference-panel"]')
										if (panel?.dataset.countertopDefaultWidthInitialized === "true") return
										if (panel) {
											const panelContainerElement = panel.closest<HTMLElement>(".ModularPanel-container")
											if (panelContainerElement) {
												panelContainerElement.style.width = CREATE_AREA_PANEL_DEFAULT_WIDTH
											}
											panel.style.width = "100%"
											const panelContent = panel.querySelector<HTMLElement>(".ModularPanel-container")
											if (panelContent) {
												panelContent.style.width = "100%"
												panelContent.style.boxSizing = "border-box"
											}
											panel.dataset.countertopDefaultWidthInitialized = "true"
										}
									})
								}

								disposeRoot(sidebarLeftEntryRef)
								sidebarLeftEntryRef.current = { root: createRoot(panelContainer), container: panelContainer }

								if (shadowRoot) {
									sidebarLeftEntryRef.current.root.render(
										<StyleProvider container={shadowRoot}>
											<ElevationReferencesSidebar
												onClose={closePanel}
												onErefClick={handleErefClick}
												onFurnitureClick={handleFurnitureClick}
												onEditPage={handleEditPage}
												relatedObjects={relatedObjectsForSidebarRef.current}
											/>
										</StyleProvider>,
									)
								}

								// The render that lets the declarative paint take over from `initialContent`
								// on this freshly created root — see `scheduleShadowRepaint` above.
								scheduleShadowRepaint()

								return panelContainer
							},
						})

						// Add right panel for reference sidebar
						UI.addPanel({
							dataElement: "reference-panel",
							location: "right",
							render: () => {
								const panelContainer = document.createElement("div")
								panelContainer.style.height = "100%"
								if (rightPanelModeRef.current === "create-ecall") {
									requestAnimationFrame(() => {
										const panel = panelContainer.closest<HTMLElement>('[data-element="reference-panel"]')
										if (panel?.dataset.ecallDefaultWidthInitialized === "true") return
										if (panel) {
											const panelContainerElement = panel.closest<HTMLElement>(".ModularPanel-container")
											if (panelContainerElement) {
												panelContainerElement.style.width = CREATE_AREA_PANEL_DEFAULT_WIDTH
											}
											panel.style.width = "100%"
											const panelContent = panel.querySelector<HTMLElement>(".ModularPanel-container")
											if (panelContent) {
												panelContent.style.width = "100%"
												panelContent.style.boxSizing = "border-box"
											}
											panel.dataset.ecallDefaultWidthInitialized = "true"
										}
									})
								}

								disposeRoot(sidebarRightEntryRef)
								sidebarRightEntryRef.current = { root: createRoot(panelContainer), container: panelContainer }

								if (shadowRoot) {
									let initialContent: ReactNode = null
									if (rightPanelModeRef.current === "furniture" && selectedFurnitureRef.current) {
										// Left blank for the one microtask until the declarative repaint lands, on
										// purpose. This branch used to paint a FurnitureSidebar with four of its eight
										// props — no `onNavigateToEref` (so the header rendered NO Back button), no
										// `connectedEcalls`, no `initialTab`, no `onView` — and without the `key` the
										// declarative element carries, so that element REMOUNTED the sidebar rather
										// than updating it: a reduced panel first, then a full one over the top of it,
										// with the object's form state reset in between. Keeping the element in
										// `rightPanelContent` alone means one mount, one paint, and no prop list to
										// keep in sync in two places.
										initialContent = null
									} else if (rightPanelModeRef.current === "furniture-batch") {
										// Apryse only calls render() the FIRST time the panel opens — after the state
										// change that opened it — so this branch owns that first frame; without it the
										// batch panel comes up blank until an unrelated re-render fixes it. Unlike the
										// branch above this paints the SAME element the declarative repaint uses (via
										// the ref), so its `key` matches and React updates it instead of remounting.
										initialContent = batchPanelContentRef.current
									} else if (rightPanelModeRef.current === "ecall" && selectedEcallRef.current) {
										initialContent = (
											<EcallSidebar
												ecall={selectedEcallRef.current}
												onClose={handleEcallClose}
												onDelete={() => {
													const ecall = selectedEcallRef.current
													if (ecall) handleEcallDelete(ecall)
												}}
											/>
										)
									} else if (rightPanelModeRef.current === "create-eref" && createSessionRef.current) {
										// Entering the mode (REQ-292) is what opens this panel, and Apryse only calls
										// render() the FIRST time it opens — after the state change, so the declarative
										// repaint below has no root to paint into yet and this branch owns the first
										// frame. The session here is always brand new (an area can only come from a
										// later drag), so the instruction state is the only thing it can be; every
										// later state, including the fields, comes from the repaint below.
										initialContent = (
											<CreateErefSidebar
												area={null}
												pageName={null}
												suggestedElevationNumber=""
												onCancel={closeCreateEref}
												onClose={closeCreateEref}
												// Unreachable without an area: the footer only exists in the filled state.
												onCreate={() => {}}
											/>
										)
									} else if (rightPanelModeRef.current === "create-countertop" && countertopCreateSessionRef.current) {
										initialContent = (
											<CreateCountertopSidebar
												area={null}
												relatedObjects={relatedObjectsRef.current}
												onCancel={closeCreateCountertop}
												onClose={closeCreateCountertop}
												onCreate={() => {}}
											/>
										)
									} else if (rightPanelModeRef.current === "create-ecall" && createEcallSessionRef.current) {
										initialContent = (
											<CreateEcallSidebar
												area={null}
												pageName={null}
												pageNames={[]}
												onCancel={closeCreateEcall}
												onClose={closeCreateEcall}
												onCreate={() => {}}
											/>
										)
									} else if (rightPanelModeRef.current === "create-furniture" && createFurnitureSessionRef.current) {
										// Same first-paint hand-off as the create-eref branch above (REQ-293): the
										// session is always brand new here, so the instruction state is the only
										// thing it can be.
										initialContent = (
											<CreateFurnitureSidebar
												area={null}
												pageErefs={EMPTY_EREFS}
												onCancel={closeCreateFurniture}
												onClose={closeCreateFurniture}
												// Unreachable without an area: the footer only exists in the filled state.
												onCreate={() => {}}
											/>
										)
									} else if (rightPanelModeRef.current === "create-other" && createOtherSessionRef.current) {
										// Same first-paint hand-off as the create-eref branch above (REQ-299): the
										// session is always brand new here, so the instruction state is the only
										// thing it can be.
										initialContent = (
											<CreateOtherSidebar
												area={null}
												pageErefs={EMPTY_EREFS}
												onCancel={closeCreateOther}
												onClose={closeCreateOther}
												// Unreachable without an area: the footer only exists in the filled state.
												onCreate={() => {}}
											/>
										)
									} else if (rightPanelModeRef.current === "create-custom" && createCustomSessionRef.current) {
										// Same first-paint hand-off as the create-eref branch above (#1371): the
										// session is always brand new here, so the instruction state is the only
										// thing it can be.
										initialContent = (
											<CreateCustomSidebar
												area={null}
												pageErefs={EMPTY_EREFS}
												drawingId={quotationDetailRef.current?.parent_drawing_id ?? null}
												// The model-number field (the only consumer) never renders with area=null.
												seriesName={null}
												onCancel={closeCreateCustom}
												onClose={closeCreateCustom}
												// Unreachable without an area: the footer only exists in the filled state.
												onCreate={() => {}}
											/>
										)
									} else if (
										rightPanelModeRef.current !== "create-eref" &&
										rightPanelModeRef.current !== "create-countertop" &&
										rightPanelModeRef.current !== "create-ecall" &&
										rightPanelModeRef.current !== "create-furniture" &&
										rightPanelModeRef.current !== "create-other" &&
										rightPanelModeRef.current !== "create-custom" &&
										selectedErefRef.current
									) {
										// Guards against a stale eref selection being painted over the create form.
										initialContent = (
											<ReferenceSidebar
												onClose={handleErefClose}
												eref={selectedErefRef.current}
												ecalls={allEcallsRef.current}
												configuration={quotationDetailRef.current?.configuration}
												onEcallClick={handleEcallClick}
												onFurnitureClick={handleFurnitureClick}
												onDelete={() => {
													const eref = selectedErefRef.current
													if (eref) handleErefDelete(eref)
												}}
											/>
										)
									}
									sidebarRightEntryRef.current.root.render(
										<ShadowDomProviders shadowRoot={shadowRoot}>{initialContent}</ShadowDomProviders>,
									)
								}

								// The render that lets the declarative paint take over from `initialContent`
								// on this freshly created root — see `scheduleShadowRepaint` above.
								scheduleShadowRepaint()

								return panelContainer
							},
						})

						// Optional-called: the incomplete Apryse mock in useQuotationViewer.test.tsx has no
						// setPanelWidth, and an init that throws here renders no panels at all.
						UI.setPanelWidth?.("elevation-references-panel", SIDEBAR_DEFAULT_WIDTH)
						UI.setPanelWidth?.("reference-panel", SIDEBAR_DEFAULT_WIDTH)

						// Create the floating shadow-DOM overlay containers (toggle, jumping-nav,
						// page-nav, context menu, batch toolbar) + their reposition/ResizeObserver,
						// then take ownership of the roots via our refs (the unmount cleanup below
						// disposes them).
						if (shadowRoot) {
							const overlays = createOverlayContainers(
								shadowRoot,
								instance,
								setIsPanelOpen,
								getConnectBarOffset,
								getToggleExtraTopOffset,
							)
							if (overlays) {
								toggleEntryRef.current = overlays.toggle
								jumpingNavEntryRef.current = overlays.jumpingNav
								splitJumpingNavEntryRef.current = overlays.splitJumpingNav
								pageNavEntryRef.current = overlays.pageNav
								splitPageNavEntryRef.current = overlays.splitPageNav
								contextMenuEntryRef.current = overlays.contextMenu
								selectionToolbarEntryRef.current = overlays.selectionToolbar
								noteToolbarEntryRef.current = overlays.noteToolbar
								highlightToolbarEntryRef.current = overlays.highlightToolbar
								connectObjectsEntryRef.current = overlays.connectObjects
								shortcutsPanelEntryRef.current = overlays.shortcutsPanel
								connectMarqueeElRef.current = overlays.connectMarquee
								erefDraftRectRef.current = overlays.erefDraftRect
								jumpingNavTargetContainerRef.current = overlays.appContainer
								jumpingNavDocAreaRef.current = overlays.docAreaRefEl
								jumpingNavResizeObserverRef.current?.disconnect()
								jumpingNavResizeObserverRef.current = overlays.resizeObserver
								repositionOverlaysRef.current = overlays.reposition
								rebindOverlayObserversRef.current = overlays.rebindObservers
								getPaneDocAreaElRef.current = overlays.getPaneDocAreaEl
							}
						}

						// Panel is closed by default
						setIsPanelOpen(false)

						const { documentViewer: docViewer } = instance.Core

						docViewer.addEventListener("documentLoaded", () => {
							setIsDocumentLoaded(true)
							// Re-assert the annotation-edit tool on every load (incl. modal reopen),
							// so clicks always select highlights and pan/zoom behave normally — editor
							// mode only; preview stays on the pan tool.
							if (modeRef.current === "editor") {
								setToolModeOnAllPanes(instance, instance.Core.Tools.ToolNames.EDIT)
							}
						})

						// Page-nav's own documentLoaded/pageNumberUpdated/zoomUpdated/scroll listeners.
						// Registered here (synchronously, in init) so the documentLoaded listener is in
						// place before the initial document finishes loading — same timing as before.
						registerPageNavListeners(docViewer)

						instanceRef.current = instance
						setIsInitialized(true)
					})
					.catch((error) => {
						console.error("WebViewer initialization failed:", error)
						// Release the slot so a later effect run can retry.
						isInitializingRef.current = false
					})
			} catch (error) {
				console.error("WebViewer initialization failed:", error)
				isInitializingRef.current = false
			}
		}

		initializeViewer()
	}, [
		previewRef,
		scheduleShadowRepaint,
		registerQuotationInstance,
		applyHeaderAndTools,
		fileLink,
		clearSelectionToolbar,
		closePanel,
		closeReferencePanel,
		setCreateAreaPanelDefaultWidth,
		handleErefClick,
		handleErefClose,
		handleFurnitureClick,
		handleFurnitureClose,
		handleFurnitureDelete,
		handleEcallClick,
		handleEcallClose,
		handleEcallDelete,
		handleErefDelete,
		closeCreateEref,
		closeCreateCountertop,
		closeCreateEcall,
		closeCreateFurniture,
		closeCreateOther,
		closeCreateCustom,
		handleEditPage,
		registerPageNavListeners,
		getConnectBarOffset,
		getToggleExtraTopOffset,
		isModalOpen,
	])

	// Cleanup on unmount only — separated so dep changes in the init effect don't destroy roots
	useEffect(() => {
		return () => {
			const toggleEl = toggleEntryRef.current?.container ?? null
			const jumpingNavEl = jumpingNavEntryRef.current?.container ?? null
			const splitJumpingNavEl = splitJumpingNavEntryRef.current?.container ?? null
			const pageNavEl = pageNavEntryRef.current?.container ?? null
			const splitPageNavEl = splitPageNavEntryRef.current?.container ?? null
			const contextMenuEl = contextMenuEntryRef.current?.container ?? null
			const selectionToolbarEl = selectionToolbarEntryRef.current?.container ?? null
			const selectionToolbarOverlayEl = selectionToolbarEl?.parentElement ?? null
			const highlightToolbarEl = highlightToolbarEntryRef.current?.container ?? null
			const highlightToolbarOverlayEl = highlightToolbarEl?.parentElement ?? null
			const noteToolbarEl = noteToolbarEntryRef.current?.container ?? null
			const noteToolbarOverlayEl = noteToolbarEl?.parentElement ?? null
			const connectObjectsEl = connectObjectsEntryRef.current?.container ?? null
			const shortcutsPanelEl = shortcutsPanelEntryRef.current?.container ?? null
			const connectMarqueeEl = connectMarqueeElRef.current
			connectMarqueeElRef.current = null
			const erefDraftRectEl = erefDraftRectRef.current?.element ?? null
			erefDraftRectRef.current = null

			jumpingNavResizeObserverRef.current?.disconnect()
			jumpingNavResizeObserverRef.current = null
			jumpingNavDocAreaRef.current = null
			jumpingNavTargetContainerRef.current = null

			disposeRoot(headerEntryRef)
			disposeRoot(sidebarLeftEntryRef)
			disposeRoot(sidebarRightEntryRef)
			disposeRoot(sidebarMiddleEntryRef)
			disposeRoot(toggleEntryRef)
			disposeRoot(jumpingNavEntryRef)
			disposeRoot(splitJumpingNavEntryRef)
			disposeRoot(pageNavEntryRef)
			disposeRoot(splitPageNavEntryRef)
			disposeRoot(contextMenuEntryRef)
			disposeRoot(selectionToolbarEntryRef)
			disposeRoot(noteToolbarEntryRef)
			disposeRoot(highlightToolbarEntryRef)
			disposeRoot(connectObjectsEntryRef)
			disposeRoot(shortcutsPanelEntryRef)

			const middlePanelHost = middlePanelHostRef.current
			middlePanelHostRef.current = null

			queueMicrotask(() => {
				toggleEl?.remove()
				jumpingNavEl?.remove()
				splitJumpingNavEl?.remove()
				pageNavEl?.remove()
				splitPageNavEl?.remove()
				contextMenuEl?.remove()
				selectionToolbarOverlayEl?.remove()
				highlightToolbarOverlayEl?.remove()
				noteToolbarOverlayEl?.remove()
				connectObjectsEl?.remove()
				shortcutsPanelEl?.remove()
				connectMarqueeEl?.remove()
				erefDraftRectEl?.remove()
				// Detaches the middle dock from whichever pane it was parented to.
				middlePanelHost?.destroy()
			})

			// Tear down the single shared WebViewer instance itself. UI.dispose() removes the
			// Core event listeners registered during init (documentLoaded/pageNumberUpdated/
			// zoomUpdated) and frees the viewer's workers/canvas. Without it, closing the drawer
			// or switching FileInfo tabs unmounts this hook but leaves the Apryse instance alive,
			// so a later reopen spins up a SECOND instance — the multi-instance state this whole
			// flow exists to avoid. #1119
			const instance = instanceRef.current
			instanceRef.current = null
			isInitializingRef.current = false
			if (instance) {
				instance.UI.dispose().catch((error) => {
					console.warn("WebViewer dispose failed:", error)
				})
			}
		}
	}, [])

	// Re-position the floating overlays after entering editor mode. The editor opens in a modal
	// whose scale/zoom open-animation makes getBoundingClientRect return a transformed (shrunk)
	// rect, so the position computed during the transition leaves the page-nav pills too high.
	// A transform doesn't change layout size, so the ResizeObserver never re-fires to correct it —
	// recompute once on the next frame and once after the animation has settled. #1119
	//
	// The same effect handles split screen (REQ-399): a `layoutEpoch` bump means the panes were
	// re-mounted at a new size, so the observer must be re-pointed at the current scroll elements
	// (an observer on a detached node is dead) and every overlay re-measured — without this the
	// page-nav pills stay parked where the full-width single pane used to be.
	useEffect(() => {
		if (mode !== "editor" || !isDocumentLoaded) return
		// Refresh the jumping-nav bar's reference element too — its render effect positions against
		// this ref, and the element it pointed at is detached after a split-screen re-mount.
		const liveDocArea = getPaneDocAreaElRef.current?.("primary")
		if (liveDocArea) jumpingNavDocAreaRef.current = liveDocArea
		rebindOverlayObserversRef.current?.()
		const raf = requestAnimationFrame(() => repositionOverlaysRef.current?.())
		const settleTimer = window.setTimeout(() => repositionOverlaysRef.current?.(), MODAL_ANIM_SETTLE_MS)
		return () => {
			cancelAnimationFrame(raf)
			window.clearTimeout(settleTimer)
		}
	}, [mode, isDocumentLoaded, isPageNavVisible, layoutEpoch])

	// `fileLink` is a PRESIGNED url (usePresignedDownloadUrl → Quotations.tsx), and that query has no
	// staleTime, so React Query's default (always-stale, refetchOnMount) re-fetches it — getting a
	// freshly re-signed URL for the SAME document — on every remount of whatever renders the file
	// list, not just when the user opens a different file. A presigned URL is timestamp-signed, so a
	// re-sign always changes the STRING even though the object it points at didn't. Reacting to the
	// raw string below made the effect indistinguishable from "the user opened a different document",
	// silently reloading the whole document (and everything downstream keyed on the live instance:
	// split screen, smart navigation, every selection) mid-session with no user action at all.
	// Comparing origin+pathname — the S3 object key — instead of the full URL is what tells "same
	// object, re-signed" apart from "a genuinely different document".
	const fileIdentity = useMemo(() => resolveDocumentIdentity(fileLink), [fileLink])

	// Who the cached bytes belong to. Read from the store rather than the profile query so it is
	// available synchronously — `useSyncCurrentUser` populates it (Userbar in the shell, and the
	// pop-out page). Undefined until then, which `fetchDocumentBlob` answers by downloading without
	// caching. (PR #1429 review.)
	const currentUserId = useCurrentUserStore((state) => state.currentUser.id)
	const userScope = currentUserId == null ? undefined : String(currentUserId)

	// A name for the Blob handed to `loadDocument`, taken from the object key's last segment. Only
	// cosmetic — Apryse shows it in its own UI — but a Blob has no name of its own, so without this
	// the document is nameless.
	const documentFilename = useMemo(() => {
		const lastSegment = fileIdentity?.split("/").pop()
		if (!lastSegment) return "document.pdf"
		try {
			return decodeURIComponent(lastSegment) || "document.pdf"
		} catch {
			return lastSegment
		}
	}, [fileIdentity])

	// Handle document loading — also re-loads on modal reopen, since closeDocument()
	// runs on close and the document otherwise wouldn't be re-initialized when the same
	// Start fetching the bytes as soon as the viewer is opening — deliberately NOT waiting for
	// `isInitialized`, so the download overlaps the viewer's own boot the way `initialDoc` did.
	// Declared BEFORE the load effect below so that on the render where both become ready, this one
	// has already published the promise the other consumes (React runs effects in declaration
	// order). Persisted across page reloads by core/documentBlobCache, keyed on `fileIdentity` —
	// the S3 object key — because the presigned URL is re-signed on every request and so can never
	// hit the browser's own HTTP cache.
	useEffect(() => {
		if (!fileLink || !isModalOpen) return
		documentBlobRef.current = null

		// Progress goes to a store, never to state here: it arrives once per network chunk —
		// thousands of times for a large drawing — and this hook's every memo and effect would re-run
		// on each one. `getState()` rather than the hook, so the orchestrator never subscribes either.
		const { start, report, finish } = useDocumentLoadStore.getState()
		let isSettled = false
		let hasAnnounced = false
		// Nothing is shown for the first moments. A cache hit resolves in milliseconds and a small
		// drawing on a fast link not much slower; announcing either would flash an overlay for no
		// reason. Past this delay the wait is real and worth reporting.
		const announceTimer = window.setTimeout(() => {
			if (isSettled) return
			hasAnnounced = true
			start()
		}, DOCUMENT_LOAD_ANNOUNCE_DELAY_MS)
		let lastPercent = -1

		const promise = fetchDocumentBlob({
			fileIdentity,
			userScope,
			url: fileLink,
			onProgress: ({ loaded, total }) => {
				if (!hasAnnounced || isSettled) return
				// No declared length — `start()` has already put a plain spinner up, and there is no
				// honest percentage to add to it.
				if (total === null) return
				const fraction = Math.min(loaded / total, 1)
				const percent = Math.floor(fraction * 100)
				if (percent === lastPercent) return
				lastPercent = percent
				report(fraction)
			},
		})

		// One settle for every ending — resolved, rejected, document switched, modal closed — so the
		// overlay can never outlive the download it describes.
		const settle = () => {
			isSettled = true
			window.clearTimeout(announceTimer)
			if (hasAnnounced) finish()
		}
		promise.then(settle, settle)
		// Nothing awaits this yet, so swallow here to keep an early failure from surfacing as an
		// unhandled rejection. The load effect below awaits the SAME promise and answers a failure
		// by loading from the URL instead.
		promise.catch(() => {})
		documentRequestRef.current = { identity: fileIdentity, promise }
		return settle
		// fileLink is read for its CURRENT value, not a reactive dependency — see fileIdentity above.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fileIdentity, isModalOpen])

	// fileLink is opened again. Keyed on `fileIdentity`, not `fileLink` itself — see above.
	useEffect(() => {
		if (instanceRef.current && fileLink && isInitialized && isModalOpen) {
			const { UI } = instanceRef.current
			let cancelled = false
			// Reuse the request the effect above already started; only fall back to starting one
			// here if it holds bytes for a different document (or none yet), which would otherwise
			// load the wrong file into the viewer.
			// Read into a local first: an optional-chain test does not narrow the ref itself.
			const started = documentRequestRef.current
			const pending =
				started && started.identity === fileIdentity
					? started.promise
					: fetchDocumentBlob({ fileIdentity, userScope, url: fileLink })
			pending
				.then((blob) => {
					if (cancelled) return
					documentBlobRef.current = blob
					// A Blob carries no name, and without `extension` Apryse only *assumes* "pdf" and
					// says so in a console warning. State it rather than rely on the assumption.
					return UI.loadDocument(blob, { filename: documentFilename, extension: "pdf" })
				})
				.catch((error) => {
					if (cancelled) return
					// Drop the bytes before falling back. This catch covers BOTH a failed fetch (the ref
					// was never set, so this is a no-op) and a failed `loadDocument` (the ref holds a blob
					// Apryse just refused). `useSplitScreen` reads `documentBlobRef.current ?? fileLink`,
					// so leaving a known-bad blob there would hand the second pane the very bytes that
					// just failed, and it would fail identically. (PR #1429 review.)
					documentBlobRef.current = null
					// A failed prefetch (network hiccup, CORS on some other host) shouldn't block
					// opening the document — fall back to the URL, which is what this always did
					// before the blob cache existed.
					console.warn("Failed to prefetch the document; loading by URL instead:", error)
					UI.loadDocument(fileLink)
				})
			return () => {
				cancelled = true
			}
		}
		// fileLink is read for its CURRENT value, not a reactive dependency — see fileIdentity above.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fileIdentity, isInitialized, isModalOpen])

	// Handle cleanup when the modal is closed
	useEffect(() => {
		if (!isModalOpen && instanceRef.current) {
			setIsDocumentLoaded(false)
			setFurnitureInitialTab("Details")
			closeContextMenu()

			// Close both sidebars (clears their UI elements + open flags) and clear the
			// selected entities. The viewer instance is reused across quotations, so
			// without this the right (furniture) panel would reopen with stale content
			// the next time the modal is opened.
			closeReferencePanel()
			closePanel()
			setRightPanelMode(null)
			setSelectedEref(undefined)
			setSelectedFurniture(undefined)
			setSelectedEcall(undefined)
			selectedErefRef.current = undefined
			selectedFurnitureRef.current = undefined
			selectedEcallRef.current = undefined

			try {
				const { Core } = instanceRef.current

				// Close the current document
				if (Core && Core.documentViewer) {
					try {
						Core.documentViewer.closeDocument()
					} catch (error) {
						console.log("Could not close document:", error)
					}
				}
			} catch (error) {
				console.error("Error during cleanup:", error)
			}
		}
	}, [isModalOpen, closeReferencePanel, closePanel, closeContextMenu])

	return {
		instance: instanceRef.current,
		isInitialized,
		// Split-screen state (REQ-399) — exposed for the host modal / tests; the in-viewer toggle
		// lives in the custom header.
		isSplitActive,
		canSplit,
		toggleSplit,
	}
}
